import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

export const LANGUAGES = [
  {
    code: 'en',
    label: 'English',
    nativeName: 'English',
    shortLabel: 'EN',
    description: ''
  },
  {
    code: 'kn',
    label: 'Kannada',
    nativeName: 'ಕನ್ನಡ',
    shortLabel: 'KN',
    description: ''
  },
  {
    code: 'ta',
    label: 'Tamil',
    nativeName: 'தமிழ்',
    shortLabel: 'TA',
    description: ''
  },
  {
    code: 'ml',
    label: 'Malayalam',
    nativeName: 'മലയാളം',
    shortLabel: 'ML',
    description: ''
  },
  {
    code: 'hi',
    label: 'Hindi',
    nativeName: 'हिन्दी',
    shortLabel: 'HI',
    description: ''
  },
  {
    code: 'mr',
    label: 'Marathi',
    nativeName: 'मराठी',
    shortLabel: 'MR',
    description: ''
  },
  {
    code: 'te',
    label: 'Telugu',
    nativeName: 'తెలుగు',
    shortLabel: 'TE',
    description: ''
  }
];

const baseTranslation = {
  app: {
    name: 'Pain Tracker',
    loginPortal: 'Login Portal'
  },
  common: {
    back: 'Back',
    cancel: 'Cancel',
    confirm: 'Confirm',
    loading: 'Please wait...',
    language: 'Language',
    requiredField: 'Please fill in all required fields',
    name: 'Name',
    unknownTime: 'Unknown time',
    invalidDate: 'Invalid date',
    invalidTime: 'Invalid time',
    justNow: 'Just now',
    minutesAgo: 'm ago',
    hoursAgo: 'h ago',
    unknown: 'Unknown',
    notAvailable: 'N/A'
  },
  languageGate: {
    title: 'Choose Your Language',
    subtitle: 'Select your preferred language to continue to the portal.',
    helper: 'You can switch languages anytime from the login screen.',
    helperEmphasis: 'No worries!',
    secondary: 'This helps us make sure you feel at home from the very first step.'
  },
  buttons: {
    login: 'Login',
    register: 'Register',
    verifyPasskey: 'Verify Passkey'
  },
  login: {
    initialChoice: {
      login: 'Login',
      register: 'Register'
    },
    passkey: {
      prompt: 'Enter Passkey *',
      helper: 'Use registration passkey for patient/doctor registration or admin passkey for admin access',
      invalid: 'Invalid passkey. Please try again.'
    },
    form: {
      idLabel: 'ID, email, or phone *',
      idPlaceholder: 'Patient ID or phone number, Doctor ID or email',
      passwordLabel: 'Password *',
      passwordPlaceholder: 'Enter your password',
      rememberMe: 'Remember me for 90 days'
    },
    errors: {
      missingId: 'Please enter your Patient ID or Doctor ID',
      missingPassword: 'Please enter your password',
      invalidIdFormat: 'Invalid ID format. Please use Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) or Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      adminLoginDisabled: 'Admin login is not available. Please use the admin passkey through the registration option.',
      missingDoctorEmail: 'Email is required for doctor registration',
      missingDoctorPhone: 'Phone number is required for doctor registration',
      passwordMismatch: 'Passwords do not match',
      passwordLength: 'Password must be at least 6 characters long',
      missingPatientDoctor: 'Please fill in all required fields including doctor selection',
      genericLogin: 'Login failed',
      loadDoctorsFailed: 'Failed to load doctors list. Please refresh the page.',
      noDoctors: 'No doctors available. Please contact support.'
    },
    success: {
      welcomeDoctor: 'Welcome back, Dr. {{name}}!',
      welcomePatient: 'Welcome back!'
    },
    register: {
      title: 'I am a *',
      patient: 'Patient',
      doctor: 'Doctor',
      firstName: {
        label: 'First Name *',
        placeholder: 'Enter your first name'
      },
      lastName: {
        label: 'Last Name (Optional)',
        placeholder: 'Enter your last name (optional)'
      },
      email: {
        label: 'Email *',
        placeholder: 'Enter your email address'
      },
      phone: {
        label: 'Phone Number *',
        placeholder: 'Enter your phone number'
      },
      selectDoctor: {
        label: 'Select Your Doctor *',
        placeholder: '-- Select a Doctor --',
        loading: 'Loading doctors...',
        noDoctors: 'No doctors available. Please contact support.'
      },
      dateOfBirth: {
        label: 'Date of Birth *'
      },
      contactNumber: {
        label: 'Phone Number *',
        placeholder: 'Enter your contact number'
      },
      hospitalId: {
        label: 'Hospital ID (Optional)',
        placeholder: 'Enter your hospital ID (optional)'
      },
      whatsappNumber: {
        label: 'WhatsApp Number (Optional) 💬',
        placeholder: 'Enter WhatsApp number (e.g., +1234567890)',
        helper: "You'll receive medication notifications via WhatsApp if provided"
      },
      password: {
        label: 'Password *',
        placeholder: 'Enter your password'
      },
      confirmPassword: {
        label: 'Confirm Password *',
        placeholder: 'Confirm your password'
      },
      passkeyTitle: 'Enter Passkey *',
      passkeyHelper: 'Use registration passkey for patient/doctor registration or admin passkey for admin access'
    }
  },
  toast: {
    adminWelcome: 'Welcome, {{name}}!',
    doctorRegistrationSuccess: 'Registration successful! Your Doctor ID is: {{id}}. Please login to continue.'
  },
  dashboard: {
    loading: 'Loading dashboard...',
    welcome: {
      title: 'Welcome to Pain Tracker',
      subtitle: 'Track your pain levels and monitor your health progress'
    },
    stats: {
      logsToday: 'Logs Today',
      noPain: 'No Pain',
      mild: 'Mild',
      moderatePain: 'Moderate Pain',
      severePain: 'Severe Pain',
      worstPain: 'Worst Pain',
      unknown: 'Unknown'
    },
    actions: {
      retry: 'Try Again',
      bodyDiagram: 'Body Diagram',
      viewLogs: 'View Logs',
      viewStatistics: 'View Statistics',
      history: 'History'
    },
    errors: {
      title: 'Error loading dashboard',
      loadFailed: 'Failed to load pain data'
    },
    logs: {
      title: 'Recent Pain Logs',
      emptyTitle: 'No pain logs today',
      emptyText: 'Start tracking your pain by clicking on body zones'
    },
    sidebar: {
      title: 'Pain Tracker',
      links: {
        dashboard: 'Dashboard',
        bodyDiagram: 'Body Diagram',
        painLogs: 'Pain Logs',
        history: 'History',
        statistics: 'Statistics',
        distress: 'Distress',
        medication: 'Medication'
      },
      user: {
        profile: 'Patient Profile'
      },
      actions: {
        logout: 'Logout'
      }
    }
  },
  painScale: {
    title: 'Record Pain Level',
    vasTitle: 'Visual Analog Scale (VAS)',
    vasDescription: 'Standard 0–10 scale: 0 = No pain, 10 = Worst pain imaginable',
    vasCurrentValue: 'Your pain level',
    vasWorstPain: 'Worst pain imaginable',
    vasDescriptors: {
      noPain: 'No Pain',
      mild: 'Mild',
      moderate: 'Moderate',
      severe: 'Severe',
      verySevere: 'Very Severe',
      worstPain: 'Worst Pain Possible'
    },
    vasIncrease: 'Increase',
    vasDecrease: 'Decrease',
    fixedScales: 'Fixed Pain Scales: No Pain (0) • Mild (1-3) • Moderate (4-6) • Severe (7-9) • Worst Pain (10)',
    frontView: '👤 Front View',
    backView: '👥 Back View',
    levels: {
      noPain: 'No Pain',
      mild: 'Mild',
      moderatePain: 'Moderate Pain',
      severePain: 'Severe Pain',
      worstPain: 'Worst Pain'
    },
    descriptions: {
      noPain: 'No discomfort',
      mild: 'Mild discomfort',
      moderatePain: 'Noticeable but manageable',
      severePain: 'Intense and concerning',
      worstPain: 'Worst pain possible'
    },
    additionalInfo: 'Additional Information (Optional)',
    painScale: 'Pain Scale',
    timeOfDay: 'Time of Day',
    mood: 'Mood',
    activity: 'Physical Activity Level',
    symptoms: 'Symptoms (comma-separated)',
    triggers: 'Triggers (comma-separated)',
    notes: 'Notes (Text)',
    audioNote: 'OR Record Your Description (Audio)',
    placeholders: {
      symptoms: 'e.g., stiffness, swelling, numbness',
      symptomsMultiSelect: 'Hold Ctrl/Cmd to select multiple symptoms',
      triggers: 'e.g., stress, weather, exercise',
      notes: 'Type your pain description here...',
      selectMood: 'Select mood (optional)',
      selectActivity: 'Select activity (optional)',
      selectTimeOfDay: 'Select time of day (optional)'
    },
    timeOptions: {
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
      night: 'Night'
    },
    moodOptions: {
      good: 'Good',
      moderate: 'Moderate',
      bad: 'Bad'
    },
    activityOptions: {
      able: 'Able',
      partiallyDependent: 'Partially Dependent',
      notAble: 'Not Able'
    },
    symptomOptions: {
      none: 'None',
      vomiting: 'Vomiting',
      nausea: 'Nausea',
      dizziness: 'Dizziness',
      headache: 'Headache',
      fever: 'Fever',
      fatigue: 'Fatigue',
      shortnessOfBreath: 'Shortness of Breath',
      cough: 'Cough',
      swelling: 'Swelling',
      numbness: 'Numbness',
      weakness: 'Weakness'
    },
    buttons: {
      cancel: 'Cancel',
      clear: 'Clear',
      record: 'Record Pain'
    }
  },
  audioRecorder: {
    title: 'Voice Recording',
    help: {
      start: 'Click "Start Recording" to record your pain description using your voice.',
      recording: 'Recording in progress... Click "Stop Recording" when finished.',
      recorded: 'Audio recorded successfully! You can play, delete, or record a new one.'
    },
    buttons: {
      start: 'Start Recording',
      stop: 'Stop Recording',
      play: 'Play Recording',
      pause: 'Pause',
      delete: 'Delete',
      rerecord: 'Re-record'
    },
    info: {
      duration: 'Duration',
      format: 'Format'
    },
    errors: {
      microphoneAccess: 'Unable to access microphone. Please check your browser permissions.'
    }
  },
  audioPlayer: {
    label: 'Voice Note',
    loading: 'Loading audio...',
    errors: {
      loadFailed: 'Failed to load audio: {{message}}',
      notLoaded: 'Audio not loaded yet',
      playFailed: 'Failed to play audio',
      playFailedWithMessage: 'Failed to play audio: {{message}}'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'Select time of day',
    success: {
      recorded: 'Pain level recorded successfully',
      withAudio: 'Pain level recorded successfully with audio note'
    },
    errors: {
      failed: 'Failed to record pain level: {{message}}'
    },
    confirm: {
      clearAll: 'Are you sure you want to clear all pain data for today?'
    }
  },
  profile: {
    title: 'My Profile',
    subtitle: 'Manage your personal information',
    loading: 'Loading profile...',
    editWhatsApp: '✏️ Edit WhatsApp Number',
    whatsAppLabel: 'WhatsApp Number',
    whatsAppPlaceholder: 'Enter WhatsApp number (e.g., +1234567890)',
    whatsAppHelper: "You'll receive medication notifications via WhatsApp if provided. Leave empty to remove.",
    save: '💾 Save Changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    success: 'WhatsApp number updated successfully!',
    optional: 'Optional',
    forNotifications: 'for medication notifications',
    notProvided: 'Not provided',
    patientId: 'Patient ID',
    hospitalId: 'Hospital ID',
    age: 'Age',
    ageYears: '{{age}} years',
    phoneNumber: 'Phone Number',
    whatsAppNumber: 'WhatsApp Number',
    notRegistered: 'Not registered',
    notRegisteredMessage: 'Not registered - You won\'t receive WhatsApp medication notifications',
    dateOfBirth: 'Date of Birth',
    accountStatus: 'Account Status',
    active: 'Active',
    inactive: 'Inactive',
    memberSince: 'Member Since',
    lastUpdated: 'Last Updated',
    role: 'Role',
    roles: {
      patient: 'Patient',
      doctor: 'Doctor',
      admin: 'Admin'
    },
    sections: {
      personalInfo: 'Personal Information',
      contactInfo: 'Contact Information',
      accountInfo: 'Account Information'
    },
    errors: {
      authRequired: 'Authentication required',
      updateFailed: 'Failed to update WhatsApp number'
    }
  },
  painLogs: {
    title: 'Pain Logs',
    subtitle: 'Review and manage your recorded pain entries.',
    loading: 'Loading pain logs...',
    error: 'Error loading pain logs',
    filters: {
      painLevel: 'Pain Level:',
      view: 'View:',
      dateRange: 'Date Range:',
      allLevels: 'All Levels',
      noPain: 'No Pain',
      mild: 'Mild',
      moderatePain: 'Moderate Pain',
      severePain: 'Severe Pain',
      worstPain: 'Worst Pain',
      bothViews: 'Both Views',
      frontView: 'Front View',
      backView: 'Back View',
      allTime: 'All Time',
      today: 'Today',
      week: 'This Week',
      month: 'This Month',
      startDate: 'Start Date',
      endDate: 'End Date',
      clear: 'Clear Filters',
      searchPlaceholder: 'Search logs...'
    },
    table: {
      painLevel: 'Pain Level',
      zone: 'Zone',
      level: 'Level',
      intensity: 'Intensity',
      notes: 'Notes',
      symptoms: 'Symptoms',
      triggers: 'Triggers',
      date: 'Date',
      time: 'Time',
      actions: 'Actions'
    },
    actions: {
      refresh: 'Refresh',
      deleteLogs: 'Delete Logs',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      deleteSelected: 'Delete Selected ({{count}})',
      cancel: 'Cancel',
      delete: 'Delete',
      previous: 'Previous',
      next: 'Next'
    },
    deleteMode: {
      info: '🗑️ Delete Mode: Select logs to delete',
      selected: '{{count}} selected',
      confirm: 'Are you sure you want to delete {{count}} selected log(s)? This action cannot be undone.'
    },
    empty: 'No pain logs found',
    emptyText: 'Start tracking your pain to see your logs here.',
    emptyFiltered: 'Try adjusting your filters to see more results',
    pending: 'Pending sync',
    deleteConfirm: 'Are you sure you want to delete this pain log?',
    deleteSuccess: 'Pain log deleted successfully',
    deleteError: 'Failed to delete pain log',
    success: {
      deleted: 'Pain log deleted successfully'
    },
    errors: {
      deleteFailed: 'Failed to delete pain log'
    }
  },
  statistics: {
    title: 'Statistics',
    vasScaleCaption: 'Pain levels: VAS 0–10 — No Pain · Mild (1–3) · Moderate (4–6) · Severe (7–9) · Worst (10)',
    loading: 'Loading statistics...',
    error: 'Error loading statistics',
    loadFailed: 'Failed to load statistics',
    painLevelDistribution: 'Pain Level Distribution',
    painTrends: 'Pain Trends Over Time',
    painByZone: 'Pain by Body Zone',
    timePeriod: 'Time Period',
    lastDays: 'Last {{count}} days',
    totalLogs: 'Total Logs',
    empty: 'No data available',
    emptyText: 'Start tracking your pain to see statistics and trends',
    labels: {
      noPain: 'No Pain',
      mild: 'Mild',
      moderatePain: 'Moderate Pain',
      severePain: 'Severe Pain',
      worstPain: 'Worst Pain',
      intensityOverTime: 'Pain Intensity Over Time',
      painIntensity: 'Pain Intensity',
      painLevel: 'Pain Level',
      time: 'Time',
      notes: 'Notes'
    },
    noDataForRegion: 'No data for this region',
    totalEntries: 'Total Entries',
    emptyRegionLine: 'No pain logs found for "{{zone}}" in the selected time period. Try selecting a longer time period or choose a different region.',
    noPainTrendsData: 'No pain trends data available for the selected time period.',
    emptyRegionBar: 'No pain data found for "{{zone}}" in the selected time period.',
    noPainByZoneData: 'No pain data by body zone available.',
    emptyRegionDoughnut: 'No pain level distribution data found for "{{zone}}" in the selected time period.',
    noPainLevelDistributionData: 'No pain level distribution data available.',
    painTimelineFor: 'Pain Timeline for {{zone}}',
    filterByRegion: 'Filter by Region',
    allRegions: 'All Regions',
    dateRange: 'Date Range',
    heatmapTitle: 'Pain Heatmap by Zone',
    heatmap: {
      zone: 'Zone',
      intensity: 'Intensity',
      logs: 'Logs',
      intensityScale: 'Pain Intensity Scale',
      intensityLabels: {
        severe: 'Severe',
        moderate: 'Moderate',
        less: 'Less',
        none: 'None'
      }
    }
  },
  medication: {
    title: '💊 Medication & Prescriptions',
    subtitle: 'Review the medication plans prescribed by your doctor. Download a pharmacy-ready copy or revisit previous prescriptions whenever needed.',
    loading: 'Loading prescriptions...',
    empty: {
      title: 'No prescriptions yet',
      message: 'Your doctor will share medication plans here. Check back after your consultation.'
    },
    description: 'Review the medication plans prescribed by your doctor. Download a pharmacy-ready copy or revisit previous prescriptions whenever needed.',
    legacy: {
      title: 'Legacy Prescription',
      subtitle: 'This prescription was sent using the previous format.',
      badge: 'Text-based Prescription'
    },
    current: {
      title: '⭐ Current Prescription',
      badge: 'Current Prescription',
      lastUpdated: 'Last updated',
      notice: 'This is the most recent prescription. Follow these instructions carefully and provide the downloadable copy to your pharmacist to dispense the medication exactly as prescribed.'
    },
    history: {
      title: '📚 Prescription History',
      subtitle: 'Review previous prescriptions shared by your doctor.',
      badge: 'Prescription History'
    },
    download: {
      formal: '📥 Download Formal Prescription',
      text: '📄 Download text copy',
      success: 'Prescription downloaded',
      error: 'No prescription available to download'
    },
    labels: {
      patient: 'Patient',
      doctor: 'Doctor',
      issuedOn: 'Issued On',
      medication: 'Medication',
      doseQty: 'Dose / Qty',
      timing: 'Timing',
      food: 'Food',
      duration: 'Duration',
      notes: 'Notes',
      patientInstructions: 'Patient Instructions',
      pharmacyNotes: 'Pharmacy Notes',
      asPrescribed: 'As prescribed',
      asDirected: 'As directed',
      id: 'ID:',
      reg: 'Reg:',
      contact: 'Contact:',
      prescribingDoctor: 'Prescribing Doctor:',
      treatingPhysician: 'Treating Physician',
      yourDoctor: 'Your Doctor',
      schedule: {
        morning: 'Morning',
        afternoon: 'Afternoon',
        evening: 'Evening',
        night: 'Night'
      },
      foodInstruction: {
        before: 'Before Food',
        with: 'With Food',
        after: 'After Food',
        none: 'Not Meal Dependent'
      }
    },
    footer: {
      needAssistance: 'Need assistance?',
      shareNote: 'Share the downloaded PDF with your pharmacist or consulting doctor for verification and fulfilment.'
    },
    success: {
      newPrescription: '💊 New prescription received',
      downloaded: 'Prescription downloaded'
    },
    errors: {
      loadFailed: 'Unable to load prescriptions right now',
      noPrescription: 'No prescription available to download'
    },
    reminders: {
      title: 'Medication Reminders',
      next: 'Next Reminder',
      at: 'at',
      taken: 'Taken! Record pain in 30 mins',
      iTookIt: 'I took it',
      takeMedication: 'Take medication: {{name}}',
      recordPainAfter: 'Record pain 30 min after taking for your doctor.',
      recordPainNow: 'Record your pain now (30 min after medication) so your doctor can see the effect.',
      recordPainNotification: 'Record your pain level now (30 min after medication).',
      recordPain: 'Record pain',
      recordPainPrompt: 'Record your pain now so your doctor can track the effect of your medication.',
      recordPainNowButton: 'Record pain now',
      upcoming: 'Upcoming',
      noUpcoming: 'No upcoming reminders'
    }
  },
  messages: {
    title: 'Messages from Doctor',
    subtitle: {
      unread: 'You have {{count}} unread message',
      unreadPlural: 'You have {{count}} unread messages',
      none: 'No new messages'
    },
    loading: 'Loading messages...',
    error: 'Failed to load messages',
    empty: {
      title: 'No messages yet',
      text: 'Your doctor will send you messages here about medications and other important information.'
    },
    types: {
      medication: '💊 Medication',
      appointment: '📅 Appointment',
      advice: '💡 Advice',
      instruction: '📋 Instruction',
      general: '💬 General'
    },
    from: 'From:',
    date: 'Date',
    close: 'Close',
    viewMedication: 'View Medication',
    medicationNotification: {
      single: 'Your medicine has been changed to {{medication}}.',
      multiple: 'Your medicine has been changed to {{meds}} and {{lastMed}}.',
      fallback: 'Your medication prescription has been updated.'
    }
  },
  history: {
    title: 'Pain History',
    subtitle: 'Complete record of all your pain tracking activities',
    loading: 'Loading history...',
    error: {
      title: 'Error loading history',
      message: 'Failed to load history data',
      retry: 'Try Again'
    },
    empty: {
      title: 'No records found',
      filtered: 'Try adjusting your filters or search terms',
      noData: 'Start tracking your pain to see your history here'
    },
    stats: {
      total: 'Total Records',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month'
    },
    filters: {
      painLevel: 'Pain Level:',
      view: 'View:',
      dateRange: 'Date Range:',
      allLevels: 'All Levels',
      noPain: 'No Pain',
      mild: 'Mild',
      lessPain: 'Less Pain',
      moderatePain: 'Moderate Pain',
      severePain: 'Severe Pain',
      worstPain: 'Worst Pain',
      bothViews: 'Both Views',
      frontView: 'Front View',
      backView: 'Back View',
      allTime: 'All Time',
      today: 'Today',
      thisWeek: 'This Week',
      thisMonth: 'This Month'
    },
    search: {
      placeholder: 'Search records...'
    },
    actions: {
      refresh: 'Refresh',
      deleteLogs: '🗑️ Delete Logs',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      deleteSelected: 'Delete Selected',
      cancel: 'Cancel'
    },
    deleteMode: {
      title: '🗑️ Delete Mode: Select logs to delete',
      selected: 'selected',
      confirm: 'Are you sure you want to delete {{count}} selected log(s)? This action cannot be undone.',
      success: 'Successfully deleted {{count}} log(s)',
      error: 'Failed to delete selected logs',
      selectAtLeastOne: 'Please select at least one log to delete'
    },
    header: {
      title: 'History Records ({{count}})'
    },
    labels: {
      intensity: 'Intensity:',
      symptoms: 'Symptoms:',
      triggers: 'Triggers:',
      view: 'View',
      unknownTime: 'Unknown time',
      invalidDate: 'Invalid date',
      noDate: 'No date',
      justNow: 'Just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago'
    },
    painLevels: {
      'no-pain': 'No Pain',
      'mild': 'Mild',
      'moderate': 'Moderate',
      'severe': 'Severe',
      'worst-pain': 'Worst Pain'
    }
  },
  distress: {
    title: '🌡️ Distress Tracker',
    subtitle: 'Record and monitor your distress levels over time',
    loading: 'Loading distress history...',
    error: {
      title: 'Error loading distress history',
      message: 'Failed to load distress logs',
      retry: 'Try Again',
      login: 'Please login to view distress logs'
    },
    empty: {
      title: 'No distress logs found',
      textFilter: 'No logs match your filter criteria',
      textNoFilter: 'Record your distress level using the thermometer above'
    },
    status: {
      online: 'Online',
      offline: 'Offline mode',
      syncing: 'Syncing…',
      syncNow: 'Sync Now',
      pending: 'pending',
      pendingSync: 'Pending sync',
      pendingSyncEntries: 'Pending sync entries'
    },
    filters: {
      minScore: 'Score:',
      startDate: 'Start Date:',
      endDate: 'End Date:',
      allScores: 'All Scores',
      score0: '0+',
      score4: '4+ (Clinically Significant)',
      score7: '7+ (High)',
      clinicallySignificant: '4+ (Clinically Significant)',
      high: '7+ (High)',
      clear: 'Clear Filters'
    },
    stats: {
      totalEntries: 'Total Entries',
      averageScore: 'Average Score',
      highestScore: 'Highest Score',
      highDistress: 'High Distress (≥4)'
    },
    labels: {
      distressScore: 'Distress Score:',
      sourcesOfDistress: 'Sources of Distress:',
      notes: 'Notes:',
      extreme: 'Extreme Distress',
      moderateToSevere: 'Moderate to Severe Distress',
      mild: 'Mild Distress',
      none: 'No Distress',
      alert: '🚨 Clinically Significant Distress - Your doctor has been notified'
    },
    header: {
      title: 'Distress Logs ({{count}})',
      refresh: 'Refresh'
    }
  },
  distressThermometer: {
    title: '🌡️ Distress Thermometer',
    subtitle: 'Rate your current level of distress over the past week (0 = no distress, 10 = extreme distress)',
    guidelines: 'Based on NCCN Distress Thermometer guidelines',
    problemList: {
      label: 'Problem List - What is causing your distress? (Optional)',
      helper: 'Select all that apply (NCCN Problem List)'
    },
    notes: {
      label: 'Additional Notes (Optional)',
      placeholder: 'Describe any additional details about your distress...',
      enterNumberHint: 'Or enter number (0–10):'
    },
    scoreLabels: {
      noDistress: 'No Distress',
      mildDistress: 'Mild Distress',
      moderateDistress: 'Moderate Distress',
      severeDistress: 'Severe Distress',
      extremeDistress: 'Extreme Distress'
    },
    buttons: {
      record: 'Record Distress Level',
      recording: 'Recording...',
      clear: 'Clear'
    },
    alert: '⚠️ NCCN Alert: Score of {{score}} indicates clinically significant distress (≥4). Your healthcare provider will be notified.',
    success: {
      recorded: 'Distress level {{score}} recorded successfully'
    },
    errors: {
      login: 'Please login to record distress',
      recordFailed: 'Failed to record distress'
    },
    sources: {
      // Practical Problems
      transportation: 'Transportation',
      housing: 'Housing',
      insuranceFinancial: 'Insurance/Financial',
      workSchool: 'Work/School',
      // Family Problems
      dealingWithPartner: 'Dealing with partner',
      dealingWithChildren: 'Dealing with children',
      familyHealthIssues: 'Family health issues',
      // Emotional Problems
      depression: 'Depression',
      fears: 'Fears',
      nervousness: 'Nervousness',
      sadness: 'Sadness',
      worry: 'Worry',
      lossOfInterest: 'Loss of interest in usual activities',
      // Spiritual/Religious Concerns
      spiritualReligiousConcerns: 'Spiritual/Religious concerns',
      // Physical Problems
      fatigue: 'Fatigue',
      pain: 'Pain',
      sleepProblems: 'Sleep problems',
      eatingProblems: 'Eating problems',
      bathingDressing: 'Bathing/Dressing',
      breathingProblems: 'Breathing problems',
      nausea: 'Nausea',
      memoryConcentration: 'Memory/Concentration',
      mouthSores: 'Mouth sores',
      skinProblems: 'Skin problems',
      tinglingHandsFeet: 'Tingling in hands/feet',
      otherPhysicalProblems: 'Other physical problems'
    }
  },
  bodyParts: {
    // Front view zones
    'right palm': 'Right Palm',
    'left lower abdomen': 'Left Lower Abdomen',
    'right lower abdomen': 'Right Lower Abdomen',
    'left upper abdomen': 'Left Upper Abdomen',
    'right upper abdomen': 'Right Upper Abdomen',
    'left front forearm': 'Left Front Forearm',
    'left elbow': 'Left Elbow',
    'right elbow': 'Right Elbow',
    'front head right': 'Front Head Right',
    'front head left': 'Front Head Left',
    'frons right': 'Forehead Right',
    'frons left': 'Forehead Left',
    'left shoulder': 'Left Shoulder',
    'cervicis left': 'Neck Left',
    'left lower leg': 'Left Lower Leg',
    'right lower leg': 'Right Lower Leg',
    'left ankle': 'Left Ankle',
    'right ankle': 'Right Ankle',
    'right foot': 'Right Foot',
    'left foot': 'Left Foot',
    'left upper arm': 'Left Upper Arm',
    'left lower arm': 'Left Lower Arm',
    'facies left': 'Face Left',
    'cervicis right': 'Neck Right',
    'facies right': 'Face Right',
    'right front forearm': 'Right Front Forearm',
    'right wrist': 'Right Wrist',
    'left wrist': 'Left Wrist',
    'left palm': 'Left Palm',
    'right upper chest': 'Right Upper Chest',
    'left upper chest': 'Left Upper Chest',
    'right lower chest': 'Right Lower Chest',
    'left lower chest': 'Left Lower Chest',
    'right shoulder': 'Right Shoulder',
    'right upper arm': 'Right Upper Arm',
    'right diaphragm': 'Right Diaphragm',
    'right lower arm': 'Right Lower Arm',
    'left diaphragm': 'Left Diaphragm',
    'left upper leg': 'Left Upper Leg',
    'left knee': 'Left Knee',
    'left lower thigh': 'Left Lower Thigh',
    'left upper thigh': 'Left Upper Thigh',
    'right upper leg': 'Right Upper Leg',
    'right knee': 'Right Knee',
    'right lower thigh': 'Right Lower Thigh',
    'right upper thigh': 'Right Upper Thigh',
    'right pelvic region': 'Right Pelvic Region',
    'left pelvic region': 'Left Pelvic Region',
    // Back view zones
    'back head left': 'Back Head Left',
    'back head right': 'Back Head Right',
    'left upper back': 'Left Upper Back',
    'left lumbar': 'Left Lumbar',
    'left buttock': 'Left Buttock',
    'left back of knee': 'Left Back of Knee',
    'right back of knee': 'Right Back of Knee',
    'left upper calf': 'Left Upper Calf',
    'right upper calf': 'Right Upper Calf',
    'right lower calf': 'Right Lower Calf',
    'left lower calf': 'Left Lower Calf',
    'left ankle & foot': 'Left Ankle & Foot',
    'right upper forearm': 'Right Upper Forearm',
    'left lower arm': 'Left Lower Arm',
    'left upper forearm': 'Left Upper Forearm',
    'right ankle & foot': 'Right Ankle & Foot',
    'right buttock': 'Right Buttock',
    'lower head left': 'Lower Head Left',
    'lower head right': 'Lower Head Right',
    'neck left': 'Neck Left',
    'right upper back': 'Right Upper Back',
    'left middle back': 'Left Middle Back',
    'right middle back': 'Right Middle Back',
    'left lower forearm': 'Left Lower Forearm',
    'right lumbar': 'Right Lumbar',
    'left sacral': 'Left Sacral',
    'right sacral': 'Right Sacral',
    'right lower forearm': 'Right Lower Forearm',
    'neck right': 'Neck Right',
    // Common prefixes
    left: 'Left',
    right: 'Right',
    upper: 'Upper',
    lower: 'Lower',
    front: 'Front',
    back: 'Back',
    middle: 'Middle'
  },
  bodyDiagram: {
    title: 'Body Diagram',
    subtitle: 'Click on any body zone to record pain level',
    loading: 'Loading body diagram...',
    todaysRecords: "Today's Pain Records",
    unknownZone: 'Unknown Zone',
    instructions: {
      title: 'How to Use',
      step1: 'Click on any body zone to record pain',
      step2: 'Select your pain level (No Pain, Less Pain, Moderate Pain, or Severe Pain)',
      step3: 'Add optional details like symptoms, triggers, and notes',
      step4: 'View your pain data in the logs below'
    },
    view: {
      front: 'Front View',
      back: 'Back View',
      switch: 'Switch View'
    },
    actions: {
      clearAll: 'Clear All',
      sync: 'Sync Now',
      syncing: 'Syncing...'
    },
    status: {
      online: 'Online',
      offline: 'Offline',
      syncing: 'Syncing'
    },
    empty: {
      title: 'No pain records today',
      text: 'Click on body zones above to start tracking your pain'
    },
    pending: 'Pending sync',
    time: {
      unknown: 'Unknown time',
      invalidDate: 'Invalid date',
      invalid: 'Invalid time',
      justNow: 'Just now',
      minutesAgo: 'm ago',
      hoursAgo: 'h ago'
    },
    success: {
      recorded: 'Pain level recorded successfully!',
      withAudio: 'Pain level recorded successfully with audio note!',
      cleared: 'All pain data cleared successfully!'
    },
    confirm: {
      clearAll: 'Are you sure you want to clear all pain data for today?'
    },
    errors: {
      title: 'Error loading body diagram',
      loadFailed: 'Failed to load pain data',
      clearFailed: 'Failed to clear pain data',
      failed: 'Failed to record pain level: {{message}}'
    }
  }
};

const deepMerge = (target, source) => {
  const output = Array.isArray(target) ? [...target] : { ...target };

  Object.keys(source || {}).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = output[key];

    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue)
    ) {
      output[key] = deepMerge(
        targetValue && typeof targetValue === 'object' ? targetValue : {},
        sourceValue
      );
    } else {
      output[key] = sourceValue;
    }
  });

  return output;
};

const createTranslation = (overrides) =>
  deepMerge(JSON.parse(JSON.stringify(baseTranslation)), overrides);

const knOverrides = {
  app: {
    loginPortal: 'ಲಾಗಿನ್ ಪೋರ್ಟಲ್'
  },
  common: {
    back: 'ಹಿಂದಕ್ಕೆ',
    cancel: 'ರದ್ದುಪಡಿಸಿ',
    confirm: 'ದೃಢಪಡಿಸಿ',
    loading: 'ದಯವಿಟ್ಟು ಕಾಯಿರಿ...',
    language: 'ಭಾಷೆ',
    requiredField: 'ದಯವಿಟ್ಟು ಎಲ್ಲಾ ಅಗತ್ಯ ಕ್ಷೇತ್ರಗಳನ್ನು ಭರ್ತಿ ಮಾಡಿ',
    unknownTime: 'ಅಜ್ಞಾತ ಸಮಯ',
    invalidDate: 'ಅಮಾನ್ಯ ದಿನಾಂಕ',
    invalidTime: 'ಅಮಾನ್ಯ ಸಮಯ',
    justNow: 'ಈಗಷ್ಟೇ',
    minutesAgo: 'ನಿಮಿಷಗಳ ಹಿಂದೆ',
    hoursAgo: 'ಗಂಟೆಗಳ ಹಿಂದೆ',
    unknown: 'ಅಜ್ಞಾತ',
    notAvailable: 'ಲಭ್ಯವಿಲ್ಲ'
  },
  languageGate: {
    title: 'ನಿಮ್ಮ ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ',
    subtitle: 'ಪೋರ್ಟಲ್ ಮುಂದುವರಿಸಲು ನಿಮ್ಮ ಇಷ್ಟದ ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ.',
    helper: 'ಲಾಗಿನ್ ಪರದೆಯಿಂದ ಯಾವಾಗ ಬೇಕಾದರೂ ಭಾಷೆಯನ್ನು ಬದಲಾಯಿಸಬಹುದು.',
    helperEmphasis: 'ಚಿಂತೆ ಮಾಡಬೇಡಿ!',
    secondary: 'ಮೊದಲ ಹೆಜ್ಜೆಯಿಂದಲೇ ಮನೆಯಲ್ಲಿ ಇರುವ ಅನುಭವವನ್ನು ನೀಡಲು ಇದು ಸಹಾಯ ಮಾಡುತ್ತದೆ.'
  },
  buttons: {
    login: 'ಲಾಗಿನ್',
    register: 'ನೋಂದಣಿ',
    verifyPasskey: 'ಪಾಸ್‌ಕಿಯನ್ನು ಪರಿಶೀಲಿಸಿ'
  },
  login: {
    initialChoice: {
      login: 'ಲಾಗಿನ್',
      register: 'ನೋಂದಣಿ'
    },
    passkey: {
      prompt: 'ಪಾಸ್‌ಕಿಯನ್ನು ನಮೂದಿಸಿ *',
      helper: 'ರೋಗಿ/ವೈದ್ಯರ ನೋಂದಣಿಗೆ ನೋಂದಣಿ ಪಾಸ್‌ಕಿಯನ್ನು ಅಥವಾ ಆಡ್ಮಿನ್ ಪ್ರವೇಶಕ್ಕೆ ಆಡ್ಮಿನ್ ಪಾಸ್‌ಕಿಯನ್ನು ಬಳಸಿ',
      invalid: 'ಅಮಾನ್ಯ ಪಾಸ್‌ಕಿ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.'
    },
    form: {
      idLabel: 'ಐಡಿ *',
      idPlaceholder: 'ರೋಗಿ ಐಡಿ (DEMOP00001 / HSP-MNG-7F29A1-00001) ಅಥವಾ ವೈದ್ಯರ ಐಡಿ (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      passwordLabel: 'ಪಾಸ್ವರ್ಡ್ *',
      passwordPlaceholder: 'ನಿಮ್ಮ ಪಾಸ್ವರ್ಡ್ ನಮೂದಿಸಿ',
      rememberMe: '90 ದಿನಗಳವರೆಗೆ ನನ್ನನ್ನು ನೆನಪಿನಲ್ಲಿ ಇಡಿ'
    },
    errors: {
      missingId: 'ದಯವಿಟ್ಟು ನಿಮ್ಮ ರೋಗಿ ಐಡಿ ಅಥವಾ ವೈದ್ಯರ ಐಡಿಯನ್ನು ನಮೂದಿಸಿ',
      missingPassword: 'ದಯವಿಟ್ಟು ನಿಮ್ಮ ಪಾಸ್ವರ್ಡ್ ನಮೂದಿಸಿ',
      invalidIdFormat: 'ಅಮಾನ್ಯ ಐಡಿ ರೂಪು. ದಯವಿಟ್ಟು ರೋಗಿ ಐಡಿ (DEMOP00001 / HSP-MNG-7F29A1-00001) ಅಥವಾ ವೈದ್ಯರ ಐಡಿ (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001) ಬಳಸಿ',
      adminLoginDisabled: 'ಆಡ್ಮಿನ್ ಲಾಗಿನ್ ಲಭ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ನೋಂದಣಿ ಆಯ್ಕೆಯ ಮೂಲಕ ಆಡ್ಮಿನ್ ಪಾಸ್‌ಕಿಯನ್ನು ಬಳಸಿ.',
      missingDoctorEmail: 'ವೈದ್ಯರ ನೋಂದಣಿಗೆ ಇಮೇಲ್ ಅಗತ್ಯವಿದೆ',
      missingDoctorPhone: 'ವೈದ್ಯರ ನೋಂದಣಿಗೆ ದೂರವಾಣಿ ಸಂಖ್ಯೆ ಅಗತ್ಯವಿದೆ',
      passwordMismatch: 'ಪಾಸ್ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುತ್ತಿಲ್ಲ',
      passwordLength: 'ಪಾಸ್ವರ್ಡ್ ಕನಿಷ್ಠ 6 ಅಕ್ಷರಗಳಿರಬೇಕು',
      missingPatientDoctor: 'ವೈದ್ಯರ ಆಯ್ಕೆಯನ್ನು ಸೇರಿ ಎಲ್ಲಾ ಅಗತ್ಯ ಕ್ಷೇತ್ರಗಳನ್ನು ದಯವಿಟ್ಟು ಭರ್ತಿ ಮಾಡಿ',
      genericLogin: 'ಲಾಗಿನ್ ವಿಫಲವಾಗಿದೆ',
      loadDoctorsFailed: 'ವೈದ್ಯರ ಪಟ್ಟಿಯನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಪುಟವನ್ನು ರಿಫ್ರೆಶ್ ಮಾಡಿ.',
      noDoctors: 'ಯಾವ ವೈದ್ಯರೂ ಲಭ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ಬೆಂಬಲವನ್ನು ಸಂಪರ್ಕಿಸಿ.'
    },
    success: {
      welcomeDoctor: 'ಮತ್ತೆ ಸ್ವಾಗತ, ಡಾ. {{name}}!',
      welcomePatient: 'ಮತ್ತೆ ಸ್ವಾಗತ!'
    },
    register: {
      title: 'ನಾನು *',
      patient: 'ರೋಗಿ',
      doctor: 'ವೈದ್ಯ',
      firstName: {
        label: 'ಮೊದಲ ಹೆಸರು *',
        placeholder: 'ನಿಮ್ಮ ಮೊದಲ ಹೆಸರನ್ನು ನಮೂದಿಸಿ'
      },
      lastName: {
        label: 'ಕೊನೆಯ ಹೆಸರು (ಐಚ್ಛಿಕ)',
        placeholder: 'ನಿಮ್ಮ ಕೊನೆಯ ಹೆಸರನ್ನು ನಮೂದಿಸಿ (ಐಚ್ಛಿಕ)'
      },
      email: {
        label: 'ಇಮೇಲ್ *',
        placeholder: 'ನಿಮ್ಮ ಇಮೇಲ್ ವಿಳಾಸವನ್ನು ನಮೂದಿಸಿ'
      },
      phone: {
        label: 'ದೂರವಾಣಿ ಸಂಖ್ಯೆ *',
        placeholder: 'ನಿಮ್ಮ ದೂರವಾಣಿ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ'
      },
      selectDoctor: {
        label: 'ನಿಮ್ಮ ವೈದ್ಯರನ್ನು ಆಯ್ಕೆಮಾಡಿ *',
        placeholder: '-- ವೈದ್ಯರನ್ನು ಆಯ್ಕೆಮಾಡಿ --',
        loading: 'ವೈದ್ಯರನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಬೆಂಬಲವನ್ನು ಸಂಪರ್ಕಿಸಿ.',
        noDoctors: 'ಯಾವ ವೈದ್ಯರೂ ಲಭ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ಬೆಂಬಲವನ್ನು ಸಂಪರ್ಕಿಸಿ.'
      },
      dateOfBirth: {
        label: 'ಜನ್ಮ ದಿನಾಂಕ *'
      },
      contactNumber: {
        label: 'ದೂರವಾಣಿ ಸಂಖ್ಯೆ *',
        placeholder: 'ನಿಮ್ಮ ಸಂಪರ್ಕ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ'
      },
      whatsappNumber: {
        label: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆ (ಐಚ್ಛಿಕ) 💬',
        placeholder: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ (ಉದಾ: +1234567890)',
        helper: 'ನೀವು ನೀಡಿದರೆ ಔಷಧ ಸೂಚನೆಗಳು ವಾಟ್ಸಾಪ್ ಮೂಲಕ ಬರುತ್ತವೆ'
      },
      password: {
        label: 'ಪಾಸ್ವರ್ಡ್ *',
        placeholder: 'ನಿಮ್ಮ ಪಾಸ್ವರ್ಡ್ ನಮೂದಿಸಿ'
      },
      confirmPassword: {
        label: 'ಪಾಸ್ವರ್ಡ್ ದೃಢಪಡಿಸಿ *',
        placeholder: 'ನಿಮ್ಮ ಪಾಸ್ವರ್ಡ್ ದೃಢಪಡಿಸಿ'
      },
      passkeyTitle: 'Enter Passkey *',
      passkeyHelper: 'Use registration passkey for patient/doctor registration or admin passkey for admin access'
    }
  },
  toast: {
    adminWelcome: 'ಸ್ವಾಗತ, {{name}}!',
    doctorRegistrationSuccess: 'ನೋಂದಣಿ ಯಶಸ್ವಿಯಾಗಿದೆ! ನಿಮ್ಮ ವೈದ್ಯರ ಐಡಿ: {{id}}. ಮುಂದುವರೆಯಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ.'
  },
  dashboard: {
    loading: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
    welcome: {
      title: 'ಪೇನ್ ಟ್ರ್ಯಾಕರ್‌ಗೆ ಸ್ವಾಗತ',
      subtitle: 'ನಿಮ್ಮ ನೋವಿನ ಮಟ್ಟಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಿ ಮತ್ತು ಆರೋಗ್ಯ ಪ್ರಗತಿಯನ್ನು ಗಮನಿಸಿ'
    },
    stats: {
      logsToday: 'ಇಂದಿನ ದಾಖಲೆಗಳು',
      noPain: 'ನೋವು ಇಲ್ಲ',
      mild: 'ಸೌಮ್ಯ',
      moderatePain: 'ಮಧ್ಯಮ ನೋವು',
      severePain: 'ತೀವ್ರ ನೋವು',
      worstPain: 'ಅತ್ಯಂತ ನೋವು',
      lessPain: 'ಕಡಿಮೆ ನೋವು',
      unknown: 'ಅಪರಿಚಿತ'
    },
    actions: {
      retry: 'ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',
      bodyDiagram: 'ದೇಹದ ಚಿತ್ರ',
      viewLogs: 'ದಾಖಲೆಗಳನ್ನು ನೋಡಿ',
      viewStatistics: 'ಸಂಖ್ಯಾ ವಿವರಗಳನ್ನು ನೋಡಿ',
      history: 'ಇತಿಹಾಸ'
    },
    errors: {
      title: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್ ಲೋಡ್ ಮಾಡುವಲ್ಲಿ ದೋಷ',
      loadFailed: 'ನೋವು ಮಾಹಿತಿಯನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಯಿತು'
    },
    logs: {
      title: 'ಇತ್ತೀಚಿನ ನೋವು ದಾಖಲೆಗಳು',
      emptyTitle: 'ಇಂದು ನೋವು ದಾಖಲೆಗಳಿಲ್ಲ',
      emptyText: 'ದೇಹದ ಭಾಗಗಳ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ ನೋವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಪ್ರಾರಂಭಿಸಿ'
    },
    sidebar: {
      title: 'ಪೇನ್ ಟ್ರ್ಯಾಕರ್',
      links: {
        dashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
        bodyDiagram: 'ದೇಹದ ಚಿತ್ರ',
        painLogs: 'ನೋವು ದಾಖಲೆಗಳು',
        history: 'ಇತಿಹಾಸ',
        statistics: 'ಸಂಖ್ಯಾ ವಿವರಗಳು',
        distress: 'ಮಾನಸಿಕ ಒತ್ತಡ',
        medication: 'ಔಷಧ'
      },
      user: {
        profile: 'ರೋಗಿ ವಿವರಗಳು'
      },
      actions: {
        logout: 'ಲಾಗ್ ಔಟ್'
      }
    }
  },
  painScale: {
    title: 'ನೋವಿನ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಿ',
    vasTitle: 'ವಿಷುಯಲ್ ಅನಲಾಗ್ ಸ್ಕೇಲ್ (VAS)',
    vasDescription: 'ಸ್ಟ್ಯಾಂಡರ್ಡ್ 0–10 ಸ್ಕೇಲ್: 0 = ನೋವು ಇಲ್ಲ, 10 = ಕೆಟ್ಟ ನೋವು',
    vasCurrentValue: 'ನಿಮ್ಮ ನೋವಿನ ಮಟ್ಟ',
    vasWorstPain: 'ಕೆಟ್ಟ ನೋವು',
    vasDescriptors: {
      noPain: 'ನೋವು ಇಲ್ಲ',
      mild: 'ಸೌಮ್ಯ',
      moderate: 'ಮಧ್ಯಮ',
      severe: 'ತೀವ್ರ',
      verySevere: 'ಅತಿ ತೀವ್ರ',
      worstPain: 'ಅತ್ಯಂತ ಕೆಟ್ಟ ನೋವು'
    },
    fixedScales: 'ನಿಗದಿತ ನೋವಿನ ಮಾಪನ: ನೋವು ಇಲ್ಲ (0) • ಕಡಿಮೆ ನೋವು (1-3) • ಮಧ್ಯಮ (4-7) • ತೀವ್ರ (8-10)',
    frontView: '👤 ಮುಂಭಾಗದ ನೋಟ',
    backView: '👥 ಹಿಂದಿನ ನೋಟ',
    levels: {
      noPain: 'ನೋವು ಇಲ್ಲ',
      lessPain: 'ಕಡಿಮೆ ನೋವು',
      moderatePain: 'ಮಧ್ಯಮ ನೋವು',
      severePain: 'ತೀವ್ರ ನೋವು'
    },
    descriptions: {
      noPain: 'ಯಾವುದೇ ಅಸ್ವಸ್ಥತೆ ಇಲ್ಲ',
      lessPain: 'ಸೌಮ್ಯ ಅಸ್ವಸ್ಥತೆ',
      moderatePain: 'ಗಮನಿಸಬಹುದಾದ ಆದರೆ ನಿರ್ವಹಿಸಬಹುದಾದ',
      severePain: 'ತೀವ್ರ ಮತ್ತು ಕಾಳಜಿಯ'
    },
    additionalInfo: 'ಹೆಚ್ಚುವರಿ ಮಾಹಿತಿ (ಐಚ್ಛಿಕ)',
    painScale: 'ನೋವಿನ ಮಾಪನ',
    timeOfDay: 'ದಿನದ ಸಮಯ',
    mood: 'ಮನಸ್ಥಿತಿ',
    activity: 'ಚಟುವಟಿಕೆಯ ಮಟ್ಟ',
    symptoms: 'ಲಕ್ಷಣಗಳು (ಅಲ್ಪವಿರಾಮದಿಂದ ಬೇರ್ಪಡಿಸಲಾಗಿದೆ)',
    triggers: 'ಟ್ರಿಗ್ಗರ್ಗಳು (ಅಲ್ಪವಿರಾಮದಿಂದ ಬೇರ್ಪಡಿಸಲಾಗಿದೆ)',
    notes: 'ಟಿಪ್ಪಣಿಗಳು (ಪಠ್ಯ)',
    audioNote: 'ಅಥವಾ ನಿಮ್ಮ ವಿವರಣೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿ (ಆಡಿಯೋ)',
    placeholders: {
      symptoms: 'ಉದಾ., ಬಿಗಿತ, ಊತ, ಮರವೆ',
      triggers: 'ಉದಾ., ಒತ್ತಡ, ಹವಾಮಾನ, ವ್ಯಾಯಾಮ',
      notes: 'ನಿಮ್ಮ ನೋವಿನ ವಿವರಣೆಯನ್ನು ಇಲ್ಲಿ ಟೈಪ್ ಮಾಡಿ...',
      selectMood: 'ಮನಸ್ಥಿತಿಯನ್ನು ಆಯ್ಕೆಮಾಡಿ (ಐಚ್ಛಿಕ)',
      selectActivity: 'ಚಟುವಟಿಕೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ (ಐಚ್ಛಿಕ)',
      symptomsMultiSelect: 'ಹಲವಾರು ಲಕ್ಷಣಗಳನ್ನು ಆಯ್ಕೆಮಾಡಲು Ctrl/Cmd ಒತ್ತಿ ಹಿಡಿದಿರಿ'
    },
    timeOptions: {
      morning: 'ಬೆಳಿಗ್ಗೆ',
      afternoon: 'ಮಧ್ಯಾಹ್ನ',
      evening: 'ಸಂಜೆ',
      night: 'ರಾತ್ರಿ'
    },
    moodOptions: {
      good: 'ಒಳ್ಳೆಯದು',
      moderate: 'ಮಧ್ಯಮ',
      bad: 'ಕೆಟ್ಟದು'
    },
    activityOptions: {
      able: 'ಸಮರ್ಥ',
      partiallyDependent: 'ಅಂಶಿಕ ಅವಲಂಬಿತ',
      notAble: 'ಸಮರ್ಥವಲ್ಲ'
    },
    symptomOptions: {
      none: 'ಯಾವುದೂ ಇಲ್ಲ',
      vomiting: 'ವಾಂತಿ',
      nausea: 'ವಾಕರಿಕೆ',
      dizziness: 'ತಲೆತಿರುಗುವಿಕೆ',
      headache: 'ತಲೆನೋವು',
      fever: 'ಜ್ವರ',
      fatigue: 'ಅಲಸತೆ',
      shortnessOfBreath: 'ಶ್ವಾಸಕೋಶದ ತೊಂದರೆ',
      cough: 'ಕೆಮ್ಮು',
      swelling: 'ಊತ',
      numbness: 'ಮರವೆ',
      weakness: 'ದೌರ್ಬಲ್ಯ'
    },
    buttons: {
      cancel: 'ರದ್ದುಪಡಿಸಿ',
      clear: 'ಸ್ಪಷ್ಟ',
      record: 'ನೋವನ್ನು ದಾಖಲಿಸಿ'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'ದಿನದ ಸಮಯವನ್ನು ಆಯ್ಕೆಮಾಡಿ',
    success: {
      recorded: 'ನೋವಿನ ಮಟ್ಟವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ದಾಖಲಿಸಲಾಗಿದೆ',
      withAudio: 'ಆಡಿಯೋ ಟಿಪ್ಪಣಿಯೊಂದಿಗೆ ನೋವಿನ ಮಟ್ಟವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ದಾಖಲಿಸಲಾಗಿದೆ'
    },
    errors: {
      failed: 'ನೋವಿನ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಲು ವಿಫಲವಾಗಿದೆ: {{message}}'
    },
    confirm: {
      clearAll: 'ನೀವು ಇಂದಿನ ಎಲ್ಲಾ ನೋವು ಡೇಟಾವನ್ನು ಅಳಿಸಲು ಖಚಿತವಾಗಿ ಬಯಸುವಿರಾ?'
    }
  },
  profile: {
    title: 'ನನ್ನ ಪ್ರೊಫೈಲ್',
    subtitle: 'ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಮಾಹಿತಿಯನ್ನು ನಿರ್ವಹಿಸಿ',
    loading: 'ಪ್ರೊಫೈಲ್ ಲೋಡ್ ಆಗುತ್ತಿದೆ...',
    editWhatsApp: '✏️ ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆಯನ್ನು ಸಂಪಾದಿಸಿ',
    whatsAppLabel: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆ',
    whatsAppPlaceholder: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ (ಉದಾ: +1234567890)',
    whatsAppHelper: 'ಒದಗಿಸಿದರೆ WhatsApp ಮೂಲಕ ಔಷಧಿ ಅಧಿಸೂಚನೆಗಳನ್ನು ಸ್ವೀಕರಿಸುತ್ತೀರಿ',
    save: '💾 ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ',
    saving: 'ಉಳಿಸಲಾಗುತ್ತಿದೆ...',
    cancel: 'ರದ್ದುಪಡಿಸಿ',
    success: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆಯನ್ನು ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ!',
    optional: 'ಐಚ್ಛಿಕ',
    forNotifications: 'ಔಷಧಿ ಅಧಿಸೂಚನೆಗಳಿಗಾಗಿ',
    notProvided: 'ಒದಗಿಸಲಾಗಿಲ್ಲ',
    patientId: 'ರೋಗಿ ಐಡಿ',
    age: 'ವಯಸ್ಸು',
    ageYears: '{{age}} ವರ್ಷಗಳು',
    phoneNumber: 'ದೂರವಾಣಿ ಸಂಖ್ಯೆ',
    whatsAppNumber: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆ',
    notRegistered: 'ನೋಂದಾಯಿಸಲಾಗಿಲ್ಲ',
    notRegisteredMessage: 'ನೋಂದಾಯಿಸಲಾಗಿಲ್ಲ - ನೀವು WhatsApp ಔಷಧಿ ಅಧಿಸೂಚನೆಗಳನ್ನು ಸ್ವೀಕರಿಸುವುದಿಲ್ಲ',
    dateOfBirth: 'ಜನ್ಮ ದಿನಾಂಕ',
    accountStatus: 'ಖಾತೆ ಸ್ಥಿತಿ',
    active: 'ಸಕ್ರಿಯ',
    inactive: 'ನಿಷ್ಕ್ರಿಯ',
    memberSince: 'ಸದಸ್ಯರಾದಂದಿನಿಂದ',
    lastUpdated: 'ಕೊನೆಯದಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ',
    role: 'ಪಾತ್ರ',
    roles: {
      patient: 'ರೋಗಿ',
      doctor: 'ವೈದ್ಯ',
      admin: 'ನಿರ್ವಾಹಕ'
    },
    sections: {
      personalInfo: 'ವೈಯಕ್ತಿಕ ಮಾಹಿತಿ',
      contactInfo: 'ಸಂಪರ್ಕ ಮಾಹಿತಿ',
      accountInfo: 'ಖಾತೆ ಮಾಹಿತಿ'
    },
    errors: {
      authRequired: 'ದೃಢೀಕರಣ ಅಗತ್ಯವಿದೆ',
      updateFailed: 'ವಾಟ್ಸಾಪ್ ಸಂಖ್ಯೆಯನ್ನು ನವೀಕರಿಸಲು ವಿಫಲವಾಗಿದೆ'
    }
  },
  painLogs: {
    title: 'ನೋವು ದಾಖಲೆಗಳು',
    subtitle: 'ನಿಮ್ಮ ದಾಖಲಾದ ನೋವು ನಮೂದುಗಳನ್ನು ಪರಿಶೀಲಿಸಿ ಮತ್ತು ನಿರ್ವಹಿಸಿ.',
    loading: 'ನೋವು ದಾಖಲೆಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    error: 'ನೋವು ದಾಖಲೆಗಳನ್ನು ಲೋಡ್ ಮಾಡುವಲ್ಲಿ ದೋಷ',
    filters: {
      painLevel: 'ನೋವಿನ ಮಟ್ಟ:',
      view: 'ವೀಕ್ಷಣೆ:',
      dateRange: 'ದಿನಾಂಕ ಶ್ರೇಣಿ:',
      allLevels: 'ಎಲ್ಲಾ ಮಟ್ಟಗಳು',
      noPain: 'ನೋವು ಇಲ್ಲ',
      mild: 'ಸೌಮ್ಯ',
      lessPain: 'ಕಡಿಮೆ ನೋವು',
      moderatePain: 'ಮಧ್ಯಮ ನೋವು',
      severePain: 'ತೀವ್ರ ನೋವು',
      worstPain: 'ಅತ್ಯಂತ ನೋವು',
      bothViews: 'ಎರಡೂ ವೀಕ್ಷಣೆಗಳು',
      frontView: 'ಮುಂಭಾಗದ ವೀಕ್ಷಣೆ',
      backView: 'ಹಿಂಭಾಗದ ವೀಕ್ಷಣೆ',
      allTime: 'ಎಲ್ಲಾ ಸಮಯ',
      today: 'ಇಂದು',
      week: 'ಈ ವಾರ',
      month: 'ಈ ತಿಂಗಳು',
      startDate: 'ಪ್ರಾರಂಭ ದಿನಾಂಕ',
      endDate: 'ಅಂತ್ಯ ದಿನಾಂಕ',
      clear: 'ಫಿಲ್ಟರ್‌ಗಳನ್ನು ತೆರವುಗೊಳಿಸಿ',
      searchPlaceholder: 'ದಾಖಲೆಗಳನ್ನು ಹುಡುಕಿ...'
    },
    table: {
      painLevel: 'ನೋವಿನ ಮಟ್ಟ',
      zone: 'ವಲಯ',
      level: 'ಮಟ್ಟ',
      intensity: 'ತೀವ್ರತೆ',
      notes: 'ಟಿಪ್ಪಣಿಗಳು',
      symptoms: 'ರೋಗಲಕ್ಷಣಗಳು',
      triggers: 'ಪ್ರಚೋದಕಗಳು',
      date: 'ದಿನಾಂಕ',
      time: 'ಸಮಯ',
      actions: 'ಕ್ರಿಯೆಗಳು'
    },
    actions: {
      refresh: 'ರಿಫ್ರೆಶ್',
      deleteLogs: 'ದಾಖಲೆಗಳನ್ನು ಅಳಿಸಿ',
      selectAll: 'ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ',
      deselectAll: 'ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆ ರದ್ದುಮಾಡಿ',
      deleteSelected: 'ಆಯ್ಕೆಮಾಡಿದವುಗಳನ್ನು ಅಳಿಸಿ ({{count}})',
      cancel: 'ರದ್ದುಪಡಿಸಿ',
      delete: 'ಅಳಿಸಿ',
      previous: 'ಹಿಂದಿನ',
      next: 'ಮುಂದಿನ'
    },
    deleteMode: {
      info: '🗑️ ಅಳಿಸುವ ಮೋಡ್: ಅಳಿಸಲು ದಾಖಲೆಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ',
      selected: '{{count}} ಆಯ್ಕೆಮಾಡಲಾಗಿದೆ',
      confirm: 'ನೀವು ಆಯ್ಕೆಮಾಡಿದ {{count}} ದಾಖಲೆ(ಗಳನ್ನು) ಅಳಿಸಲು ಖಚಿತವಾಗಿದ್ದೀರಾ? ಈ ಕ್ರಿಯೆಯನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗುವುದಿಲ್ಲ.'
    },
    empty: 'ನೋವು ದಾಖಲೆಗಳು ಕಂಡುಬಂದಿಲ್ಲ',
    emptyText: 'ನಿಮ್ಮ ದಾಖಲೆಗಳನ್ನು ಇಲ್ಲಿ ನೋಡಲು ನಿಮ್ಮ ನೋವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಪ್ರಾರಂಭಿಸಿ.',
    emptyFiltered: 'ಹೆಚ್ಚಿನ ಫಲಿತಾಂಶಗಳನ್ನು ನೋಡಲು ನಿಮ್ಮ ಫಿಲ್ಟರ್‌ಗಳನ್ನು ಹೊಂದಿಸಲು ಪ್ರಯತ್ನಿಸಿ',
    pending: 'ಸಿಂಕ್ ಬಾಕಿ ಇದೆ',
    deleteConfirm: 'ನೀವು ಈ ನೋವು ದಾಖಲೆಯನ್ನು ಅಳಿಸಲು ಖಚಿತವಾಗಿ ಬಯಸುವಿರಾ?',
    deleteSuccess: 'ನೋವು ದಾಖಲೆಯನ್ನು ಯಶಸ್ವಿಯಾಗಿ ಅಳಿಸಲಾಗಿದೆ',
    deleteError: 'ನೋವು ದಾಖಲೆಯನ್ನು ಅಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
    success: {
      deleted: 'ನೋವು ದಾಖಲೆಯನ್ನು ಯಶಸ್ವಿಯಾಗಿ ಅಳಿಸಲಾಗಿದೆ'
    },
    errors: {
      deleteFailed: 'ನೋವು ದಾಖಲೆಯನ್ನು ಅಳಿಸಲು ವಿಫಲವಾಗಿದೆ'
    }
  },
  statistics: {
    title: 'ಸಂಖ್ಯಾ ವಿವರಗಳು',
    vasScaleCaption: 'ನೋವಿನ ಮಟ್ಟಗಳು: VAS 0–10 — ನೋವು ಇಲ್ಲ · ಸೌಮ್ಯ (1–3) · ಮಧ್ಯಮ (4–6) · ತೀವ್ರ (7–9) · ಅತ್ಯಂತ (10)',
    loading: 'ಸಂಖ್ಯಾ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    error: 'ಸಂಖ್ಯಾ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡುವಲ್ಲಿ ದೋಷ',
    loadFailed: 'ಸಂಖ್ಯಾ ವಿವರಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
    painLevelDistribution: 'ನೋವಿನ ಮಟ್ಟದ ವಿತರಣೆ',
    painTrends: 'ಕಾಲಾನಂತರದಲ್ಲಿ ನೋವಿನ ಪ್ರವೃತ್ತಿಗಳು',
    painByZone: 'ದೇಹದ ವಲಯದ ಪ್ರಕಾರ ನೋವು',
    timePeriod: 'ಸಮಯಾವಧಿ',
    filterByRegion: 'ವಲಯದಿಂದ ಫಿಲ್ಟರ್ ಮಾಡಿ',
    lastDays: 'ಕಳೆದ {{count}} ದಿನಗಳು',
    totalLogs: 'ಒಟ್ಟು ದಾಖಲೆಗಳು',
    empty: 'ಯಾವುದೇ ಡೇಟಾ ಲಭ್ಯವಿಲ್ಲ',
    emptyText: 'ಅಂಕಿಅಂಶಗಳು ಮತ್ತು ಪ್ರವೃತ್ತಿಗಳನ್ನು ನೋಡಲು ನಿಮ್ಮ ನೋವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಪ್ರಾರಂಭಿಸಿ',
    labels: {
      severePain: 'ತೀವ್ರ ನೋವು',
      moderatePain: 'ಮಧ್ಯಮ ನೋವು',
      mild: 'ಸೌಮ್ಯ',
      lessPain: 'ಕಡಿಮೆ ನೋವು',
      noPain: 'ನೋವು ಇಲ್ಲ',
      worstPain: 'ಅತ್ಯಂತ ನೋವು',
      intensityOverTime: 'ಸಮಯದ ಮೇಲೆ ನೋವಿನ ತೀವ್ರತೆ',
      painIntensity: 'ನೋವಿನ ತೀವ್ರತೆ',
      painLevel: 'ನೋವಿನ ಮಟ್ಟ',
      time: 'ಸಮಯ',
      notes: 'ಟಿಪ್ಪಣಿಗಳು'
    },
    noDataForRegion: 'ಈ ವಲಯಕ್ಕೆ ಡೇಟಾ ಇಲ್ಲ',
    totalEntries: 'ಒಟ್ಟು ನಮೂದುಗಳು',
    emptyRegionLine: 'ಆಯ್ದ ಕಾಲದಲ್ಲಿ "{{zone}}" ಕ್ಕೆ ನೋವು ದಾಖಲೆಗಳು ಕಂಡುಬಂದಿಲ್ಲ. ಉದ್ದದ ಕಾಲಾವಧಿಯನ್ನು ಆಯ್ಕೆಮಾಡಿ ಅಥವಾ ಬೇರೆ ವಲಯವನ್ನು ಆಯ್ಕೆಮಾಡಿ.',
    noPainTrendsData: 'ಆಯ್ದ ಕಾಲಾವಧಿಗೆ ನೋವು ಪ್ರವೃತ್ತಿ ಡೇಟಾ ಲಭ್ಯವಿಲ್ಲ.',
    emptyRegionBar: 'ಆಯ್ದ ಕಾಲಾವಧಿಯಲ್ಲಿ "{{zone}}" ಕ್ಕೆ ನೋವು ಡೇಟಾ ಕಂಡುಬಂದಿಲ್ಲ.',
    noPainByZoneData: 'ದೇಹದ ವಲಯದ ಪ್ರಕಾರ ನೋವು ಡೇಟಾ ಲಭ್ಯವಿಲ್ಲ.',
    emptyRegionDoughnut: 'ಆಯ್ದ ಕಾಲಾವಧಿಯಲ್ಲಿ "{{zone}}" ಕ್ಕೆ ನೋವಿನ ಮಟ್ಟದ ವಿತರಣೆ ಡೇಟಾ ಕಂಡುಬಂದಿಲ್ಲ.',
    noPainLevelDistributionData: 'ನೋವಿನ ಮಟ್ಟದ ವಿತರಣೆ ಡೇಟಾ ಲಭ್ಯವಿಲ್ಲ.',
    painTimelineFor: '{{zone}} ಗಾಗಿ ನೋವು ಟೈಮ್‌ಲೈನ್',
    allRegions: 'ಎಲ್ಲಾ ವಲಯಗಳು',
    dateRange: 'ದಿನಾಂಕ ಶ್ರೇಣಿ',
    heatmapTitle: 'ವಲಯದ ಪ್ರಕಾರ ನೋವು ಹೀಟ್‌ಮ್ಯಾಪ್',
    heatmap: {
      zone: 'ವಲಯ',
      intensity: 'ತೀವ್ರತೆ',
      logs: 'ದಾಖಲೆಗಳು',
      intensityScale: 'ನೋವಿನ ತೀವ್ರತೆಯ ಮಾಪನ',
      intensityLabels: {
        severe: 'ತೀವ್ರ',
        moderate: 'ಮಧ್ಯಮ',
        less: 'ಕಡಿಮೆ',
        none: 'ಇಲ್ಲ'
      }
    }
  },
  medication: {
    title: 'ಔಷಧ',
    subtitle: 'ನಿಮ್ಮ ವೈದ್ಯರಿಂದ ನಿರ್ದೇಶಿಸಲಾದ ಔಷಧ ಯೋಜನೆಗಳನ್ನು ಪರಿಶೀಲಿಸಿ. ಫಾರ್ಮಸಿ-ಸಿದ್ಧ ಪ್ರತಿಯನ್ನು ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ ಅಥವಾ ಅಗತ್ಯವಿದ್ದಾಗ ಹಿಂದಿನ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳನ್ನು ಮತ್ತೆ ನೋಡಿ.',
    loading: 'ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    empty: {
      title: 'ಇನ್ನೂ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳಿಲ್ಲ',
      message: 'ನಿಮ್ಮ ವೈದ್ಯರು ಇಲ್ಲಿ ಔಷಧ ಯೋಜನೆಗಳನ್ನು ಹಂಚಿಕೊಳ್ಳುತ್ತಾರೆ. ಸಮಾಲೋಚನೆಯ ನಂತರ ಮತ್ತೆ ಪರಿಶೀಲಿಸಿ.'
    },
    description: 'ನಿಮ್ಮ ವೈದ್ಯರಿಂದ ನಿರ್ದೇಶಿಸಲಾದ ಔಷಧ ಯೋಜನೆಗಳನ್ನು ಪರಿಶೀಲಿಸಿ. ಫಾರ್ಮಸಿ-ಸಿದ್ಧ ಪ್ರತಿಯನ್ನು ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ ಅಥವಾ ಅಗತ್ಯವಿದ್ದಾಗ ಹಿಂದಿನ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳನ್ನು ಮತ್ತೆ ನೋಡಿ.',
    legacy: {
      title: 'ಹಳೆಯ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್',
      subtitle: 'ಈ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಅನ್ನು ಹಿಂದಿನ ಫಾರ್ಮಾಟ್ ಬಳಸಿ ಕಳುಹಿಸಲಾಗಿದೆ.',
      badge: 'ಟೆಕ್ಸ್ಟ್-ಆಧಾರಿತ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್'
    },
    current: {
      title: '⭐ ಪ್ರಸ್ತುತ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್',
      badge: 'ಪ್ರಸ್ತುತ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್',
      lastUpdated: 'ಕೊನೆಯ ನವೀಕರಣ',
      notice: 'ಇದು ಅತ್ಯಂತ ಇತ್ತೀಚಿನ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್. ಈ ಸೂಚನೆಗಳನ್ನು ಎಚ್ಚರಿಕೆಯಿಂದ ಅನುಸರಿಸಿ ಮತ್ತು ಔಷಧವನ್ನು ನಿಖರವಾಗಿ ನಿರ್ದೇಶಿಸಿದಂತೆ ನೀಡಲು ನಿಮ್ಮ ಫಾರ್ಮಸಿಸ್ಟ್‌ಗೆ ಡೌನ್‌ಲೋಡ್ ಮಾಡಬಹುದಾದ ಪ್ರತಿಯನ್ನು ಒದಗಿಸಿ.'
    },
    history: {
      title: '📚 ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಇತಿಹಾಸ',
      subtitle: 'ನಿಮ್ಮ ವೈದ್ಯರು ಹಂಚಿಕೊಂಡ ಹಿಂದಿನ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳನ್ನು ಪರಿಶೀಲಿಸಿ.',
      badge: 'ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಇತಿಹಾಸ'
    },
    download: {
      formal: '📥 ಔಪಚಾರಿಕ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ',
      text: '📄 ಪಠ್ಯ ಪ್ರತಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ',
      success: 'ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಲಾಗಿದೆ',
      error: 'ಡೌನ್‌ಲೋಡ್ ಮಾಡಲು ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಲಭ್ಯವಿಲ್ಲ'
    },
    labels: {
      patient: 'ರೋಗಿ',
      doctor: 'ವೈದ್ಯ',
      issuedOn: 'ನೀಡಲಾಗಿದೆ',
      medication: 'ಔಷಧ',
      doseQty: 'ಡೋಸ್ / ಪ್ರಮಾಣ',
      timing: 'ಸಮಯ',
      food: 'ಆಹಾರ',
      duration: 'ಕಾಲಾವಧಿ',
      notes: 'ಟಿಪ್ಪಣಿಗಳು',
      patientInstructions: 'ರೋಗಿ ಸೂಚನೆಗಳು',
      pharmacyNotes: 'ಫಾರ್ಮಸಿ ಟಿಪ್ಪಣಿಗಳು',
      asPrescribed: 'ನಿರ್ದೇಶಿಸಿದಂತೆ',
      asDirected: 'ನಿರ್ದೇಶಿಸಿದಂತೆ',
      id: 'ಐಡಿ:',
      reg: 'ರೆಜ್:',
      contact: 'ಸಂಪರ್ಕ:',
      prescribingDoctor: 'ಪ್ರಿಸ್ಕ್ರೈಬ್ ಮಾಡುವ ವೈದ್ಯ:',
      treatingPhysician: 'ಚಿಕಿತ್ಸೆ ನೀಡುವ ವೈದ್ಯ',
      yourDoctor: 'ನಿಮ್ಮ ವೈದ್ಯ',
      schedule: {
        morning: 'ಬೆಳಿಗ್ಗೆ',
        afternoon: 'ಮಧ್ಯಾಹ್ನ',
        evening: 'ಸಂಜೆ',
        night: 'ರಾತ್ರಿ'
      },
      foodInstruction: {
        before: 'ಆಹಾರದ ಮೊದಲು',
        with: 'ಆಹಾರದೊಂದಿಗೆ',
        after: 'ಆಹಾರದ ನಂತರ',
        none: 'ಆಹಾರವನ್ನು ಅವಲಂಬಿಸಿಲ್ಲ'
      }
    },
    footer: {
      needAssistance: 'ಸಹಾಯ ಬೇಕೇ?',
      shareNote: 'ಪರಿಶೀಲನೆ ಮತ್ತು ಪೂರೈಕೆಗಾಗಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿದ PDF ಅನ್ನು ನಿಮ್ಮ ಫಾರ್ಮಸಿಸ್ಟ್ ಅಥವಾ ಸಲಹೆಗಾರ ವೈದ್ಯರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಿ.'
    },
    success: {
      newPrescription: '💊 ಹೊಸ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಸ್ವೀಕರಿಸಲಾಗಿದೆ',
      downloaded: 'ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಲಾಗಿದೆ'
    },
    errors: {
      loadFailed: 'ಇದೀಗ ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್‌ಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಿಲ್ಲ',
      noPrescription: 'ಡೌನ್‌ಲೋಡ್ ಮಾಡಲು ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್ ಲಭ್ಯವಿಲ್ಲ'
    },
    reminders: {
      title: 'ಔಷಧಿ ಜ್ಞಾಪನೆಗಳು',
      next: 'ಮುಂದಿನ ಜ್ಞಾಪನೆ',
      at: 'ರಲ್ಲಿನ',
      taken: 'ತೆಗೆದುಕೊಳ್ಳಲಾಗಿದೆ! 30 ನಿಮಿಷಗಳಲ್ಲಿ ನೋವನ್ನು ದಾಖಲಿಸಿ',
      iTookIt: 'ನಾನು ತೆಗೆದುಕೊಂಡಿದ್ದೇನೆ',
      takeMedication: 'ಔಷಧಿ ತೆಗೆದುಕೊಳ್ಳಿ: {{name}}',
      recordPainAfter: 'ನಿಮ್ಮ ವೈದ್ಯರಿಗಾಗಿ ತೆಗೆದುಕೊಂಡ 30 ನಿಮಿಷದ ನಂತರ ನೋವನ್ನು ದಾಖಲಿಸಿ.',
      recordPainNow: 'ನಿಮ್ಮ ವೈದ್ಯರು ಪರಿಣಾಮವನ್ನು ನೋಡಲು ಔಷಧಿ ತೆಗೆದುಕೊಂಡ 30 ನಿಮಿಷದ ನಂತರ ಈಗ ನಿಮ್ಮ ನೋವನ್ನು ದಾಖಲಿಸಿ.',
      recordPainNotification: 'ಔಷಧಿ ತೆಗೆದುಕೊಂಡ 30 ನಿಮಿಷದ ನಂತರ ಈಗ ನಿಮ್ಮ ನೋವಿನ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಿ.',
      recordPain: 'ನೋವನ್ನು ದಾಖಲಿಸಿ',
      recordPainPrompt: 'ನಿಮ್ಮ ಔಷಧಿಯ ಪರಿಣಾಮವನ್ನು ನಿಮ್ಮ ವೈದ್ಯರು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಈಗ ನಿಮ್ಮ ನೋವನ್ನು ದಾಖಲಿಸಿ.',
      recordPainNowButton: 'ಈಗ ನೋವನ್ನು ದಾಖಲಿಸಿ',
      upcoming: 'ಮುಂಬರುವ',
      noUpcoming: 'ಯಾವುದೇ ಮುಂಬರುವ ಜ್ಞಾಪನೆಗಳಿಲ್ಲ'
    }
  },
  messages: {
    title: 'ಸಂದೇಶಗಳು',
    loading: 'ಸಂದೇಶಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    error: 'ಸಂದೇಶಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
    empty: 'ಇನ್ನೂ ಸಂದೇಶಗಳಿಲ್ಲ'
  },
  history: {
    title: 'ಇತಿಹಾಸ',
    subtitle: 'ನಿಮ್ಮ ಎಲ್ಲಾ ನೋವು ಟ್ರ್ಯಾಕಿಂಗ್ ಚಟುವಟಿಕೆಗಳ ಸಂಪೂರ್ಣ ದಾಖಲೆ',
    loading: 'ಇತಿಹಾಸವನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    error: {
      title: 'ಇತಿಹಾಸವನ್ನು ಲೋಡ್ ಮಾಡುವಲ್ಲಿ ದೋಷ',
      message: 'ಇತಿಹಾಸ ಡೇಟಾವನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
      retry: 'ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ'
    },
    empty: {
      title: 'ಯಾವುದೇ ದಾಖಲೆಗಳು ಕಂಡುಬಂದಿಲ್ಲ',
      filtered: 'ನಿಮ್ಮ ಫಿಲ್ಟರ್‌ಗಳು ಅಥವಾ ಹುಡುಕಾಟ ಪದಗಳನ್ನು ಹೊಂದಿಸಲು ಪ್ರಯತ್ನಿಸಿ',
      noData: 'ನಿಮ್ಮ ಇತಿಹಾಸವನ್ನು ಇಲ್ಲಿ ನೋಡಲು ನಿಮ್ಮ ನೋವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಪ್ರಾರಂಭಿಸಿ'
    },
    stats: {
      total: 'ಒಟ್ಟು ದಾಖಲೆಗಳು',
      today: 'ಇಂದು',
      thisWeek: 'ಈ ವಾರ',
      thisMonth: 'ಈ ತಿಂಗಳು'
    },
    filters: {
      painLevel: 'ನೋವಿನ ಮಟ್ಟ:',
      view: 'ವೀಕ್ಷಣೆ:',
      dateRange: 'ದಿನಾಂಕ ಶ್ರೇಣಿ:',
      allLevels: 'ಎಲ್ಲಾ ಮಟ್ಟಗಳು',
      noPain: 'ನೋವು ಇಲ್ಲ',
      mild: 'ಸೌಮ್ಯ',
      lessPain: 'ಕಡಿಮೆ ನೋವು',
      moderatePain: 'ಮಧ್ಯಮ ನೋವು',
      severePain: 'ತೀವ್ರ ನೋವು',
      worstPain: 'ಅತ್ಯಂತ ನೋವು',
      bothViews: 'ಎರಡೂ ವೀಕ್ಷಣೆಗಳು',
      frontView: 'ಮುಂಭಾಗದ ವೀಕ್ಷಣೆ',
      backView: 'ಹಿಂಭಾಗದ ವೀಕ್ಷಣೆ',
      allTime: 'ಎಲ್ಲಾ ಸಮಯ',
      today: 'ಇಂದು',
      thisWeek: 'ಈ ವಾರ',
      thisMonth: 'ಈ ತಿಂಗಳು'
    },
    search: {
      placeholder: 'ದಾಖಲೆಗಳನ್ನು ಹುಡುಕಿ...'
    },
    actions: {
      refresh: 'ರಿಫ್ರೆಶ್',
      deleteLogs: '🗑️ ದಾಖಲೆಗಳನ್ನು ಅಳಿಸಿ',
      selectAll: 'ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ',
      deselectAll: 'ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆ ರದ್ದುಮಾಡಿ',
      deleteSelected: 'ಆಯ್ಕೆಮಾಡಿದವುಗಳನ್ನು ಅಳಿಸಿ',
      cancel: 'ರದ್ದುಪಡಿಸಿ'
    },
    deleteMode: {
      title: '🗑️ ಅಳಿಸುವ ಮೋಡ್: ಅಳಿಸಲು ದಾಖಲೆಗಳನ್ನು ಆಯ್ಕೆಮಾಡಿ',
      selected: 'ಆಯ್ಕೆಮಾಡಲಾಗಿದೆ',
      confirm: 'ನೀವು ಆಯ್ಕೆಮಾಡಿದ {{count}} ದಾಖಲೆ(ಗಳನ್ನು) ಅಳಿಸಲು ಖಚಿತವಾಗಿದ್ದೀರಾ? ಈ ಕ್ರಿಯೆಯನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗುವುದಿಲ್ಲ.',
      success: '{{count}} ದಾಖಲೆ(ಗಳನ್ನು) ಯಶಸ್ವಿಯಾಗಿ ಅಳಿಸಲಾಗಿದೆ',
      error: 'ಆಯ್ಕೆಮಾಡಿದ ದಾಖಲೆಗಳನ್ನು ಅಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
      selectAtLeastOne: 'ದಯವಿಟ್ಟು ಕನಿಷ್ಠ ಒಂದು ದಾಖಲೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ'
    },
    header: {
      title: 'ಇತಿಹಾಸ ದಾಖಲೆಗಳು ({{count}})'
    },
    labels: {
      intensity: 'ತೀವ್ರತೆ:',
      symptoms: 'ಲಕ್ಷಣಗಳು:',
      triggers: 'ಪ್ರಚೋದಕಗಳು:',
      view: 'ವೀಕ್ಷಣೆ',
      unknownTime: 'ಅಜ್ಞಾತ ಸಮಯ',
      invalidDate: 'ಅಮಾನ್ಯ ದಿನಾಂಕ',
      noDate: 'ದಿನಾಂಕ ಇಲ್ಲ',
      justNow: 'ಈಗಷ್ಟೇ',
      minutesAgo: 'ನಿಮಿಷಗಳ ಹಿಂದೆ',
      hoursAgo: 'ಗಂಟೆಗಳ ಹಿಂದೆ'
    },
    painLevels: {
      'no-pain': 'ನೋವು ಇಲ್ಲ',
      'mild': 'ಸೌಮ್ಯ',
      'moderate': 'ಮಧ್ಯಮ ನೋವು',
      'severe': 'ತೀವ್ರ ನೋವು',
      'worst-pain': 'ಅತ್ಯಂತ ನೋವು',
      'less-pain': 'ಕಡಿಮೆ ನೋವು',
      'moderate-pain': 'ಮಧ್ಯಮ ನೋವು',
      'severe-pain': 'ತೀವ್ರ ನೋವು'
    }
  },
  distress: {
    title: '🌡️ ಮಾನಸಿಕ ಒತ್ತಡ ಟ್ರ್ಯಾಕರ್',
    subtitle: 'ಕಾಲಾನಂತರದಲ್ಲಿ ನಿಮ್ಮ ಮಾನಸಿಕ ಒತ್ತಡದ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಿ ಮತ್ತು ಮೇಲ್ವಿಚಾರಣೆ ಮಾಡಿ',
    loading: 'ಮಾನಸಿಕ ಒತ್ತಡದ ಇತಿಹಾಸವನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    error: {
      title: 'ಮಾನಸಿಕ ಒತ್ತಡದ ಇತಿಹಾಸವನ್ನು ಲೋಡ್ ಮಾಡುವಲ್ಲಿ ದೋಷ',
      message: 'ಮಾನಸಿಕ ಒತ್ತಡದ ದಾಖಲೆಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
      retry: 'ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ',
      login: 'ಮಾನಸಿಕ ಒತ್ತಡದ ದಾಖಲೆಗಳನ್ನು ವೀಕ್ಷಿಸಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ'
    },
    empty: {
      title: 'ಯಾವುದೇ ಮಾನಸಿಕ ಒತ್ತಡದ ದಾಖಲೆಗಳು ಕಂಡುಬಂದಿಲ್ಲ',
      textFilter: 'ನಿಮ್ಮ ಫಿಲ್ಟರ್ ಮಾನದಂಡಗಳಿಗೆ ಯಾವುದೇ ದಾಖಲೆಗಳು ಹೊಂದಿಕೆಯಾಗುವುದಿಲ್ಲ',
      textNoFilter: 'ಮೇಲಿನ ಥರ್ಮಾಮೀಟರ್ ಬಳಸಿ ನಿಮ್ಮ ಮಾನಸಿಕ ಒತ್ತಡದ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಿ'
    },
    status: {
      offline: 'ಆಫ್‌ಲೈನ್ ಮೋಡ್',
      syncing: 'ಸಿಂಕ್ ಆಗುತ್ತಿದೆ…',
      online: 'ಆನ್‌ಲೈನ್',
      pending: 'ಬಾಕಿ ಇದೆ',
      pendingSync: 'ಸಿಂಕ್ ಬಾಕಿ ಇದೆ',
      pendingSyncEntries: 'ಸಿಂಕ್ ಬಾಕಿ ಇರುವ ನಮೂದುಗಳು',
      syncNow: 'ಈಗ ಸಿಂಕ್ ಮಾಡಿ'
    },
    filters: {
      minScore: 'ಸ್ಕೋರ್:',
      startDate: 'ಪ್ರಾರಂಭ ದಿನಾಂಕ:',
      endDate: 'ಅಂತ್ಯ ದಿನಾಂಕ:',
      allScores: 'ಎಲ್ಲಾ ಸ್ಕೋರ್‌ಗಳು',
      score0: '0+',
      score4: '4+ (ಕ್ಲಿನಿಕಲ್ ಆಗಿ ಗಮನಾರ್ಹ)',
      score7: '7+ (ಹೆಚ್ಚು)',
      clinicallySignificant: '4+ (ಕ್ಲಿನಿಕಲ್ ಆಗಿ ಗಮನಾರ್ಹ)',
      high: '7+ (ಹೆಚ್ಚು)',
      clear: 'ಫಿಲ್ಟರ್‌ಗಳನ್ನು ತೆರವುಗೊಳಿಸಿ'
    },
    stats: {
      totalEntries: 'ಒಟ್ಟು ನಮೂದುಗಳು',
      averageScore: 'ಸರಾಸರಿ ಸ್ಕೋರ್',
      highestScore: 'ಅತಿ ಹೆಚ್ಚು ಸ್ಕೋರ್',
      highDistress: 'ಹೆಚ್ಚು ಮಾನಸಿಕ ಒತ್ತಡ (≥4)'
    },
    labels: {
      distressScore: 'ಮಾನಸಿಕ ಒತ್ತಡದ ಸ್ಕೋರ್:',
      sourcesOfDistress: 'ಮಾನಸಿಕ ಒತ್ತಡದ ಮೂಲಗಳು:',
      notes: 'ಟಿಪ್ಪಣಿಗಳು:',
      extreme: 'ಅತ್ಯಂತ ಮಾನಸಿಕ ಒತ್ತಡ',
      moderateToSevere: 'ಮಧ್ಯಮದಿಂದ ತೀವ್ರ ಮಾನಸಿಕ ಒತ್ತಡ',
      mild: 'ಸೌಮ್ಯ ಮಾನಸಿಕ ಒತ್ತಡ',
      none: 'ಮಾನಸಿಕ ಒತ್ತಡ ಇಲ್ಲ',
      alert: '🚨 ಕ್ಲಿನಿಕಲ್ ಆಗಿ ಗಮನಾರ್ಹ ಮಾನಸಿಕ ಒತ್ತಡ - ನಿಮ್ಮ ವೈದ್ಯರಿಗೆ ತಿಳಿಸಲಾಗಿದೆ'
    },
    header: {
      title: 'ಮಾನಸಿಕ ಒತ್ತಡದ ದಾಖಲೆಗಳು ({{count}})',
      refresh: 'ರಿಫ್ರೆಶ್'
    }
  },
  distressThermometer: {
    title: '🌡️ ಮಾನಸಿಕ ಒತ್ತಡ ಥರ್ಮಾಮೀಟರ್',
    subtitle: 'ಕಳೆದ ವಾರದ ನಿಮ್ಮ ಪ್ರಸ್ತುತ ಮಾನಸಿಕ ಒತ್ತಡದ ಮಟ್ಟವನ್ನು ರೇಟ್ ಮಾಡಿ (0 = ಮಾನಸಿಕ ಒತ್ತಡ ಇಲ್ಲ, 10 = ಅತ್ಯಂತ ಮಾನಸಿಕ ಒತ್ತಡ)',
    guidelines: 'NCCN ಮಾನಸಿಕ ಒತ್ತಡ ಥರ್ಮಾಮೀಟರ್ ಮಾರ್ಗದರ್ಶನಗಳ ಆಧಾರದ ಮೇಲೆ',
    problemList: {
      label: 'ಸಮಸ್ಯೆಗಳ ಪಟ್ಟಿ - ನಿಮ್ಮ ಮಾನಸಿಕ ಒತ್ತಡಕ್ಕೆ ಕಾರಣವೇನು? (ಐಚ್ಛಿಕ)',
      helper: 'ಅನ್ವಯಿಸುವ ಎಲ್ಲವನ್ನೂ ಆಯ್ಕೆಮಾಡಿ (NCCN ಸಮಸ್ಯೆಗಳ ಪಟ್ಟಿ)'
    },
    notes: {
      label: 'ಹೆಚ್ಚುವರಿ ಟಿಪ್ಪಣಿಗಳು (ಐಚ್ಛಿಕ)',
      placeholder: 'ನಿಮ್ಮ ಮಾನಸಿಕ ಒತ್ತಡದ ಬಗ್ಗೆ ಹೆಚ್ಚುವರಿ ವಿವರಗಳನ್ನು ವಿವರಿಸಿ...',
      enterNumberHint: 'ಅಥವಾ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ (0-10):'
    },
    scoreLabels: {
      noDistress: 'ಮಾನಸಿಕ ಒತ್ತಡ ಇಲ್ಲ',
      mildDistress: 'ಸೌಮ್ಯ ಮಾನಸಿಕ ಒತ್ತಡ',
      moderateDistress: 'ಮಧ್ಯಮ ಮಾನಸಿಕ ಒತ್ತಡ',
      severeDistress: 'ತೀವ್ರ ಮಾನಸಿಕ ಒತ್ತಡ',
      extremeDistress: 'ಅತ್ಯಂತ ಮಾನಸಿಕ ಒತ್ತಡ'
    },
    buttons: {
      record: 'ಮಾನಸಿಕ ಒತ್ತಡದ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಿ',
      recording: 'ದಾಖಲಿಸಲಾಗುತ್ತಿದೆ...',
      clear: 'ತೆರವುಗೊಳಿಸಿ'
    },
    alert: '⚠️ NCCN ಎಚ್ಚರಿಕೆ: {{score}} ಸ್ಕೋರ್ ಕ್ಲಿನಿಕಲ್ ಆಗಿ ಗಮನಾರ್ಹ ಮಾನಸಿಕ ಒತ್ತಡವನ್ನು ಸೂಚಿಸುತ್ತದೆ (≥4). ನಿಮ್ಮ ಆರೋಗ್ಯ ಸೇವಾ ಪೂರೈಕೆದಾರರಿಗೆ ತಿಳಿಸಲಾಗುವುದು.',
    success: {
      recorded: 'ಮಾನಸಿಕ ಒತ್ತಡದ ಮಟ್ಟ {{score}} ಯಶಸ್ವಿಯಾಗಿ ದಾಖಲಿಸಲಾಗಿದೆ'
    },
    errors: {
      login: 'ಮಾನಸಿಕ ಒತ್ತಡವನ್ನು ದಾಖಲಿಸಲು ದಯವಿಟ್ಟು ಲಾಗಿನ್ ಮಾಡಿ',
      recordFailed: 'ಮಾನಸಿಕ ಒತ್ತಡವನ್ನು ದಾಖಲಿಸಲು ವಿಫಲವಾಗಿದೆ'
    },
    sources: {
      transportation: 'ಸಾರಿಗೆ',
      housing: 'ವಸತಿ',
      insuranceFinancial: 'ವಿಮಾ/ಹಣಕಾಸು',
      workSchool: 'ಕೆಲಸ/ಶಾಲೆ',
      dealingWithPartner: 'ಭಾಗೀದಾರರೊಂದಿಗೆ ವ್ಯವಹರಿಸುವುದು',
      dealingWithChildren: 'ಮಕ್ಕಳೊಂದಿಗೆ ವ್ಯವಹರಿಸುವುದು',
      familyHealthIssues: 'ಕುಟುಂಬ ಆರೋಗ್ಯ ಸಮಸ್ಯೆಗಳು',
      depression: 'ಖಿನ್ನತೆ',
      fears: 'ಭಯಗಳು',
      nervousness: 'ನರಗಳು',
      sadness: 'ದುಃಖ',
      worry: 'ಚಿಂತೆ',
      lossOfInterest: 'ಸಾಮಾನ್ಯ ಚಟುವಟಿಕೆಗಳಲ್ಲಿ ಆಸಕ್ತಿಯ ನಷ್ಟ',
      spiritualReligiousConcerns: 'ಆಧ್ಯಾತ್ಮಿಕ/ಧಾರ್ಮಿಕ ಕಾಳಜಿಗಳು',
      fatigue: 'ಅಲಸಿಕೆ',
      pain: 'ನೋವು',
      sleepProblems: 'ನಿದ್ರೆ ಸಮಸ್ಯೆಗಳು',
      eatingProblems: 'ತಿನ್ನುವ ಸಮಸ್ಯೆಗಳು',
      bathingDressing: 'ಸ್ನಾನ/ಉಡುಪು',
      breathingProblems: 'ಶ್ವಾಸಕೋಶದ ಸಮಸ್ಯೆಗಳು',
      nausea: 'ವಾಕರಿಕೆ',
      memoryConcentration: 'ನೆನಪು/ಸಾಂದ್ರತೆ',
      mouthSores: 'ಬಾಯಿ ಹುಣ್ಣುಗಳು',
      skinProblems: 'ಚರ್ಮದ ಸಮಸ್ಯೆಗಳು',
      tinglingHandsFeet: 'ಕೈಗಳು/ಕಾಲುಗಳಲ್ಲಿ ಝುಂಝುಂ',
      otherPhysicalProblems: 'ಇತರ ದೈಹಿಕ ಸಮಸ್ಯೆಗಳು'
    }
  },
  bodyParts: {
    'right palm': 'ಬಲ ಅಂಗೈ',
    'left lower abdomen': 'ಎಡ ಕೆಳ ಹೊಟ್ಟೆ',
    'right lower abdomen': 'ಬಲ ಕೆಳ ಹೊಟ್ಟೆ',
    'left upper abdomen': 'ಎಡ ಮೇಲಿನ ಹೊಟ್ಟೆ',
    'right upper abdomen': 'ಬಲ ಮೇಲಿನ ಹೊಟ್ಟೆ',
    'left front forearm': 'ಎಡ ಮುಂಭಾಗದ ಮುಂಗೈ',
    'left elbow': 'ಎಡ ಮೊಣಕೈ',
    'right elbow': 'ಬಲ ಮೊಣಕೈ',
    'front head right': 'ಮುಂಭಾಗದ ತಲೆ ಬಲ',
    'front head left': 'ಮುಂಭಾಗದ ತಲೆ ಎಡ',
    'frons right': 'ಹಣೆ ಬಲ',
    'frons left': 'ಹಣೆ ಎಡ',
    'left shoulder': 'ಎಡ ಭುಜ',
    'cervicis left': 'ಕುತ್ತಿಗೆ ಎಡ',
    'left lower leg': 'ಎಡ ಕೆಳ ಕಾಲು',
    'right lower leg': 'ಬಲ ಕೆಳ ಕಾಲು',
    'left ankle': 'ಎಡ ಕಣ್ಣು',
    'right ankle': 'ಬಲ ಕಣ್ಣು',
    'right foot': 'ಬಲ ಪಾದ',
    'left foot': 'ಎಡ ಪಾದ',
    'left upper arm': 'ಎಡ ಮೇಲಿನ ತೋಳು',
    'left lower arm': 'ಎಡ ಕೆಳ ತೋಳು',
    'facies left': 'ಮುಖ ಎಡ',
    'cervicis right': 'ಕುತ್ತಿಗೆ ಬಲ',
    'facies right': 'ಮುಖ ಬಲ',
    'right front forearm': 'ಬಲ ಮುಂಭಾಗದ ಮುಂಗೈ',
    'right wrist': 'ಬಲ ಮಣಿಕಟ್ಟು',
    'left wrist': 'ಎಡ ಮಣಿಕಟ್ಟು',
    'left palm': 'ಎಡ ಅಂಗೈ',
    'right upper chest': 'ಬಲ ಮೇಲಿನ ಎದೆ',
    'left upper chest': 'ಎಡ ಮೇಲಿನ ಎದೆ',
    'right lower chest': 'ಬಲ ಕೆಳ ಎದೆ',
    'left lower chest': 'ಎಡ ಕೆಳ ಎದೆ',
    'right shoulder': 'ಬಲ ಭುಜ',
    'right upper arm': 'ಬಲ ಮೇಲಿನ ತೋಳು',
    'right diaphragm': 'ಬಲ ಉಸಿರಾಟದ ಸ್ನಾಯು',
    'right lower arm': 'ಬಲ ಕೆಳ ತೋಳು',
    'left diaphragm': 'ಎಡ ಉಸಿರಾಟದ ಸ್ನಾಯು',
    'left upper leg': 'ಎಡ ಮೇಲಿನ ಕಾಲು',
    'left knee': 'ಎಡ ಮೊಣಕಾಲು',
    'left lower thigh': 'ಎಡ ಕೆಳ ತೊಡೆ',
    'left upper thigh': 'ಎಡ ಮೇಲಿನ ತೊಡೆ',
    'right upper leg': 'ಬಲ ಮೇಲಿನ ಕಾಲು',
    'right knee': 'ಬಲ ಮೊಣಕಾಲು',
    'right lower thigh': 'ಬಲ ಕೆಳ ತೊಡೆ',
    'right upper thigh': 'ಬಲ ಮೇಲಿನ ತೊಡೆ',
    'right pelvic region': 'ಬಲ ಶ್ರೋಣಿ ಪ್ರದೇಶ',
    'left pelvic region': 'ಎಡ ಶ್ರೋಣಿ ಪ್ರದೇಶ',
    'back head left': 'ಹಿಂದಿನ ತಲೆ ಎಡ',
    'back head right': 'ಹಿಂದಿನ ತಲೆ ಬಲ',
    'left upper back': 'ಎಡ ಮೇಲಿನ ಬೆನ್ನಿನ',
    'left lumbar': 'ಎಡ ಕಟಿ',
    'left buttock': 'ಎಡ ಸೊಂಟ',
    'left back of knee': 'ಎಡ ಮೊಣಕಾಲಿನ ಹಿಂದೆ',
    'right back of knee': 'ಬಲ ಮೊಣಕಾಲಿನ ಹಿಂದೆ',
    'left upper calf': 'ಎಡ ಮೇಲಿನ ಕಾಲು',
    'right upper calf': 'ಬಲ ಮೇಲಿನ ಕಾಲು',
    'right lower calf': 'ಬಲ ಕೆಳ ಕಾಲು',
    'left lower calf': 'ಎಡ ಕೆಳ ಕಾಲು',
    'left ankle & foot': 'ಎಡ ಕಣ್ಣು ಮತ್ತು ಪಾದ',
    'right upper forearm': 'ಬಲ ಮೇಲಿನ ಮುಂಗೈ',
    'left lower arm': 'ಎಡ ಕೆಳ ತೋಳು',
    'left upper forearm': 'ಎಡ ಮೇಲಿನ ಮುಂಗೈ',
    'right ankle & foot': 'ಬಲ ಕಣ್ಣು ಮತ್ತು ಪಾದ',
    'right buttock': 'ಬಲ ಸೊಂಟ',
    'lower head left': 'ಕೆಳ ತಲೆ ಎಡ',
    'lower head right': 'ಕೆಳ ತಲೆ ಬಲ',
    'neck left': 'ಕುತ್ತಿಗೆ ಎಡ',
    'right upper back': 'ಬಲ ಮೇಲಿನ ಬೆನ್ನಿನ',
    'left middle back': 'ಎಡ ಮಧ್ಯ ಬೆನ್ನಿನ',
    'right middle back': 'ಬಲ ಮಧ್ಯ ಬೆನ್ನಿನ',
    'left lower forearm': 'ಎಡ ಕೆಳ ಮುಂಗೈ',
    'right lumbar': 'ಬಲ ಕಟಿ',
    'left sacral': 'ಎಡ ತ್ರಿಕಾಸ್ಥಿ',
    'right sacral': 'ಬಲ ತ್ರಿಕಾಸ್ಥಿ',
    'right lower forearm': 'ಬಲ ಕೆಳ ಮುಂಗೈ',
    'neck right': 'ಕುತ್ತಿಗೆ ಬಲ',
    left: 'ಎಡ',
    right: 'ಬಲ',
    upper: 'ಮೇಲಿನ',
    lower: 'ಕೆಳ',
    front: 'ಮುಂಭಾಗ',
    back: 'ಹಿಂದೆ',
    middle: 'ಮಧ್ಯ'
  },
  bodyDiagram: {
    selectTimeOfDay: 'ದಿನದ ಸಮಯವನ್ನು ಆಯ್ಕೆಮಾಡಿ',
    title: 'ದೇಹದ ಚಿತ್ರ',
    subtitle: 'ನೋವಿನ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಲು ಯಾವುದೇ ದೇಹದ ಭಾಗದ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ',
    loading: 'ದೇಹದ ಚಿತ್ರವನ್ನು ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    todaysRecords: 'ಇಂದಿನ ನೋವು ದಾಖಲೆಗಳು',
    unknownZone: 'ಅಜ್ಞಾತ ಭಾಗ',
    instructions: {
      title: 'ಹೇಗೆ ಬಳಸುವುದು',
      step1: 'ನೋವನ್ನು ದಾಖಲಿಸಲು ಯಾವುದೇ ದೇಹದ ಭಾಗದ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ',
      step2: 'ನಿಮ್ಮ ನೋವಿನ ಮಟ್ಟವನ್ನು ಆಯ್ಕೆಮಾಡಿ (ನೋವು ಇಲ್ಲ, ಕಡಿಮೆ ನೋವು, ಮಧ್ಯಮ ನೋವು, ಅಥವಾ ತೀವ್ರ ನೋವು)',
      step3: 'ಲಕ್ಷಣಗಳು, ಟ್ರಿಗ್ಗರ್ಗಳು ಮತ್ತು ಟಿಪ್ಪಣಿಗಳಂತಹ ಹೆಚ್ಚುವರಿ ವಿವರಗಳನ್ನು ಸೇರಿಸಿ',
      step4: 'ಕೆಳಗಿನ ದಾಖಲೆಗಳಲ್ಲಿ ನಿಮ್ಮ ನೋವು ಡೇಟಾವನ್ನು ನೋಡಿ'
    },
    view: {
      front: 'ಮುಂಭಾಗದ ನೋಟ',
      back: 'ಹಿಂದಿನ ನೋಟ',
      switch: 'ನೋಟವನ್ನು ಬದಲಾಯಿಸಿ'
    },
    actions: {
      clearAll: 'ಎಲ್ಲಾ ಅಳಿಸಿ',
      sync: 'ಈಗ ಸಿಂಕ್ ಮಾಡಿ',
      syncing: 'ಸಿಂಕ್ ಮಾಡಲಾಗುತ್ತಿದೆ...'
    },
    status: {
      online: 'ಆನ್‌ಲೈನ್',
      offline: 'ಆಫ್‌ಲೈನ್',
      syncing: 'ಸಿಂಕ್ ಮಾಡಲಾಗುತ್ತಿದೆ'
    },
    empty: {
      title: 'ಇಂದು ನೋವು ದಾಖಲೆಗಳಿಲ್ಲ',
      text: 'ನಿಮ್ಮ ನೋವನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಪ್ರಾರಂಭಿಸಲು ಮೇಲಿನ ದೇಹದ ಭಾಗಗಳ ಮೇಲೆ ಕ್ಲಿಕ್ ಮಾಡಿ'
    },
    pending: 'ಸಿಂಕ್ ಮಾಡಲು ಬಾಕಿ',
    time: {
      unknown: 'ಅಜ್ಞಾತ ಸಮಯ',
      invalidDate: 'ಅಮಾನ್ಯ ದಿನಾಂಕ',
      invalid: 'ಅಮಾನ್ಯ ಸಮಯ',
      justNow: 'ಈಗ',
      minutesAgo: 'ನಿಮಿಷಗಳ ಹಿಂದೆ',
      hoursAgo: 'ಗಂಟೆಗಳ ಹಿಂದೆ'
    },
    success: {
      recorded: 'ನೋವಿನ ಮಟ್ಟವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ದಾಖಲಿಸಲಾಗಿದೆ!',
      withAudio: 'ಆಡಿಯೋ ಟಿಪ್ಪಣಿಯೊಂದಿಗೆ ನೋವಿನ ಮಟ್ಟವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ದಾಖಲಿಸಲಾಗಿದೆ!',
      cleared: 'ಎಲ್ಲಾ ನೋವು ಡೇಟಾವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ಅಳಿಸಲಾಗಿದೆ!'
    },
    confirm: {
      clearAll: 'ಇಂದಿನ ಎಲ್ಲಾ ನೋವು ಮಾಹಿತಿಯನ್ನು ತೆರವುಗೊಳಿಸಲು ನೀವು ಖಚಿತವಾಗಿರುವಿರಾ?'
    },
    errors: {
      title: 'ದೇಹದ ಚಿತ್ರವನ್ನು ಲೋಡ್ ಮಾಡುವಲ್ಲಿ ದೋಷ',
      loadFailed: 'ನೋವು ಡೇಟಾವನ್ನು ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
      clearFailed: 'ನೋವು ಡೇಟಾವನ್ನು ಅಳಿಸಲು ವಿಫಲವಾಗಿದೆ',
      failed: 'ನೋವಿನ ಮಟ್ಟವನ್ನು ದಾಖಲಿಸಲು ವಿಫಲವಾಗಿದೆ: {{message}}'
    }
  },
  audioRecorder: {
    title: 'ಧ್ವನಿ ರೆಕಾರ್ಡಿಂಗ್',
    help: {
      start: 'ನಿಮ್ಮ ನೋವಿನ ವಿವರಣೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಲು "ರೆಕಾರ್ಡಿಂಗ್ ಪ್ರಾರಂಭಿಸಿ" ಕ್ಲಿಕ್ ಮಾಡಿ.',
      recording: 'ರೆಕಾರ್ಡಿಂಗ್ ಪ್ರಗತಿಯಲ್ಲಿದೆ... ಮುಗಿದ ನಂತರ "ರೆಕಾರ್ಡಿಂಗ್ ನಿಲ್ಲಿಸಿ" ಕ್ಲಿಕ್ ಮಾಡಿ.',
      recorded: 'ಆಡಿಯೋ ಯಶಸ್ವಿಯಾಗಿ ರೆಕಾರ್ಡ್ ಮಾಡಲಾಗಿದೆ! ನೀವು ಪ್ಲೇ ಮಾಡಬಹುದು, ಅಳಿಸಬಹುದು ಅಥವಾ ಹೊಸದನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಬಹುದು.'
    },
    buttons: {
      start: 'ರೆಕಾರ್ಡಿಂಗ್ ಪ್ರಾರಂಭಿಸಿ',
      stop: 'ರೆಕಾರ್ಡಿಂಗ್ ನಿಲ್ಲಿಸಿ',
      play: 'ರೆಕಾರ್ಡಿಂಗ್ ಪ್ಲೇ ಮಾಡಿ',
      pause: 'ವಿರಾಮ',
      delete: 'ಅಳಿಸಿ',
      rerecord: 'ಮತ್ತೆ ರೆಕಾರ್ಡ್ ಮಾಡಿ'
    },
    info: {
      duration: 'ಅವಧಿ',
      format: 'ಫಾರ್ಮಾಟ್'
    },
    errors: {
      microphoneAccess: 'ಮೈಕ್ರೋಫೋನ್ ಪ್ರವೇಶಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಬ್ರೌಸರ್ ಅನುಮತಿಗಳನ್ನು ಪರಿಶೀಲಿಸಿ.'
    }
  },
  audioPlayer: {
    label: 'ಧ್ವನಿ ಟಿಪ್ಪಣಿ',
    loading: 'ಆಡಿಯೋ ಲೋಡ್ ಮಾಡಲಾಗುತ್ತಿದೆ...',
    errors: {
      loadFailed: 'ಆಡಿಯೋ ಲೋಡ್ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ: {{message}}',
      notLoaded: 'ಆಡಿಯೋ ಇನ್ನೂ ಲೋಡ್ ಆಗಿಲ್ಲ',
      playFailed: 'ಆಡಿಯೋ ಪ್ಲೇ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ',
      playFailedWithMessage: 'ಆಡಿಯೋ ಪ್ಲೇ ಮಾಡಲು ವಿಫಲವಾಗಿದೆ: {{message}}'
    }
  }
};

const taOverrides = {
  app: {
    loginPortal: 'உள்நுழைவு வாயில்'
  },
  common: {
    back: 'மீண்டும்',
    cancel: 'ரத்து செய்',
    confirm: 'உறுதிப்படுத்து',
    loading: 'தயவுசெய்து காத்திருக்கவும்...',
    language: 'மொழி',
    requiredField: 'தேவைப்படும் அனைத்துப் புலங்களையும் நிரப்பவும்'
  },
  languageGate: {
    title: 'உங்கள் மொழியைத் தேர்ந்தெடுக்கவும்',
    subtitle: 'போர்டலுக்குத் தொடர நீங்கள் விரும்பும் மொழியைத் தேர்ந்தெடுக்கவும்.',
    helper: 'உள்நுழைவு திரையில் இருந்து எப்போதும் மொழியை மாற்றலாம்.',
    helperEmphasis: 'கவலைப்பட வேண்டாம்!',
    secondary: 'மొத்தம் படியிலேயே வீட்டில் இருப்பதுபோல் உணர்வதற்கு இது உதவும்.'
  },
  buttons: {
    login: 'உள்நுழை',
    register: 'பதிவு',
    verifyPasskey: 'பாஸ்கியை சரிபார்க்கவும்'
  },
  login: {
    initialChoice: {
      login: 'உள்நுழை',
      register: 'பதிவு'
    },
    passkey: {
      prompt: 'பாஸ்கியை உள்ளிடவும் *',
      helper: 'நோயாளர்/மருத்துவர் பதிவு செய்ய பதிவு பாஸ்கியை அல்லது நிர்வாக அணுகலுக்கு நிர்வாக பாஸ்கியை பயன்படுத்தவும்',
      invalid: 'தவறான பாஸ்கி. தயவுசெய்து மீண்டும் முயற்சிக்கவும்.'
    },
    form: {
      idLabel: 'அடையாளம் *',
      idPlaceholder: 'Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) அல்லது Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      passwordLabel: 'கடவுச்சொல் *',
      passwordPlaceholder: 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்',
      rememberMe: '90 நாட்களுக்கு என்னை நினைவில் கொள்ளவும்'
    },
    errors: {
      missingId: 'தயவுசெய்து உங்கள் Patient ID அல்லது Doctor ID‌ஐ உள்ளிடவும்',
      missingPassword: 'தயவுசெய்து உங்கள் கடவுச்சொல்லை உள்ளிடவும்',
      invalidIdFormat: 'தவறான ID வடிவம். தயவுசெய்து Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) அல்லது Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001) பயன்படுத்தவும்',
      adminLoginDisabled: 'நிர்வாக உள்நுழைவு கிடைக்கவில்லை. பதிவு விருப்பத்தின் மூலம் நிர்வாக பாஸ்கியை பயன்படுத்தவும்.',
      missingDoctorEmail: 'மருத்துவர் பதிவு செய்ய மின்னஞ்சல் அவசியம்',
      missingDoctorPhone: 'மருத்துவர் பதிவு செய்ய தொலைபேசி எண் அவசியம்',
      passwordMismatch: 'கடவுச்சொற்கள் பொருந்தவில்லை',
      passwordLength: 'கடவுச்சொல் குறைந்தது 6 எழுத்துகள் இருக்க வேண்டும்',
      missingPatientDoctor: 'மருத்துவர் தேர்வையும் சேர்த்து அனைத்து தேவையான புலங்களையும் நிரப்பவும்',
      genericLogin: 'உள்நுழைவு தோல்வியடைந்தது',
      loadDoctorsFailed: 'மருத்துவர் பட்டியலை ஏற்ற முடியவில்லை. தயவுசெய்து பக்கத்தை புதுப்பிக்கவும்.',
      noDoctors: 'மருத்தவர்கள் இல்லை. தயவுசெய்து உதவியை தொடர்பு கொள்ளவும்.'
    },
    success: {
      welcomeDoctor: 'மீண்டும் வரவேற்கிறோம், டாக்டர் {{name}}!',
      welcomePatient: 'மீண்டும் வரவேற்கிறோம்!'
    },
    register: {
      title: 'நான் *',
      patient: 'நோயாளர்',
      doctor: 'மருத்துவர்',
      firstName: {
        label: 'மొதலி பெயர் *',
        placeholder: 'உங்கள் மொதலி பெயரை உள்ளிடவும்'
      },
      lastName: {
        label: 'அவஸாந பெயர் (விருப்பம்)',
        placeholder: 'உங்கள் அவஸாந பெயரை உள்ளிடவும் (விருப்பம்)'
      },
      email: {
        label: 'இமেইল *',
        placeholder: 'உங்கள் மின்னஞ்சலை உள்ளிடவும்'
      },
      phone: {
        label: 'தொலைபேசி எண் *',
        placeholder: 'உங்கள் தொலைபேசி எண்ணை உள்ளிடவும்'
      },
      selectDoctor: {
        label: 'உங்கள் மருத்துவரைத் தேர்ந்தெடுக்கவும் *',
        placeholder: '-- ஒரு மருத்துவரைத் தேர்ந்தெடுக்கவும் --',
        loading: 'மருத்தவர்கள் ஏற்றப்படுகின்றனர்...',
        noDoctors: 'மருத்தவர்கள் இல்லை. தயவுசெய்து உதவியை தொடர்பு கொள்ளவும்.'
      },
      dateOfBirth: {
        label: 'பிறந்த தேதி *'
      },
      contactNumber: {
        label: 'தொடர்பு எண் *',
        placeholder: 'உங்கள் தொடர்பு எண்ணை உள்ளிடவும்'
      },
      whatsappNumber: {
        label: 'வாட்ஸ்அப் எண் (விருப்பம்) 💬',
        placeholder: 'வாட்ஸ்அப் எண்ணை உள்ளிடவும் (உ.தா. +1234567890)',
        helper: 'நீங்கள் வழங்கினால் மருந்து அறிவிப்புகள் வாட்ஸ்அப் மூலம் வரும்'
      },
      password: {
        label: 'கடவுச்சொல் *',
        placeholder: 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்'
      },
      confirmPassword: {
        label: 'கடவுச்சொல் உறுதிப்படுத்து *',
        placeholder: 'உங்கள் கடவுச்சொல்லை உறுதிப்படுத்து'
      },
      passkeyTitle: 'பாஸ்கியை உள்ளிடவும் *',
      passkeyHelper: 'நோயாளர்/மருத்துவர் பதிவு செய்ய பதிவு பாஸ்கியை அல்லது நிர்வாக அணுகலுக்கு நிர்வாக பாஸ்கியை பயன்படுத்தவும்'
    }
  },
  toast: {
    adminWelcome: 'வரவேற்கிறோம், {{name}}!',
    doctorRegistrationSuccess: 'பதிவு வெற்றிகரமாக முடிந்தது! உங்கள் மருத்துவர் ஐடி: {{id}}. தொடர உள்நுழைக.'
  },
  dashboard: {
    loading: 'டாஷ்போர்டு ஏற்றப்படுகிறது...',
    welcome: {
      title: 'பெயின் டிராக்கருக்கு வரவேற்கிறோம்',
      subtitle: 'உங்கள் வலி நிலைகள் மற்றும் ஆரோக்கிய முன்னேற்றத்தை கண்காணிக்கவும்'
    },
    stats: {
      logsToday: 'இன்றைய பதிவுகள்',
      noPain: 'வலி இல்லை',
      mild: 'லேசான',
      moderatePain: 'மிதமான வலி',
      severePain: 'கடுமையான வலி',
      worstPain: 'மோசமான வலி',
      lessPain: 'குறைந்த வலி',
      unknown: 'தெரியவில்லை'
    },
    actions: {
      retry: 'மீண்டும் முயலவும்',
      bodyDiagram: 'உடல் வரைபடம்',
      viewLogs: 'பதிவுகளைப் பார்க்க',
      viewStatistics: 'புள்ளிவிவரங்களைப் பார்க்க',
      history: 'வரலாறு'
    },
    errors: {
      title: 'டாஷ்போர்டை ஏற்றும்போது பிழை',
      loadFailed: 'வலி தரவை ஏற்ற முடியவில்லை'
    },
    logs: {
      title: 'அல்லிக்குள் வலி பதிவுகள்',
      emptyTitle: 'இன்று எந்த வலி பதிவும் இல்லை',
      emptyText: 'உடல் பகுதிகளை கிளிக் செய்து வலியை பதிவு செய்யத் தொடங்குங்கள்'
    },
    sidebar: {
      title: 'பெயின் டிராக்கர்',
      links: {
        dashboard: 'டாஷ்போர்டு',
        bodyDiagram: 'உடல் வரைபடம்',
        painLogs: 'வலி பதிவுகள்',
        history: 'வரலாறு',
        statistics: 'புள்ளிவிவரங்கள்',
        distress: 'உளைச்சல்',
        medication: 'மருந்துகள்'
      },
      user: {
        profile: 'நோயாளி விவரம்'
      },
      actions: {
        logout: 'வெளியேறு'
      }
    }
  },
  painScale: {
    title: 'வலி நிலையை பதிவு செய்யவும்',
    vasTitle: 'விசுவல் அனலாக் ஸ்கேல் (VAS)',
    vasDescription: 'நிலையான 0–10 அளவு: 0 = வலி இல்லை, 10 = மோசமான வலி',
    vasCurrentValue: 'உங்கள் வலி நிலை',
    vasWorstPain: 'மோசமான வலி',
    vasDescriptors: {
      noPain: 'வலி இல்லை',
      mild: 'லேசான',
      moderate: 'மிதமான',
      severe: 'கடுமையான',
      verySevere: 'மிக கடுமையான',
      worstPain: 'மோசமான வலி'
    },
    fixedScales: 'நிலையான வலி அளவுகள்: வலி இல்லை (0) • குறைந்த வலி (1-3) • மிதமான (4-7) • கடுமையான (8-10)',
    frontView: '👤 முன் பார்வை',
    backView: '👥 பின் பார்வை',
    levels: {
      noPain: 'வலி இல்லை',
      lessPain: 'குறைந்த வலி',
      moderatePain: 'மிதமான வலி',
      severePain: 'கடுமையான வலி'
    },
    descriptions: {
      noPain: 'எந்த அசௌகரியமும் இல்லை',
      lessPain: 'லேசான அசௌகரியம்',
      moderatePain: 'கவனிக்கத்தக்க ஆனால் நிர்வகிக்கக்கூடிய',
      severePain: 'தீவிரமான மற்றும் கவலைக்குரிய'
    },
    additionalInfo: 'கூடுதல் தகவல் (விருப்பமானது)',
    painScale: 'வலி அளவு',
    timeOfDay: 'நாளின் நேரம்',
    mood: 'மனநிலை',
    activity: 'செயல்பாட்டு நிலை',
    symptoms: 'அறிகுறிகள் (காற்புள்ளியால் பிரிக்கப்பட்டது)',
    triggers: 'தூண்டிகள் (காற்புள்ளியால் பிரிக்கப்பட்டது)',
    notes: 'குறிப்புகள் (உரை)',
    audioNote: 'அல்லது உங்கள் விளக்கத்தை பதிவு செய்யவும் (ஆடியோ)',
    placeholders: {
      symptoms: 'எ.கா., விறைப்பு, வீக்கம், மரத்தல்',
      triggers: 'எ.கா., மன அழுத்தம், வானிலை, உடற்பயிற்சி',
      notes: 'உங்கள் வலி விளக்கத்தை இங்கே தட்டச்சு செய்யவும்...',
      selectMood: 'மனநிலையைத் தேர்ந்தெடுக்கவும் (விருப்பமானது)',
      selectActivity: 'செயல்பாட்டைத் தேர்ந்தெடுக்கவும் (விருப்பமானது)',
      symptomsMultiSelect: 'பல அறிகுறிகளைத் தேர்ந்தெடுக்க Ctrl/Cmd ஐ அழுத்தி வைக்கவும்'
    },
    timeOptions: {
      morning: 'காலை',
      afternoon: 'மதியம்',
      evening: 'மாலை',
      night: 'இரவு'
    },
    moodOptions: {
      good: 'நல்லது',
      moderate: 'மிதமான',
      bad: 'மோசமான'
    },
    activityOptions: {
      able: 'முடியும்',
      partiallyDependent: 'பகுதி சார்பு',
      notAble: 'முடியாது'
    },
    symptomOptions: {
      none: 'எதுவும் இல்லை',
      vomiting: 'வாந்தி',
      nausea: 'குமட்டல்',
      dizziness: 'தலைச்சுற்றல்',
      headache: 'தலைவலி',
      fever: 'காய்ச்சல்',
      fatigue: 'சோர்வு',
      shortnessOfBreath: 'மூச்சுத்திணறல்',
      cough: 'இருமல்',
      swelling: 'வீக்கம்',
      numbness: 'மரப்பு',
      weakness: 'பலவீனம்'
    },
    buttons: {
      cancel: 'ரத்து செய்',
      clear: 'அழிக்க',
      record: 'வலியை பதிவு செய்'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'நாளின் நேரத்தைத் தேர்ந்தெடுக்கவும்',
    success: {
      recorded: 'வலி நிலை வெற்றிகரமாக பதிவு செய்யப்பட்டது',
      withAudio: 'ஆடியோ குறிப்புடன் வலி நிலை வெற்றிகரமாக பதிவு செய்யப்பட்டது'
    },
    errors: {
      failed: 'வலி நிலையை பதிவு செய்ய முடியவில்லை: {{message}}'
    },
    confirm: {
      clearAll: 'இன்றைய அனைத்து வலி தரவையும் அழிக்க விரும்புகிறீர்களா?'
    }
  },
  profile: {
    title: 'எனது சுயவிவரம்',
    subtitle: 'உங்கள் தனிப்பட்ட தகவல்களை நிர்வகிக்கவும்',
    loading: 'சுயவிவரம் ஏற்றப்படுகிறது...',
    editWhatsApp: '✏️ வாட்ஸ்அப் எண்ணை திருத்தவும்',
    whatsAppLabel: 'வாட்ஸ்அப் எண்',
    whatsAppPlaceholder: 'வாட்ஸ்அப் எண்ணை உள்ளிடவும் (உ.தா. +1234567890)',
    whatsAppHelper: 'நீங்கள் வழங்கினால் மருந்து அறிவிப்புகள் வாட்ஸ்அப் மூலம் வரும். அழிக்க வெற்றிடமாக விடவும்.',
    save: '💾 மாற்றங்களை சேமிக்கவும்',
    saving: 'சேமிக்கப்படுகிறது...',
    cancel: 'ரத்து செய்',
    success: 'வாட்ஸ்அப் எண் வெற்றிகரமாக புதுப்பிக்கப்பட்டது!',
    optional: 'விருப்பமானது',
    forNotifications: 'மருந்து அறிவிப்புகளுக்கு',
    notProvided: 'வழங்கப்படவில்லை',
    patientId: 'நோயாளி ஐடி',
    age: 'வயது',
    sections: {
      personalInfo: 'தனிப்பட்ட தகவல்',
      contactInfo: 'தொடர்பு தகவல்'
    },
    errors: {
      authRequired: 'அங்கீகாரம் தேவை',
      updateFailed: 'வாட்ஸ்அப் எண்ணை புதுப்பிக்க முடியவில்லை'
    }
  },
  painLogs: {
    title: 'வலி பதிவுகள்',
    subtitle: 'உங்கள் பதிவுசெய்யப்பட்ட வலி நுழைவுகளை மதிப்பாய்வு செய்து நிர்வகிக்கவும்.',
    loading: 'வலி பதிவுகள் ஏற்றப்படுகின்றன...',
    error: 'வலி பதிவுகளை ஏற்றுவதில் பிழை',
    filters: {
      painLevel: 'வலி நிலை:',
      view: 'காட்சி:',
      dateRange: 'தேதி வரம்பு:',
      allLevels: 'அனைத்து நிலைகள்',
      noPain: 'வலி இல்லை',
      mild: 'லேசான',
      lessPain: 'குறைந்த வலி',
      moderatePain: 'மிதமான வலி',
      severePain: 'கடுமையான வலி',
      worstPain: 'மோசமான வலி',
      bothViews: 'இரண்டு காட்சிகளும்',
      frontView: 'முன் காட்சி',
      backView: 'பின் காட்சி',
      allTime: 'அனைத்து நேரம்',
      today: 'இன்று',
      week: 'இந்த வாரம்',
      month: 'இந்த மாதம்',
      startDate: 'தொடக்க தேதி',
      endDate: 'முடிவு தேதி',
      clear: 'வடிகட்டிகளை அழிக்கவும்',
      searchPlaceholder: 'பதிவுகளைத் தேடவும்...'
    },
    table: {
      painLevel: 'வலி நிலை',
      zone: 'உடல் பகுதி',
      level: 'நிலை',
      intensity: 'தீவிரம்',
      notes: 'குறிப்புகள்',
      symptoms: 'அறிகுறிகள்',
      triggers: 'தூண்டிகள்',
      date: 'தேதி',
      time: 'நேரம்',
      actions: 'செயல்கள்'
    },
    actions: {
      refresh: 'புதுப்பிக்க',
      deleteLogs: 'பதிவுகளை அழிக்க',
      selectAll: 'அனைத்தையும் தேர்ந்தெடு',
      deselectAll: 'அனைத்தையும் தேர்வு நீக்க',
      deleteSelected: 'தேர்ந்தெடுக்கப்பட்டவற்றை அழிக்க ({{count}})',
      cancel: 'ரத்து செய்',
      delete: 'அழி',
      previous: 'முந்தைய',
      next: 'அடுத்து'
    },
    deleteMode: {
      info: '🗑️ அழிப்பு முறை: அழிக்க பதிவுகளைத் தேர்ந்தெடுக்கவும்',
      selected: '{{count}} தேர்ந்தெடுக்கப்பட்டது',
      confirm: 'நீங்கள் தேர்ந்தெடுத்த {{count}} பதிவு(களை) அழிக்க விரும்புகிறீர்களா? இந்த செயலை திரும்பப் பெற முடியாது.'
    },
    empty: 'வலி பதிவுகள் காணப்படவில்லை',
    emptyText: 'உங்கள் பதிவுகளை இங்கே பார்க்க உங்கள் வலியை கண்காணிக்கத் தொடங்கவும்.',
    emptyFiltered: 'மேலும் முடிவுகளைப் பார்க்க உங்கள் வடிகட்டிகளை சரிசெய்ய முயற்சிக்கவும்',
    pending: 'ஒத்திசைவு நிலுவையில்',
    deleteConfirm: 'இந்த வலி பதிவை அழிக்க விரும்புகிறீர்களா?',
    deleteSuccess: 'வலி பதிவு வெற்றிகரமாக அழிக்கப்பட்டது',
    deleteError: 'வலி பதிவை அழிக்க முடியவில்லை',
    success: {
      deleted: 'வலி பதிவு வெற்றிகரமாக அழிக்கப்பட்டது'
    },
    errors: {
      deleteFailed: 'வலி பதிவை அழிக்க முடியவில்லை'
    }
  },
  statistics: {
    title: 'புள்ளிவிவரங்கள்',
    vasScaleCaption: 'வலி நிலைகள்: VAS 0–10 — வலி இல்லை · லேசான (1–3) · மிதமான (4–6) · கடுமையான (7–9) · மோசமான (10)',
    loading: 'புள்ளிவிவரங்கள் ஏற்றப்படுகின்றன...',
    error: 'புள்ளிவிவரங்களை ஏற்றுவதில் பிழை',
    loadFailed: 'புள்ளிவிவரங்களை ஏற்ற முடியவில்லை',
    painLevelDistribution: 'வலி நிலை விநியோகம்',
    painTrends: 'காலப்போக்கில் வலி போக்குகள்',
    painByZone: 'உடல் பகுதியால் வலி',
    timePeriod: 'கால அளவு',
    filterByRegion: 'பிராந்தியத்தால் வடிகட்டு',
    lastDays: 'கடந்த {{count}} நாட்கள்',
    totalLogs: 'மொத்த பதிவுகள்',
    empty: 'தரவு கிடைக்கவில்லை',
    emptyText: 'புள்ளிவிவரங்கள் மற்றும் போக்குகளைப் பார்க்க வலியைக் கண்காணிக்கத் தொடங்குங்கள்',
    labels: {
      severePain: 'கடுமையான வலி',
      moderatePain: 'மிதமான வலி',
      mild: 'லேசான',
      lessPain: 'குறைந்த வலி',
      noPain: 'வலி இல்லை',
      worstPain: 'மோசமான வலி',
      intensityOverTime: 'காலப்போக்கில் வலி தீவிரம்',
      painIntensity: 'வலி தீவிரம்',
      painLevel: 'வலி நிலை',
      time: 'நேரம்',
      notes: 'குறிப்புகள்'
    },
    noDataForRegion: 'இந்த பகுதிக்கு தரவு இல்லை',
    totalEntries: 'மொத்த பதிவுகள்',
    emptyRegionLine: 'தேர்ந்தெடுக்கப்பட்ட காலத்தில் "{{zone}}" க்கு வலி பதிவுகள் காணப்படவில்லை. நீண்ட காலத்தைத் தேர்ந்தெடுக்கவும் அல்லது வேறு பகுதியைத் தேர்ந்தெடுக்கவும்.',
    noPainTrendsData: 'தேர்ந்தெடுக்கப்பட்ட காலத்திற்கு வலி போக்கு தரவு கிடைக்கவில்லை.',
    emptyRegionBar: 'தேர்ந்தெடுக்கப்பட்ட காலத்தில் "{{zone}}" க்கு வலி தரவு காணப்படவில்லை.',
    noPainByZoneData: 'உடல் பகுதியால் வலி தரவு கிடைக்கவில்லை.',
    emptyRegionDoughnut: 'தேர்ந்தெடுக்கப்பட்ட காலத்தில் "{{zone}}" க்கு வலி நிலை விநியோக தரவு காணப்படவில்லை.',
    noPainLevelDistributionData: 'வலி நிலை விநியோக தரவு கிடைக்கவில்லை.',
    painTimelineFor: '{{zone}} க்கான வலி காலவரிசை',
    allRegions: 'அனைத்து மண்டலங்களும்',
    dateRange: 'தேதி வரம்பு',
    heatmapTitle: 'பகுதியால் வலி வெப்ப வரைபடம்',
    heatmap: {
      zone: 'பகுதி',
      intensity: 'தீவிரம்',
      logs: 'பதிவுகள்',
      intensityScale: 'வலி தீவிரம் அளவுகோல்',
      intensityLabels: {
        severe: 'கடுமையான',
        moderate: 'மிதமான',
        less: 'குறைந்த',
        none: 'இல்லை'
      }
    }
  },
  medication: {
    title: 'மருந்துகள்',
    subtitle: 'உங்கள் மருத்துவரால் பரிந்துரைக்கப்பட்ட மருந்து திட்டங்களைப் பரிசீலிக்கவும். மருந்தகம்-தயாரான நகலைப் பதிவிறக்கவும் அல்லது தேவைப்படும்போது முந்தைய மருந்துச் சீட்டுகளை மீண்டும் பார்க்கவும்.',
    loading: 'மருந்துச் சீட்டுகள் ஏற்றப்படுகின்றன...',
    empty: {
      title: 'இன்னும் மருந்துச் சீட்டுகள் இல்லை',
      message: 'உங்கள் மருத்துவர் இங்கே மருந்து திட்டங்களைப் பகிர்வார். ஆலோசனையின் பிறகு மீண்டும் சரிபார்க்கவும்.'
    },
    description: 'உங்கள் மருத்துவரால் பரிந்துரைக்கப்பட்ட மருந்து திட்டங்களைப் பரிசீலிக்கவும். மருந்தகம்-தயாரான நகலைப் பதிவிறக்கவும் அல்லது தேவைப்படும்போது முந்தைய மருந்துச் சீட்டுகளை மீண்டும் பார்க்கவும்.',
    legacy: {
      title: 'பழைய மருந்துச் சீட்டு',
      subtitle: 'இந்த மருந்துச் சீட்டு முந்தைய வடிவத்தைப் பயன்படுத்தி அனுப்பப்பட்டது.',
      badge: 'உரை-அடிப்படையிலான மருந்துச் சீட்டு'
    },
    current: {
      title: '⭐ தற்போதைய மருந்துச் சீட்டு',
      badge: 'தற்போதைய மருந்துச் சீட்டு',
      lastUpdated: 'கடைசியாக புதுப்பிக்கப்பட்டது',
      notice: 'இது மிக சமீபத்திய மருந்துச் சீட்டு. இந்த வழிமுறைகளை கவனமாக பின்பற்றவும் மற்றும் மருந்தை சரியாக பரிந்துரைக்கப்பட்டபடி வழங்க உங்கள் மருந்தகத்திற்கு பதிவிறக்கக்கூடிய நகலை வழங்கவும்.'
    },
    history: {
      title: '📚 மருந்துச் சீட்டு வரலாறு',
      subtitle: 'உங்கள் மருத்துவர் பகிர்ந்த முந்தைய மருந்துச் சீட்டுகளைப் பரிசீலிக்கவும்.',
      badge: 'மருந்துச் சீட்டு வரலாறு'
    },
    download: {
      formal: '📥 முறையான மருந்துச் சீட்டை பதிவிறக்கவும்',
      text: '📄 உரை நகலை பதிவிறக்கவும்',
      success: 'மருந்துச் சீட்டு பதிவிறக்கப்பட்டது',
      error: 'பதிவிறக்க மருந்துச் சீட்டு கிடைக்கவில்லை'
    },
    labels: {
      patient: 'நோயாளி',
      doctor: 'மருத்துவர்',
      issuedOn: 'வழங்கப்பட்டது',
      medication: 'மருந்து',
      doseQty: 'மருந்தளவு / அளவு',
      timing: 'நேரம்',
      food: 'உணவு',
      duration: 'காலம்',
      notes: 'குறிப்புகள்',
      patientInstructions: 'நோயாளி வழிமுறைகள்',
      pharmacyNotes: 'மருந்தக குறிப்புகள்',
      asPrescribed: 'பரிந்துரைக்கப்பட்டபடி',
      asDirected: 'வழிநடத்தப்பட்டபடி',
      id: 'அடையாளம்:',
      reg: 'ரெஜ்:',
      contact: 'தொடர்பு:',
      prescribingDoctor: 'மருந்து பரிந்துரைக்கும் மருத்துவர்:',
      treatingPhysician: 'சிகிச்சை அளிக்கும் மருத்துவர்',
      yourDoctor: 'உங்கள் மருத்துவர்',
      schedule: {
        morning: 'காலை',
        afternoon: 'மதியம்',
        evening: 'மாலை',
        night: 'இரவு'
      },
      foodInstruction: {
        before: 'உணவுக்கு முன்',
        with: 'உணவுடன்',
        after: 'உணவுக்குப் பிறகு',
        none: 'உணவைச் சார்ந்தது அல்ல'
      }
    },
    footer: {
      needAssistance: 'உதவி தேவையா?',
      shareNote: 'சரிபார்ப்பு மற்றும் நிறைவேற்றத்திற்காக பதிவிறக்கப்பட்ட PDF ஐ உங்கள் மருந்தகத்திற்கு அல்லது ஆலோசனை மருத்துவருக்கு பகிரவும்.'
    },
    success: {
      newPrescription: '💊 புதிய மருந்துச் சீட்டு பெறப்பட்டது',
      downloaded: 'மருந்துச் சீட்டு பதிவிறக்கப்பட்டது'
    },
    errors: {
      loadFailed: 'இப்போது மருந்துச் சீட்டுகளை ஏற்ற முடியவில்லை',
      noPrescription: 'பதிவிறக்க மருந்துச் சீட்டு கிடைக்கவில்லை'
    },
    reminders: {
      title: 'மருந்து நினைவூட்டல்கள்',
      next: 'அடுத்த நினைவூட்டல்',
      at: 'நேரத்தில்',
      taken: 'எடுக்கப்பட்டது! 30 நிமிடங்களில் வலியைப் பதிவு செய்யவும்',
      iTookIt: 'நான் எடுத்துக்கொண்டேன்',
      takeMedication: 'மருந்து எடுக்கவும்: {{name}}',
      recordPainAfter: 'உங்கள் மருத்துவருக்காக எடுத்த 30 நிமிடத்திற்குப் பிறகு வலியைப் பதிவு செய்யவும்.',
      recordPainNow: 'உங்கள் மருத்துவர் விளைவைப் பார்க்க மருந்து எடுத்த 30 நிமிடத்திற்குப் பிறகு இப்போது உங்கள் வலியைப் பதிவு செய்யவும்.',
      recordPainNotification: 'மருந்து எடுத்த 30 நிமிடத்திற்குப் பிறகு இப்போது உங்கள் வலி நிலையைப் பதிவு செய்யவும்.',
      recordPain: 'வலியைப் பதிவு செய்யவும்',
      recordPainPrompt: 'உங்கள் மருந்தின் விளைவை மருத்துவர் கண்காணிக்க இப்போது உங்கள் வலியைப் பதிவு செய்யவும்.',
      recordPainNowButton: 'இப்போது வலியைப் பதிவு செய்யவும்',
      upcoming: 'வரவிருக்கும்',
      noUpcoming: 'வரவிருக்கும் நினைவூட்டல்கள் எதுவும் இல்லை'
    }
  },
  messages: {
    title: 'செய்திகள்',
    loading: 'செய்திகள் ஏற்றப்படுகின்றன...',
    error: 'செய்திகளை ஏற்ற முடியவில்லை',
    empty: 'இன்னும் செய்திகள் இல்லை'
  },
  history: {
    title: 'வரலாறு',
    subtitle: 'உங்கள் அனைத்து வலி கண்காணிப்பு செயல்பாடுகளின் முழுமையான பதிவு',
    loading: 'வரலாறு ஏற்றப்படுகிறது...',
    error: {
      title: 'வரலாற்றை ஏற்றுவதில் பிழை',
      message: 'வரலாறு தரவை ஏற்ற முடியவில்லை',
      retry: 'மீண்டும் முயற்சிக்கவும்'
    },
    empty: {
      title: 'பதிவுகள் எதுவும் காணப்படவில்லை',
      filtered: 'உங்கள் வடிகட்டிகள் அல்லது தேடல் சொற்களை சரிசெய்ய முயற்சிக்கவும்',
      noData: 'உங்கள் வரலாற்றை இங்கே பார்க்க வலியை கண்காணிக்கத் தொடங்கவும்'
    },
    stats: {
      total: 'மொத்த பதிவுகள்',
      today: 'இன்று',
      thisWeek: 'இந்த வாரம்',
      thisMonth: 'இந்த மாதம்'
    },
    filters: {
      painLevel: 'வலி நிலை:',
      view: 'காட்சி:',
      dateRange: 'தேதி வரம்பு:',
      allLevels: 'அனைத்து நிலைகள்',
      noPain: 'வலி இல்லை',
      mild: 'லேசான',
      lessPain: 'குறைந்த வலி',
      moderatePain: 'மிதமான வலி',
      severePain: 'கடுமையான வலி',
      worstPain: 'மோசமான வலி',
      bothViews: 'இரண்டு காட்சிகளும்',
      frontView: 'முன் காட்சி',
      backView: 'பின் காட்சி',
      allTime: 'அனைத்து நேரம்',
      today: 'இன்று',
      thisWeek: 'இந்த வாரம்',
      thisMonth: 'இந்த மாதம்'
    },
    search: {
      placeholder: 'பதிவுகளைத் தேடவும்...'
    },
    actions: {
      refresh: 'புதுப்பிக்க',
      deleteLogs: '🗑️ பதிவுகளை நீக்கு',
      selectAll: 'அனைத்தையும் தேர்ந்தெடு',
      deselectAll: 'அனைத்தையும் தேர்வு நீக்கு',
      deleteSelected: 'தேர்ந்தெடுக்கப்பட்டவற்றை நீக்கு',
      cancel: 'ரத்துசெய்'
    },
    deleteMode: {
      title: '🗑️ நீக்கு முறை: நீக்க பதிவுகளைத் தேர்ந்தெடுக்கவும்',
      selected: 'தேர்ந்தெடுக்கப்பட்டது',
      confirm: 'நீங்கள் தேர்ந்தெடுத்த {{count}} பதிவு(களை) நீக்க விரும்புகிறீர்களா? இந்த செயலை திரும்பப் பெற முடியாது.',
      success: '{{count}} பதிவு(கள்) வெற்றிகரமாக நீக்கப்பட்டது',
      error: 'தேர்ந்தெடுக்கப்பட்ட பதிவுகளை நீக்க முடியவில்லை',
      selectAtLeastOne: 'தயவுசெய்து குறைந்தது ஒரு பதிவையாவது தேர்ந்தெடுக்கவும்'
    },
    header: {
      title: 'வரலாறு பதிவுகள் ({{count}})'
    },
    labels: {
      intensity: 'தீவிரம்:',
      symptoms: 'அறிகுறிகள்:',
      triggers: 'தூண்டிகள்:',
      view: 'காட்சி',
      unknownTime: 'அறியப்படாத நேரம்',
      invalidDate: 'தவறான தேதி',
      noDate: 'தேதி இல்லை',
      justNow: 'இப்போது',
      minutesAgo: 'நிமிடங்களுக்கு முன்பு',
      hoursAgo: 'மணிநேரங்களுக்கு முன்பு'
    },
    painLevels: {
      'no-pain': 'வலி இல்லை',
      'mild': 'லேசான',
      'moderate': 'மிதமான வலி',
      'severe': 'கடுமையான வலி',
      'worst-pain': 'மோசமான வலி',
      'less-pain': 'குறைந்த வலி',
      'moderate-pain': 'மிதமான வலி',
      'severe-pain': 'கடுமையான வலி'
    }
  },
  distress: {
    title: '🌡️ உளைச்சல் கண்காணிப்பு',
    subtitle: 'காலப்போக்கில் உங்கள் உளைச்சல் நிலைகளை பதிவு செய்து கண்காணிக்கவும்',
    loading: 'உளைச்சல் வரலாறு ஏற்றப்படுகிறது...',
    error: {
      title: 'உளைச்சல் வரலாறு ஏற்றுவதில் பிழை',
      message: 'உளைச்சல் பதிவுகளை ஏற்ற முடியவில்லை',
      retry: 'மீண்டும் முயற்சிக்கவும்',
      login: 'உளைச்சல் பதிவுகளைப் பார்க்க தயவுசெய்து உள்நுழையவும்'
    },
    empty: {
      title: 'உளைச்சல் பதிவுகள் காணப்படவில்லை',
      textFilter: 'உங்கள் வடிகட்டி அளவுகோல்களுடன் பொருந்தக்கூடிய பதிவுகள் இல்லை',
      textNoFilter: 'மேலே உள்ள வெப்பமானியைப் பயன்படுத்தி உங்கள் உளைச்சல் நிலையை பதிவு செய்யவும்'
    },
    status: {
      offline: 'ஆஃப்லைன் முறை',
      syncing: 'ஒத்திசைவு செய்யப்படுகிறது…',
      online: 'ஆன்லைன்',
      pending: 'ஒத்திசைவு நிலுவையில்',
      pendingSync: 'ஒத்திசைவு நிலுவையில்',
      pendingSyncEntries: 'ஒத்திசைவு நிலுவையில் உள்ள உள்ளீடுகள்',
      syncNow: 'இப்போது ஒத்திசைக்கவும்'
    },
    filters: {
      minScore: 'மதிப்பெண்:',
      startDate: 'தொடக்க தேதி:',
      endDate: 'முடிவு தேதி:',
      allScores: 'அனைத்து மதிப்பெண்கள்',
      score0: '0+',
      score4: '4+ (மருத்துவ ரீதியாக குறிப்பிடத்தக்கது)',
      score7: '7+ (உயர்)',
      clinicallySignificant: '4+ (மருத்துவ ரீதியாக குறிப்பிடத்தக்கது)',
      high: '7+ (உயர்)',
      clear: 'வடிகட்டிகளை அழிக்கவும்'
    },
    stats: {
      totalEntries: 'மொத்த உள்ளீடுகள்',
      averageScore: 'சராசரி மதிப்பெண்',
      highestScore: 'அதிக மதிப்பெண்',
      highDistress: 'உயர் உளைச்சல் (≥4)'
    },
    labels: {
      distressScore: 'உளைச்சல் மதிப்பெண்:',
      sourcesOfDistress: 'உளைச்சலின் ஆதாரங்கள்:',
      notes: 'குறிப்புகள்:',
      extreme: 'தீவிர உளைச்சல்',
      moderateToSevere: 'மிதமான முதல் தீவிர உளைச்சல்',
      mild: 'லேசான உளைச்சல்',
      none: 'உளைச்சல் இல்லை',
      alert: '🚨 மருத்துவ ரீதியாக குறிப்பிடத்தக்க உளைச்சல் - உங்கள் மருத்துவருக்கு தெரிவிக்கப்பட்டது'
    },
    header: {
      title: 'உளைச்சல் பதிவுகள் ({{count}})',
      refresh: 'புதுப்பிக்க'
    }
  },
  distressThermometer: {
    title: '🌡️ உளைச்சல் வெப்பமானி',
    subtitle: 'கடந்த வாரத்தில் உங்கள் தற்போதைய உளைச்சல் நிலையை மதிப்பிடவும் (0 = உளைச்சல் இல்லை, 10 = தீவிர உளைச்சல்)',
    guidelines: 'NCCN உளைச்சல் வெப்பமானி வழிகாட்டுதல்களின் அடிப்படையில்',
    problemList: {
      label: 'சிக்கல்களின் பட்டியல் - உங்கள் உளைச்சலுக்கு காரணம் என்ன? (விருப்பமானது)',
      helper: 'பொருந்தும் அனைத்தையும் தேர்ந்தெடுக்கவும் (NCCN சிக்கல்களின் பட்டியல்)'
    },
    notes: {
      label: 'கூடுதல் குறிப்புகள் (விருப்பமானது)',
      placeholder: 'உங்கள் உளைச்சல் பற்றிய கூடுதல் விவரங்களை விவரிக்கவும்...',
      enterNumberHint: 'அல்லது எண்ணை உள்ளிடவும் (0-10):'
    },
    scoreLabels: {
      noDistress: 'உளைச்சல் இல்லை',
      mildDistress: 'லேசான உளைச்சல்',
      moderateDistress: 'மிதமான உளைச்சல்',
      severeDistress: 'கடுமையான உளைச்சல்',
      extremeDistress: 'மிகவும் தீவிரமான உளைச்சல்'
    },
    buttons: {
      record: 'உளைச்சல் நிலையை பதிவு செய்யவும்',
      recording: 'பதிவு செய்யப்படுகிறது...',
      clear: 'அழிக்க'
    },
    alert: '⚠️ NCCN எச்சரிக்கை: {{score}} மதிப்பெண் மருத்துவ ரீதியாக குறிப்பிடத்தக்க உளைச்சலைக் குறிக்கிறது (≥4). உங்கள் சுகாதார சேவை வழங்குநருக்கு தெரிவிக்கப்படும்.',
    success: {
      recorded: 'உளைச்சல் நிலை {{score}} வெற்றிகரமாக பதிவு செய்யப்பட்டது'
    },
    errors: {
      login: 'உளைச்சலை பதிவு செய்ய தயவுசெய்து உள்நுழையவும்',
      recordFailed: 'உளைச்சலை பதிவு செய்ய முடியவில்லை'
    },
    sources: {
      transportation: 'போக்குவரத்து',
      housing: 'வீடு',
      insuranceFinancial: 'காப்பீடு/நிதி',
      workSchool: 'வேலை/பள்ளி',
      dealingWithPartner: 'கூட்டாளருடன் கையாளுதல்',
      dealingWithChildren: 'குழந்தைகளுடன் கையாளுதல்',
      familyHealthIssues: 'குடும்ப சுகாதார பிரச்சினைகள்',
      depression: 'மனச்சோர்வு',
      fears: 'பயங்கள்',
      nervousness: 'நரம்பு',
      sadness: 'வருத்தம்',
      worry: 'கவலை',
      lossOfInterest: 'வழக்கமான செயல்பாடுகளில் ஆர்வம் இழத்தல்',
      spiritualReligiousConcerns: 'ஆன்மீக/மத கவலைகள்',
      fatigue: 'சோர்வு',
      pain: 'வலி',
      sleepProblems: 'தூக்கம் பிரச்சினைகள்',
      eatingProblems: 'சாப்பிடும் பிரச்சினைகள்',
      bathingDressing: 'குளித்தல்/உடை',
      breathingProblems: 'சுவாச பிரச்சினைகள்',
      nausea: 'வாந்தி',
      memoryConcentration: 'நினைவு/கவனம்',
      mouthSores: 'வாய் புண்கள்',
      skinProblems: 'தோல் பிரச்சினைகள்',
      tinglingHandsFeet: 'கைகள்/கால்களில் சிலிர்ப்பு',
      otherPhysicalProblems: 'மற்ற உடல் பிரச்சினைகள்'
    }
  },
  bodyParts: {
    'right palm': 'வலது உள்ளங்கை',
    'left lower abdomen': 'இடது கீழ் வயிறு',
    'right lower abdomen': 'வலது கீழ் வயிறு',
    'left upper abdomen': 'இடது மேல் வயிறு',
    'right upper abdomen': 'வலது மேல் வயிறு',
    'left front forearm': 'இடது முன் முழங்கை',
    'left elbow': 'இடது முழங்கை',
    'right elbow': 'வலது முழங்கை',
    'front head right': 'முன் தலை வலது',
    'front head left': 'முன் தலை இடது',
    'frons right': 'நெற்றி வலது',
    'frons left': 'நெற்றி இடது',
    'left shoulder': 'இடது தோள்',
    'cervicis left': 'கழுத்து இடது',
    'left lower leg': 'இடது கீழ் கால்',
    'right lower leg': 'வலது கீழ் கால்',
    'left ankle': 'இடது கணுக்கால்',
    'right ankle': 'வலது கணுக்கால்',
    'right foot': 'வலது பாதம்',
    'left foot': 'இடது பாதம்',
    'left upper arm': 'இடது மேல் கை',
    'left lower arm': 'இடது கீழ் கை',
    'facies left': 'முகம் இடது',
    'cervicis right': 'கழுத்து வலது',
    'facies right': 'முகம் வலது',
    'right front forearm': 'வலது முன் முழங்கை',
    'right wrist': 'வலது மணிக்கட்டு',
    'left wrist': 'இடது மணிக்கட்டு',
    'left palm': 'இடது உள்ளங்கை',
    'right upper chest': 'வலது மேல் மார்பு',
    'left upper chest': 'இடது மேல் மார்பு',
    'right lower chest': 'வலது கீழ் மார்பு',
    'left lower chest': 'இடது கீழ் மார்பு',
    'right shoulder': 'வலது தோள்',
    'right upper arm': 'வலது மேல் கை',
    'right diaphragm': 'வலது மூச்சுத்தசை',
    'right lower arm': 'வலது கீழ் கை',
    'left diaphragm': 'இடது மூச்சுத்தசை',
    'left upper leg': 'இடது மேல் கால்',
    'left knee': 'இடது முழங்கால்',
    'left lower thigh': 'இடது கீழ் தொடை',
    'left upper thigh': 'இடது மேல் தொடை',
    'right upper leg': 'வலது மேல் கால்',
    'right knee': 'வலது முழங்கால்',
    'right lower thigh': 'வலது கீழ் தொடை',
    'right upper thigh': 'வலது மேல் தொடை',
    'right pelvic region': 'வலது இடுப்பு பகுதி',
    'left pelvic region': 'இடது இடுப்பு பகுதி',
    'back head left': 'பின் தலை இடது',
    'back head right': 'பின் தலை வலது',
    'left upper back': 'இடது மேல் முதுகு',
    'left lumbar': 'இடது இடுப்பு',
    'left buttock': 'இடது பிட்டம்',
    'left back of knee': 'இடது முழங்கால் பின்புறம்',
    'right back of knee': 'வலது முழங்கால் பின்புறம்',
    'left upper calf': 'இடது மேல் கால்',
    'right upper calf': 'வலது மேல் கால்',
    'right lower calf': 'வலது கீழ் கால்',
    'left lower calf': 'இடது கீழ் கால்',
    'left ankle & foot': 'இடது கணுக்கால் மற்றும் பாதம்',
    'right upper forearm': 'வலது மேல் முழங்கை',
    'left lower arm': 'இடது கீழ் கை',
    'left upper forearm': 'இடது மேல் முழங்கை',
    'right ankle & foot': 'வலது கணுக்கால் மற்றும் பாதம்',
    'right buttock': 'வலது பிட்டம்',
    'lower head left': 'கீழ் தலை இடது',
    'lower head right': 'கீழ் தலை வலது',
    'neck left': 'கழுத்து இடது',
    'right upper back': 'வலது மேல் முதுகு',
    'left middle back': 'இடது நடு முதுகு',
    'right middle back': 'வலது நடு முதுகு',
    'left lower forearm': 'இடது கீழ் முழங்கை',
    'right lumbar': 'வலது இடுப்பு',
    'left sacral': 'இடது திரிகாஸ்தி',
    'right sacral': 'வலது திரிகாஸ்தி',
    'right lower forearm': 'வலது கீழ் முழங்கை',
    'neck right': 'கழுத்து வலது',
    left: 'இடது',
    right: 'வலது',
    upper: 'மேல்',
    lower: 'கீழ்',
    front: 'முன்',
    back: 'பின்',
    middle: 'நடு'
  },
  bodyDiagram: {
    selectTimeOfDay: 'நாளின் நேரத்தைத் தேர்ந்தெடுக்கவும்',
    title: 'உடல் வரைபடம்',
    subtitle: 'வலி நிலையை பதிவு செய்ய எந்த உடல் பகுதியையும் கிளிக் செய்யவும்',
    loading: 'உடல் வரைபடம் ஏற்றப்படுகிறது...',
    todaysRecords: 'இன்றைய வலி பதிவுகள்',
    unknownZone: 'அறியப்படாத பகுதி',
    instructions: {
      title: 'எப்படி பயன்படுத்துவது',
      step1: 'வலியை பதிவு செய்ய எந்த உடல் பகுதியையும் கிளிக் செய்யவும்',
      step2: 'உங்கள் வலி நிலையைத் தேர்ந்தெடுக்கவும் (வலி இல்லை, குறைந்த வலி, மிதமான வலி, அல்லது கடுமையான வலி)',
      step3: 'அறிகுறிகள், தூண்டிகள் மற்றும் குறிப்புகள் போன்ற விருப்பமான விவரங்களைச் சேர்க்கவும்',
      step4: 'கீழே உள்ள பதிவுகளில் உங்கள் வலி தரவைப் பார்க்கவும்'
    },
    view: {
      front: 'முன் பார்வை',
      back: 'பின் பார்வை',
      switch: 'பார்வையை மாற்றவும்'
    },
    actions: {
      clearAll: 'அனைத்தும் அழிக்க',
      sync: 'இப்போது ஒத்திசை',
      syncing: 'ஒத்திசைக்கப்படுகிறது...'
    },
    status: {
      online: 'ஆன்லைன்',
      offline: 'ஆஃப்லைன்',
      syncing: 'ஒத்திசைக்கப்படுகிறது'
    },
    empty: {
      title: 'இன்று வலி பதிவுகள் இல்லை',
      text: 'உங்கள் வலியைக் கண்காணிக்கத் தொடங்க மேலே உள்ள உடல் பகுதிகளைக் கிளிக் செய்யவும்'
    },
    pending: 'ஒத்திசைக்க நிலுவையில்',
    time: {
      unknown: 'அறியப்படாத நேரம்',
      invalidDate: 'தவறான தேதி',
      invalid: 'தவறான நேரம்',
      justNow: 'இப்போது',
      minutesAgo: 'நிமிடங்களுக்கு முன்பு',
      hoursAgo: 'மணிநேரங்களுக்கு முன்பு'
    },
    success: {
      recorded: 'வலி நிலை வெற்றிகரமாக பதிவு செய்யப்பட்டது!',
      withAudio: 'ஆடியோ குறிப்புடன் வலி நிலை வெற்றிகரமாக பதிவு செய்யப்பட்டது!',
      cleared: 'அனைத்து வலி தரவும் வெற்றிகரமாக அழிக்கப்பட்டது!'
    },
    confirm: {
      clearAll: 'இன்றைய அனைத்து வலி தரவையும் அழிக்க விரும்புகிறீர்களா?'
    },
    errors: {
      title: 'உடல் வரைபடத்தை ஏற்றுவதில் பிழை',
      loadFailed: 'வலி தரவை ஏற்ற முடியவில்லை',
      clearFailed: 'வலி தரவை அழிக்க முடியவில்லை',
      failed: 'வலி நிலையை பதிவு செய்ய முடியவில்லை: {{message}}'
    }
  },
  audioRecorder: {
    title: 'குரல் பதிவு',
    help: {
      start: 'உங்கள் வலி விளக்கத்தை பதிவு செய்ய "பதிவைத் தொடங்கு" என்பதை கிளிக் செய்யவும்.',
      recording: 'பதிவு நடந்து கொண்டிருக்கிறது... முடிந்ததும் "பதிவை நிறுத்து" என்பதை கிளிக் செய்யவும்.',
      recorded: 'ஆடியோ வெற்றிகரமாக பதிவு செய்யப்பட்டது! நீங்கள் இயக்கலாம், நீக்கலாம் அல்லது புதியதை பதிவு செய்யலாம்.'
    },
    buttons: {
      start: 'பதிவைத் தொடங்கு',
      stop: 'பதிவை நிறுத்து',
      play: 'பதிவை இயக்கு',
      pause: 'இடைநிறுத்து',
      delete: 'நீக்கு',
      rerecord: 'மீண்டும் பதிவு செய்'
    },
    info: {
      duration: 'கால அளவு',
      format: 'வடிவம்'
    },
    errors: {
      microphoneAccess: 'மைக்ரோஃபோனை அணுக முடியவில்லை. தயவுசெய்து உங்கள் உலாவி அனுமதிகளை சரிபார்க்கவும்.'
    }
  },
  audioPlayer: {
    label: 'குரல் குறிப்பு',
    loading: 'ஆடியோ ஏற்றப்படுகிறது...',
    errors: {
      loadFailed: 'ஆடியோ ஏற்ற முடியவில்லை: {{message}}',
      notLoaded: 'ஆடியோ இன்னும் ஏற்றப்படவில்லை',
      playFailed: 'ஆடியோ இயக்க முடியவில்லை',
      playFailedWithMessage: 'ஆடியோ இயக்க முடியவில்லை: {{message}}'
    }
  }
};

const mlOverrides = {
  app: {
    loginPortal: 'ലോഗിൻ പോർട്ടൽ'
  },
  common: {
    back: 'തിരികെ',
    cancel: 'റദ്ദാക്കുക',
    confirm: 'സ്ഥിരീകരിക്കുക',
    loading: 'ദയവായി കാത്തിരിക്കുക...',
    language: 'ഭാഷ',
    requiredField: 'ദയവായി ആവശ്യമായ എല്ലാ ഫീൽഡുകളും പൂരിപ്പിക്കുക'
  },
  languageGate: {
    title: 'നിങ്ങളുടെ ഭാഷ തിരഞ്ഞെടുക്കുക',
    subtitle: 'പോർട്ടൽ തുടരാൻ നിങ്ങൾക്ക് ഇഷ്ടമുള്ള ഭാഷ തിരഞ്ഞെടുക്കുക.',
    helper: 'ലോഗിൻ സ്ക്രീനിൽ നിന്ന് ഏത് സമയവും ഭാഷ മാറ്റാം.',
    helperEmphasis: 'ഘബറിക്കേണ്ട!',
    secondary: 'ആദ്യ ചുവടുവെപ്പിൽ തന്നെ വീട്ടിലുളളത് പോലെ അനുഭവിക്കാൻ ഇത് സഹായിക്കുന്നു.'
  },
  buttons: {
    login: 'ലോഗിൻ',
    register: 'രജിസ്റ്റർ',
    verifyPasskey: 'പാസ്‌കി സ്ഥിരീകരിക്കുക'
  },
  login: {
    initialChoice: {
      login: 'ലോഗിൻ',
      register: 'രജിസ്റ്റർ'
    },
    passkey: {
      prompt: 'പാസ്‌കി നൽകുക *',
      helper: 'രോഗി/ഡോക്ടർ രജിസ്ട്രേഷനായി രജിസ്ട്രേഷൻ പാസ്‌കി അല്ലെങ്കിൽ അഡ്മിൻ ആക്‌സസിനായി അഡ്മിൻ പാസ്‌കി ഉപയോഗിക്കുക',
      invalid: 'അസാധുവായ പാസ്‌കി. ദയവായി വീണ്ടും ശ്രമിക്കുക.'
    },
    form: {
      idLabel: 'ഐഡി *',
      idPlaceholder: 'Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) അല്ലെങ്കിൽ Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      passwordLabel: 'പാസ്സ്വേഡ് *',
      passwordPlaceholder: 'നിങ്ങളുടെ പാസ്സ്വേഡ് നൽകുക',
      rememberMe: '90 ദിവസത്തേക്ക് എന്നെ ഓർക്കുക'
    },
    errors: {
      missingId: 'ദയവായി നിങ്ങളുടെ Patient ID അല്ലെങ്കിൽ Doctor ID നൽകുക',
      missingPassword: 'ദയവായി നിങ്ങളുടെ പാസ്സ്വേഡ് നൽകുക',
      invalidIdFormat: 'അസാധുവായ ഐഡി ഫോർമാറ്റ്. Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) അല്ലെങ്കിൽ Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001) ഉപയോഗിക്കുക',
      adminLoginDisabled: 'അഡ്മിൻ ലോഗിൻ ലഭ്യമല്ല. രജിസ്ട്രേഷൻ ഓപ്ഷൻ വഴി അഡ്മിൻ പാസ്‌കി ഉപയോഗിക്കുക.',
      missingDoctorEmail: 'ഡോക്ടർ രജിസ്ട്രേഷനായി ഇമെയിൽ ആവശ്യമാണ്',
      missingDoctorPhone: 'ഡോക്ടർ രജിസ്ട്രേഷനായി ഫോൺ നമ്പർ ആവശ്യമാണ്',
      passwordMismatch: 'പാസ്സ്വേഡുകൾ ഒത്തുപോകുന്നില്ല',
      passwordLength: 'പാസ്സ്വേഡ് കുറഞ്ഞത് 6 അക്ഷരങ്ങൾ ഉണ്ടായിരിക്കണം',
      missingPatientDoctor: 'ഡോക്ടർ തിരഞ്ഞെടുപ്പുൾപ്പെടെ ആവശ്യമായ എല്ലാ ഫീൽഡുകളും പൂരിപ്പിക്കുക',
      genericLogin: 'ലോഗിൻ പരാജയപ്പെട്ടു',
      loadDoctorsFailed: 'ഡോക്ടർമാരുടെ പട്ടിക ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല. ദയവായി പേജ് റിലോഡ് ചെയ്യുക.',
      noDoctors: 'ഡോക്ടർമാർ ഇല്ല. ദയവായി സഹായ സംഘത്തെ ബന്ധപ്പെടുക.'
    },
    success: {
      welcomeDoctor: 'വീണ്ടും സ്വാഗതം, ഡോ. {{name}}!',
      welcomePatient: 'വീണ്ടും സ്വാഗതം!'
    },
    register: {
      title: 'ഞാൻ *',
      patient: 'രോഗി',
      doctor: 'ഡോക്ടർ',
      firstName: {
        label: 'മുതൽ പേര് *',
        placeholder: 'നിങ്ങളുടെ പേര് നൽകുക'
      },
      lastName: {
        label: 'അവസാന പേര് (ഐച്ഛികം)',
        placeholder: 'നിങ്ങളുടെ അവസാന പേര് നൽകുക (ഐച്ഛികം)'
      },
      email: {
        label: 'ഇമെയിൽ *',
        placeholder: 'നിങ്ങളുടെ ഇമെയിൽ വിലാസം നൽകുക'
      },
      phone: {
        label: 'ഫോൺ നമ്പർ *',
        placeholder: 'നിങ്ങളുടെ ഫോൺ നമ്പർ നൽകുക'
      },
      selectDoctor: {
        label: 'നിങ്ങളുടെ ഡോക്ടറെ തിരഞ്ഞെടുക്കുക *',
        placeholder: '-- ഒരു ഡോക്ടറെ തിരഞ്ഞെടുക്കുക --',
        loading: 'ഡോക്ടർമാരെ ലോഡ് ചെയ്യുന്നു...',
        noDoctors: 'ഡോക്ടർമാർ ഇല്ല. ദയവായി സഹായ സംഘത്തെ ബന്ധപ്പെടുക.'
      },
      dateOfBirth: {
        label: 'ജനനത്തീയതി *'
      },
      contactNumber: {
        label: 'ബന്ധപ്പെടാനുള്ള നമ്പർ *',
        placeholder: 'നിങ്ങളുടെ ബന്ധപ്പെടാനുള്ള നമ്പർ നൽകുക'
      },
      whatsappNumber: {
        label: 'വാട്ട്സ്ആപ്പ് നമ്പർ (ഐച്ഛികം) 💬',
        placeholder: 'വാട്ട്സ്ആപ്പ് നമ്പർ നൽകുക (ഉദാ. +1234567890)',
        helper: 'നിങ്ങൾ നൽകിയാൽ മരുന്ന് അറിയിപ്പുകൾ വാട്ട്സ്ആപ്പിലൂടെ ലഭിക്കും'
      },
      password: {
        label: 'പാസ്സ്വേഡ് *',
        placeholder: 'നിങ്ങളുടെ പാസ്സ്വേഡ് നൽകുക'
      },
      confirmPassword: {
        label: 'പാസ്സ്വേഡ് സ്ഥിരീകരിക്കുക *',
        placeholder: 'നിങ്ങളുടെ പാസ്സ്വേഡ് സ്ഥിരീകരിക്കുക'
      },
      passkeyTitle: 'പാസ്‌കി നൽകുക *',
      passkeyHelper: 'രോഗി/ഡോക്ടർ രജിസ്ട്രേഷനായി രജിസ്ട്രേഷൻ പാസ്‌കി അല്ലെങ്കിൽ അഡ്മിൻ ആക്‌സസിനായി അഡ്മിൻ പാസ്‌കി ഉപയോഗിക്കുക'
    }
  },
  toast: {
    adminWelcome: 'സ്വാഗതം, {{name}}!',
    doctorRegistrationSuccess: 'രജിസ്ട്രേഷൻ വിജയകരം! നിങ്ങളുടെ ഡോക്ടർ ഐഡി: {{id}}. തുടർന്നുകൊണ്ടുപോകാൻ ദയവായി ലോഗിൻ ചെയ്യുക.'
  },
  dashboard: {
    loading: 'ഡാഷ്‌ബോർഡ് ലോഡ് ചെയ്യുന്നു...',
    welcome: {
      title: 'പെയിൻ ട്രാക്കറിലേക്ക് സ്വാഗതം',
      subtitle: 'നിങ്ങളുടെ വേദന നിലയും ആരോഗ്യ പുരോഗതിയും നിരീക്ഷിക്കുക'
    },
    stats: {
      logsToday: 'ഇന്നത്തെ രേഖകള്‍',
      noPain: 'വേദനയില്ല',
      mild: 'ലഘു',
      moderatePain: 'മിതമായ വേദന',
      severePain: 'തീവ്ര വേദന',
      worstPain: 'ഏറ്റവും മോശം വേദന',
      lessPain: 'കുറഞ്ഞ വേദന',
      unknown: 'അജ്ഞാതം'
    },
    actions: {
      retry: 'വീണ്ടും ശ്രമിക്കുക',
      bodyDiagram: 'ശരീര ചിത്രം',
      viewLogs: 'രേഖകള്‍ കാണുക',
      viewStatistics: 'സ്ഥിതിവിവരങ്ങള്‍ കാണുക',
      history: 'ചരിത്രം'
    },
    errors: {
      title: 'ഡാഷ്‌ബോർഡ് ലോഡ് ചെയ്യുമ്പോൾ പിശക്',
      loadFailed: 'വേദന ഡാറ്റ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല'
    },
    logs: {
      title: 'സമീപകാല വേദന രേഖകള്‍',
      emptyTitle: 'ഇന്ന് വേദന രേഖകളില്ല',
      emptyText: 'ശരീര ഭാഗങ്ങള്‍ ക്ലിക്ക് ചെയ്ത് വേദന ട്രാക്കിംഗ് ആരംഭിക്കുക'
    },
    sidebar: {
      title: 'പെയിൻ ട്രാക്കർ',
      links: {
        dashboard: 'ഡാഷ്‌ബോർഡ്',
        bodyDiagram: 'ശരീര ചിത്രം',
        painLogs: 'വേദന രേഖകള്‍',
        history: 'ചരിത്രം',
        statistics: 'സ്ഥിതിവിവരങ്ങള്‍',
        distress: 'മാനസിക സമ്മര്‍ദ്ദം',
        medication: 'മരുന്ന്'
      },
      user: {
        profile: 'രോഗി പ്രൊഫൈൽ'
      },
      actions: {
        logout: 'ലോഗൗട്ട്'
      }
    }
  },
  painScale: {
    title: 'വേദന നില രേഖപ്പെടുത്തുക',
    vasTitle: 'വിഷ്വൽ അനലോഗ് സ്കെയിൽ (VAS)',
    vasDescription: 'സ്റ്റാൻഡേർഡ് 0–10 സ്കെയിൽ: 0 = വേദനയില്ല, 10 = ഏറ്റവും മോശം വേദന',
    vasCurrentValue: 'നിങ്ങളുടെ വേദന നില',
    vasWorstPain: 'ഏറ്റവും മോശം വേദന',
    vasDescriptors: {
      noPain: 'വേദനയില്ല',
      mild: 'ലഘു',
      moderate: 'മിതമായ',
      severe: 'തീവ്ര',
      verySevere: 'വളരെ തീവ്ര',
      worstPain: 'ഏറ്റവും മോശം വേദന'
    },
    fixedScales: 'നിശ്ചിത വേദന അളവുകൾ: വേദനയില്ല (0) • കുറഞ്ഞ വേദന (1-3) • മിതമായ (4-7) • തീവ്ര (8-10)',
    frontView: '👤 മുൻവശ കാഴ്ച',
    backView: '👥 പിൻവശ കാഴ്ച',
    levels: {
      noPain: 'വേദനയില്ല',
      lessPain: 'കുറഞ്ഞ വേദന',
      moderatePain: 'മിതമായ വേദന',
      severePain: 'തീവ്ര വേദന'
    },
    descriptions: {
      noPain: 'അസ്വസ്ഥതയില്ല',
      lessPain: 'ലഘു അസ്വസ്ഥത',
      moderatePain: 'ശ്രദ്ധേയമാണെങ്കിലും നിയന്ത്രിക്കാവുന്നത്',
      severePain: 'തീവ്രവും ആശങ്കാജനകവുമായ'
    },
    additionalInfo: 'അധിക വിവരങ്ങൾ (ഐച്ഛികം)',
    painScale: 'വേദന അളവ്',
    timeOfDay: 'ദിവസത്തിന്റെ സമയം',
    mood: 'മാനസികാവസ്ഥ',
    activity: 'പ്രവർത്തന നില',
    symptoms: 'ലക്ഷണങ്ങൾ (കോമയാൽ വേർതിരിച്ചത്)',
    triggers: 'ട്രിഗർ ചെയ്യുന്നവ (കോമയാൽ വേർതിരിച്ചത്)',
    notes: 'കുറിപ്പുകൾ (ടെക്സ്റ്റ്)',
    audioNote: 'അല്ലെങ്കിൽ നിങ്ങളുടെ വിവരണം റെക്കോർഡ് ചെയ്യുക (ഓഡിയോ)',
    placeholders: {
      symptoms: 'ഉദാ., കടുപ്പം, വീക്കം, മരവിപ്പ്',
      triggers: 'ഉദാ., സമ്മർദ്ദം, കാലാവസ്ഥ, വ്യായാമം',
      notes: 'നിങ്ങളുടെ വേദന വിവരണം ഇവിടെ ടൈപ്പ് ചെയ്യുക...',
      selectMood: 'മാനസികാവസ്ഥ തിരഞ്ഞെടുക്കുക (ഐച്ഛികം)',
      selectActivity: 'പ്രവർത്തനം തിരഞ്ഞെടുക്കുക (ഐച്ഛികം)',
      symptomsMultiSelect: 'പല ലക്ഷണങ്ങൾ തിരഞ്ഞെടുക്കാൻ Ctrl/Cmd അമർത്തി പിടിക്കുക'
    },
    timeOptions: {
      morning: 'രാവിലെ',
      afternoon: 'ഉച്ചയ്ക്ക്',
      evening: 'വൈകുന്നേരം',
      night: 'രാത്രി'
    },
    moodOptions: {
      good: 'നല്ലത്',
      moderate: 'മിതമായ',
      bad: 'മോശം'
    },
    activityOptions: {
      able: 'കഴിയും',
      partiallyDependent: 'ഭാഗിക സ്വാശ്രയം',
      notAble: 'കഴിയില്ല'
    },
    symptomOptions: {
      none: 'ഒന്നുമില്ല',
      vomiting: 'ഛർദ്ദി',
      nausea: 'ഓക്കാനം',
      dizziness: 'തലചുറ്റൽ',
      headache: 'തലവേദന',
      fever: 'പനി',
      fatigue: 'ക്ഷീണം',
      shortnessOfBreath: 'ശ്വാസകോശ തടസ്സം',
      cough: 'ചുമ',
      swelling: 'വീക്കം',
      numbness: 'മരവിപ്പ്',
      weakness: 'ദൗർബല്യം'
    },
    buttons: {
      cancel: 'റദ്ദാക്കുക',
      clear: 'മായ്ക്കുക',
      record: 'വേദന രേഖപ്പെടുത്തുക'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'ദിവസത്തിന്റെ സമയം തിരഞ്ഞെടുക്കുക',
    success: {
      recorded: 'വേദന നില വിജയകരമായി രേഖപ്പെടുത്തി',
      withAudio: 'ഓഡിയോ കുറിപ്പുമായി വേദന നില വിജയകരമായി രേഖപ്പെടുത്തി'
    },
    errors: {
      failed: 'വേദന നില രേഖപ്പെടുത്താൻ കഴിഞ്ഞില്ല: {{message}}'
    },
    confirm: {
      clearAll: 'ഇന്നത്തെ എല്ലാ വേദന ഡാറ്റയും മായ്ക്കാൻ നിങ്ങൾ ഉറപ്പാണോ?'
    }
  },
  profile: {
    title: 'എന്റെ പ്രൊഫൈൽ',
    subtitle: 'നിങ്ങളുടെ വ്യക്തിപരമായ വിവരങ്ങൾ നിയന്ത്രിക്കുക',
    loading: 'പ്രൊഫൈൽ ലോഡ് ചെയ്യുന്നു...',
    editWhatsApp: '✏️ വാട്ട്സാപ്പ് നമ്പർ എഡിറ്റ് ചെയ്യുക',
    whatsAppLabel: 'വാട്ട്സാപ്പ് നമ്പർ',
    whatsAppPlaceholder: 'വാട്ട്സാപ്പ് നമ്പർ നൽകുക (ഉദാ. +1234567890)',
    whatsAppHelper: 'നിങ്ങൾ നൽകിയാൽ മരുന്ന് അറിയിപ്പുകൾ വാട്ട്സാപ്പിലൂടെ ലഭിക്കും. നീക്കംചെയ്യാൻ ശൂന്യമായി വിടുക.',
    save: '💾 മാറ്റങ്ങൾ സേവ് ചെയ്യുക',
    saving: 'സേവ് ചെയ്യുന്നു...',
    cancel: 'റദ്ദാക്കുക',
    success: 'വാട്ട്സാപ്പ് നമ്പർ വിജയകരമായി അപ്ഡേറ്റ് ചെയ്തു!',
    optional: 'ഐച്ഛികം',
    forNotifications: 'മരുന്ന് അറിയിപ്പുകൾക്ക്',
    notProvided: 'നൽകിയിട്ടില്ല',
    patientId: 'രോഗി ഐഡി',
    age: 'വയസ്സ്',
    sections: {
      personalInfo: 'വ്യക്തിപരമായ വിവരം',
      contactInfo: 'ബന്ധപ്പെടാനുള്ള വിവരം'
    },
    errors: {
      authRequired: 'അംഗീകാരം ആവശ്യമാണ്',
      updateFailed: 'വാട്ട്സാപ്പ് നമ്പർ അപ്ഡേറ്റ് ചെയ്യാൻ കഴിഞ്ഞില്ല'
    }
  },
  painLogs: {
    title: 'വേദന രേഖകൾ',
    loading: 'വേദന രേഖകൾ ലോഡ് ചെയ്യുന്നു...',
    error: 'വേദന രേഖകൾ ലോഡ് ചെയ്യുന്നതിൽ പിശക്',
    filters: {
      painLevel: 'വേദന നില',
      allLevels: 'എല്ലാ നിലകളും',
      noPain: 'വേദനയില്ല',
      lessPain: 'കുറഞ്ഞ വേദന',
      moderatePain: 'മിതമായ വേദന',
      severePain: 'തീവ്ര വേദന'
    },
    table: {
      painLevel: 'വേദന നില',
      zone: 'ശരീര ഭാഗം',
      date: 'തീയതി',
      time: 'സമയം',
      actions: 'പ്രവർത്തനങ്ങൾ'
    },
    actions: {
      delete: 'ഇല്ലാതാക്കുക',
      previous: 'മുമ്പത്തെ',
      next: 'അടുത്തത്'
    },
    empty: 'വേദന രേഖകൾ കണ്ടെത്തിയില്ല',
    confirmDelete: 'ഈ വേദന രേഖ ഇല്ലാതാക്കാൻ നിങ്ങൾ ഉറപ്പാണോ?',
    success: {
      deleted: 'വേദന രേഖ വിജയകരമായി ഇല്ലാതാക്കി'
    },
    errors: {
      deleteFailed: 'വേദന രേഖ ഇല്ലാതാക്കാൻ കഴിഞ്ഞില്ല'
    }
  },
  statistics: {
    title: 'സ്ഥിതിവിവരങ്ങൾ',
    vasScaleCaption: 'വേദന നിലകൾ: VAS 0–10 — വേദനയില്ല · ലഘു (1–3) · മിതമായ (4–6) · തീവ്ര (7–9) · ഏറ്റവും മോശം (10)',
    loading: 'സ്ഥിതിവിവരങ്ങൾ ലോഡ് ചെയ്യുന്നു...',
    error: 'സ്ഥിതിവിവരങ്ങൾ ലോഡ് ചെയ്യുന്നതിൽ പിശക്',
    loadFailed: 'സ്ഥിതിവിവരങ്ങൾ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
    painLevelDistribution: 'വേദന നില വിതരണം',
    painTrends: 'കാലക്രമേണ വേദന പ്രവണതകൾ',
    painByZone: 'ശരീര മേഖലയനുസരിച്ച് വേദന',
    timePeriod: 'സമയ കാലയളവ്',
    filterByRegion: 'മേഖല അനുസരിച്ച് ഫിൽട്ടർ ചെയ്യുക',
    lastDays: 'കഴിഞ്ഞ {{count}} ദിവസം',
    totalLogs: 'മൊത്തം രേഖകൾ',
    empty: 'ഡാറ്റ ലഭ്യമല്ല',
    emptyText: 'സ്ഥിതിവിവരങ്ങളും പ്രവണതകളും കാണാൻ വേദന ട്രാക്ക് ചെയ്യാൻ ആരംഭിക്കുക',
    labels: {
      severePain: 'തീവ്ര വേദന',
      moderatePain: 'മിതമായ വേദന',
      mild: 'ലഘു',
      lessPain: 'കുറഞ്ഞ വേദന',
      noPain: 'വേദനയില്ല',
      worstPain: 'ഏറ്റവും മോശം വേദന',
      intensityOverTime: 'കാലക്രമേണ വേദനാ തീവ്രത',
      painIntensity: 'വേദനാ തീവ്രത',
      painLevel: 'വേദനാ നില',
      time: 'സമയം',
      notes: 'കുറിപ്പുകൾ'
    },
    noDataForRegion: 'ഈ മേഖലയ്ക്ക് ഡാറ്റ ഇല്ല',
    totalEntries: 'മൊത്തം എൻട്രികൾ',
    emptyRegionLine: 'തിരഞ്ഞെടുത്ത കാലയളവിൽ "{{zone}}" ന് വേദന രേഖകൾ കണ്ടെത്തിയില്ല. നീളമുള്ള കാലയളവ് തിരഞ്ഞെടുക്കുക അല്ലെങ്കിൽ മറ്റൊരു മേഖല തിരഞ്ഞെടുക്കുക.',
    noPainTrendsData: 'തിരഞ്ഞെടുത്ത കാലയളവിന് വേദന പ്രവണത ഡാറ്റ ലഭ്യമല്ല.',
    emptyRegionBar: 'തിരഞ്ഞെടുത്ത കാലയളവിൽ "{{zone}}" ന് വേദന ഡാറ്റ കണ്ടെത്തിയില്ല.',
    noPainByZoneData: 'ശരീര മേഖലയനുസരിച്ച് വേദന ഡാറ്റ ലഭ്യമല്ല.',
    emptyRegionDoughnut: 'തിരഞ്ഞെടുത്ത കാലയളവിൽ "{{zone}}" ന് വേദന നില വിതരണ ഡാറ്റ കണ്ടെത്തിയില്ല.',
    noPainLevelDistributionData: 'വേദന നില വിതരണ ഡാറ്റ ലഭ്യമല്ല.',
    painTimelineFor: '{{zone}} നുള്ള വേദന ടൈംലൈൻ',
    allRegions: 'എല്ലാ മേഖലകളും',
    dateRange: 'തീയതി പരിധി',
    heatmapTitle: 'മേഖലയനുസരിച്ച് വേദന ഹീറ്റ്‌മാപ്പ്',
    heatmap: {
      zone: 'മേഖല',
      intensity: 'തീവ്രത',
      logs: 'രേഖകൾ',
      intensityScale: 'വേദന തീവ്രത സ്കെയിൽ',
      intensityLabels: {
        severe: 'തീവ്ര',
        moderate: 'മിതമായ',
        less: 'കുറഞ്ഞ',
        none: 'ഇല്ല'
      }
    }
  },
  medication: {
    title: 'മരുന്ന്',
    subtitle: 'നിങ്ങളുടെ ഡോക്ടർ നിർദ്ദേശിച്ച മരുന്ന് പ്ലാനുകൾ അവലോകനം ചെയ്യുക. ഫാർമസി-തയ്യാറായ പകർപ്പ് ഡൗൺലോഡ് ചെയ്യുക അല്ലെങ്കിൽ ആവശ്യമുള്ളപ്പോൾ മുമ്പത്തെ പ്രിസ്ക്രിപ്ഷനുകൾ വീണ്ടും കാണുക.',
    loading: 'പ്രിസ്ക്രിപ്ഷനുകൾ ലോഡ് ചെയ്യുന്നു...',
    empty: {
      title: 'ഇതുവരെ പ്രിസ്ക്രിപ്ഷനുകൾ ഇല്ല',
      message: 'നിങ്ങളുടെ ഡോക്ടർ ഇവിടെ മരുന്ന് പ്ലാനുകൾ പങ്കിടും. കൺസൾട്ടേഷനിന് ശേഷം വീണ്ടും പരിശോധിക്കുക.'
    },
    description: 'നിങ്ങളുടെ ഡോക്ടർ നിർദ്ദേശിച്ച മരുന്ന് പ്ലാനുകൾ അവലോകനം ചെയ്യുക. ഫാർമസി-തയ്യാറായ പകർപ്പ് ഡൗൺലോഡ് ചെയ്യുക അല്ലെങ്കിൽ ആവശ്യമുള്ളപ്പോൾ മുമ്പത്തെ പ്രിസ്ക്രിപ്ഷനുകൾ വീണ്ടും കാണുക.',
    legacy: {
      title: 'പഴയ പ്രിസ്ക്രിപ്ഷൻ',
      subtitle: 'ഈ പ്രിസ്ക്രിപ്ഷൻ മുമ്പത്തെ ഫോർമാറ്റ് ഉപയോഗിച്ച് അയച്ചു.',
      badge: 'ടെക്സ്റ്റ്-അടിസ്ഥാന പ്രിസ്ക്രിപ്ഷൻ'
    },
    current: {
      title: '⭐ നിലവിലെ പ്രിസ്ക്രിപ്ഷൻ',
      badge: 'നിലവിലെ പ്രിസ്ക്രിപ്ഷൻ',
      lastUpdated: 'അവസാനം അപ്ഡേറ്റ് ചെയ്തത്',
      notice: 'ഇതാണ് ഏറ്റവും പുതിയ പ്രിസ്ക്രിപ്ഷൻ. ഈ നിർദ്ദേശങ്ങൾ ശ്രദ്ധാപൂർവ്വം പാലിക്കുക, മരുന്ന് കൃത്യമായി നിർദ്ദേശിച്ചതുപോലെ നൽകാൻ നിങ്ങളുടെ ഫാർമസിസ്റ്റിന് ഡൗൺലോഡ് ചെയ്യാവുന്ന പകർപ്പ് നൽകുക.'
    },
    history: {
      title: '📚 പ്രിസ്ക്രിപ്ഷൻ ചരിത്രം',
      subtitle: 'നിങ്ങളുടെ ഡോക്ടർ പങ്കിട്ട മുമ്പത്തെ പ്രിസ്ക്രിപ്ഷനുകൾ അവലോകനം ചെയ്യുക.',
      badge: 'പ്രിസ്ക്രിപ്ഷൻ ചരിത്രം'
    },
    download: {
      formal: '📥 ഔപചാരിക പ്രിസ്ക്രിപ്ഷൻ ഡൗൺലോഡ് ചെയ്യുക',
      text: '📄 ടെക്സ്റ്റ് പകർപ്പ് ഡൗൺലോഡ് ചെയ്യുക',
      success: 'പ്രിസ്ക്രിപ്ഷൻ ഡൗൺലോഡ് ചെയ്തു',
      error: 'ഡൗൺലോഡ് ചെയ്യാൻ പ്രിസ്ക്രിപ്ഷൻ ലഭ്യമല്ല'
    },
    labels: {
      patient: 'രോഗി',
      doctor: 'ഡോക്ടർ',
      issuedOn: 'ഇഷ്യൂ ചെയ്തത്',
      medication: 'മരുന്ന്',
      doseQty: 'ഡോസ് / അളവ്',
      timing: 'സമയം',
      food: 'ഭക്ഷണം',
      duration: 'കാലാവധി',
      notes: 'കുറിപ്പുകൾ',
      patientInstructions: 'രോഗി നിർദ്ദേശങ്ങൾ',
      pharmacyNotes: 'ഫാർമസി കുറിപ്പുകൾ',
      asPrescribed: 'നിർദ്ദേശിച്ചതുപോലെ',
      asDirected: 'നിർദ്ദേശിച്ചതുപോലെ',
      id: 'ഐഡി:',
      reg: 'രെജ്:',
      contact: 'കോൺടാക്റ്റ്:',
      prescribingDoctor: 'പ്രിസ്ക്രൈബ് ചെയ്യുന്ന ഡോക്ടർ:',
      treatingPhysician: 'ചികിത്സിക്കുന്ന ഡോക്ടർ',
      yourDoctor: 'നിങ്ങളുടെ ഡോക്ടർ',
      schedule: {
        morning: 'രാവിലെ',
        afternoon: 'ഉച്ചയ്ക്ക്',
        evening: 'വൈകുന്നേരം',
        night: 'രാത്രി'
      },
      foodInstruction: {
        before: 'ഭക്ഷണത്തിന് മുമ്പ്',
        with: 'ഭക്ഷണത്തോടൊപ്പം',
        after: 'ഭക്ഷണത്തിന് ശേഷം',
        none: 'ഭക്ഷണത്തെ ആശ്രയിക്കുന്നില്ല'
      }
    },
    footer: {
      needAssistance: 'സഹായം വേണോ?',
      shareNote: 'പരിശോധനയ്ക്കും നിറവേറ്റലിനും ഡൗൺലോഡ് ചെയ്ത PDF നിങ്ങളുടെ ഫാർമസിസ്റ്റിനോടോ കൺസൾട്ടിംഗ് ഡോക്ടറോടോ പങ്കിടുക.'
    },
    success: {
      newPrescription: '💊 പുതിയ പ്രിസ്ക്രിപ്ഷൻ ലഭിച്ചു',
      downloaded: 'പ്രിസ്ക്രിപ്ഷൻ ഡൗൺലോഡ് ചെയ്തു'
    },
    errors: {
      loadFailed: 'ഇപ്പോൾ പ്രിസ്ക്രിപ്ഷനുകൾ ലോഡ് ചെയ്യാൻ കഴിയില്ല',
      noPrescription: 'ഡൗൺലോഡ് ചെയ്യാൻ പ്രിസ്ക്രിപ്ഷൻ ലഭ്യമല്ല'
    },
    reminders: {
      title: 'മരുന്ന് ഓർമ്മപ്പെടുത്തലുകൾ',
      next: 'അടുത്ത ഓർമ്മപ്പെടുത്തൽ',
      at: 'സമയത്ത്',
      taken: 'എടുത്തു! 30 മിനിറ്റിനുള്ളിൽ വേദന രേഖപ്പെടുത്തുക',
      iTookIt: 'ഞാൻ എടുത്തു',
      takeMedication: 'മരുന്ന് എടുക്കുക: {{name}}',
      recordPainAfter: 'നിങ്ങളുടെ ഡോക്ടറിനായി എടുത്ത 30 മിനിറ്റിനുശേഷം വേദന രേഖപ്പെടുത്തുക.',
      recordPainNow: 'നിങ്ങളുടെ ഡോക്ടർ ഫലം കാണാൻ മരുന്ന് എടുത്ത 30 മിനിറ്റിനുശേഷം ഇപ്പോൾ വേദന രേഖപ്പെടുത്തുക.',
      recordPainNotification: 'മരുന്ന് എടുത്ത 30 മിനിറ്റിനുശേഷം ഇപ്പോൾ വേദനാ നില രേഖപ്പെടുത്തുക.',
      recordPain: 'വേദന രേഖപ്പെടുത്തുക',
      recordPainPrompt: 'നിങ്ങളുടെ മരുന്നിന്റെ ഫലം ഡോക്ടർ ട്രാക്ക് ചെയ്യാൻ ഇപ്പോൾ വേദന രേഖപ്പെടുത്തുക.',
      recordPainNowButton: 'ഇപ്പോൾ വേദന രേഖപ്പെടുത്തുക',
      upcoming: 'വരാനിരിക്കുന്നവ',
      noUpcoming: 'വരാനിരിക്കുന്ന ഓർമ്മപ്പെടുത്തലുകളൊന്നുമില്ല'
    }
  },
  messages: {
    title: 'സന്ദേശങ്ങൾ',
    loading: 'സന്ദേശങ്ങൾ ലോഡ് ചെയ്യുന്നു...',
    error: 'സന്ദേശങ്ങൾ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
    empty: 'ഇതുവരെ സന്ദേശങ്ങൾ ഇല്ല'
  },
  history: {
    title: 'ചരിത്രം',
    subtitle: 'നിങ്ങളുടെ എല്ലാ വേദന ട്രാക്കിംഗ് പ്രവർത്തനങ്ങളുടെയും പൂർണ്ണ റെക്കോർഡ്',
    loading: 'ചരിത്രം ലോഡ് ചെയ്യുന്നു...',
    error: {
      title: 'ചരിത്രം ലോഡ് ചെയ്യുന്നതിൽ പിശക്',
      message: 'ചരിത്ര ഡാറ്റ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
      retry: 'വീണ്ടും ശ്രമിക്കുക'
    },
    empty: {
      title: 'റെക്കോർഡുകൾ കണ്ടെത്തിയില്ല',
      filtered: 'നിങ്ങളുടെ ഫിൽട്ടറുകൾ അല്ലെങ്കിൽ തിരയൽ പദങ്ങൾ ക്രമീകരിക്കാൻ ശ്രമിക്കുക',
      noData: 'നിങ്ങളുടെ ചരിത്രം ഇവിടെ കാണാൻ നിങ്ങളുടെ വേദന ട്രാക്ക് ചെയ്യാൻ ആരംഭിക്കുക'
    },
    stats: {
      total: 'മൊത്തം റെക്കോർഡുകൾ',
      today: 'ഇന്ന്',
      thisWeek: 'ഈ ആഴ്ച',
      thisMonth: 'ഈ മാസം'
    },
    filters: {
      painLevel: 'വേദന നില:',
      view: 'കാഴ്ച:',
      dateRange: 'തീയതി ശ്രേണി:',
      allLevels: 'എല്ലാ നിലകളും',
      noPain: 'വേദനയില്ല',
      mild: 'ലഘു',
      lessPain: 'കുറഞ്ഞ വേദന',
      moderatePain: 'മിതമായ വേദന',
      severePain: 'തീവ്രമായ വേദന',
      worstPain: 'ഏറ്റവും മോശം വേദന',
      bothViews: 'രണ്ട് കാഴ്ചകളും',
      frontView: 'മുൻവശ കാഴ്ച',
      backView: 'പിൻവശ കാഴ്ച',
      allTime: 'എല്ലാ സമയവും',
      today: 'ഇന്ന്',
      thisWeek: 'ഈ ആഴ്ച',
      thisMonth: 'ഈ മാസം'
    },
    search: {
      placeholder: 'റെക്കോർഡുകൾ തിരയുക...'
    },
    actions: {
      refresh: 'പുതുക്കുക',
      deleteLogs: '🗑️ റെക്കോർഡുകൾ ഇല്ലാതാക്കുക',
      selectAll: 'എല്ലാം തിരഞ്ഞെടുക്കുക',
      deselectAll: 'എല്ലാം തിരഞ്ഞെടുപ്പ് റദ്ദാക്കുക',
      deleteSelected: 'തിരഞ്ഞെടുത്തവ ഇല്ലാതാക്കുക',
      cancel: 'റദ്ദാക്കുക'
    },
    deleteMode: {
      title: '🗑️ ഇല്ലാതാക്കൽ മോഡ്: ഇല്ലാതാക്കാൻ റെക്കോർഡുകൾ തിരഞ്ഞെടുക്കുക',
      selected: 'തിരഞ്ഞെടുത്തു',
      confirm: 'നിങ്ങൾ തിരഞ്ഞെടുത്ത {{count}} റെക്കോർഡ്(കൾ) ഇല്ലാതാക്കാൻ ഉറപ്പാണോ? ഈ പ്രവർത്തനം പുനഃസ്ഥാപിക്കാൻ കഴിയില്ല.',
      success: '{{count}} റെക്കോർഡ്(കൾ) വിജയകരമായി ഇല്ലാതാക്കി',
      error: 'തിരഞ്ഞെടുത്ത റെക്കോർഡുകൾ ഇല്ലാതാക്കാൻ കഴിഞ്ഞില്ല',
      selectAtLeastOne: 'ദയവായി കുറഞ്ഞത് ഒരു റെക്കോർഡ് തിരഞ്ഞെടുക്കുക'
    },
    header: {
      title: 'ചരിത്ര റെക്കോർഡുകൾ ({{count}})'
    },
    labels: {
      intensity: 'തീവ്രത:',
      symptoms: 'ലക്ഷണങ്ങൾ:',
      triggers: 'ട്രിഗറുകൾ:',
      view: 'കാഴ്ച',
      unknownTime: 'അജ്ഞാത സമയം',
      invalidDate: 'അസാധുവായ തീയതി',
      noDate: 'തീയതി ഇല്ല',
      justNow: 'ഇപ്പോൾ',
      minutesAgo: 'മിനിറ്റുകൾക്ക് മുമ്പ്',
      hoursAgo: 'മണിക്കൂറുകൾക്ക് മുമ്പ്'
    },
    painLevels: {
      'no-pain': 'വേദനയില്ല',
      'mild': 'ലഘു',
      'moderate': 'മിതമായ വേദന',
      'severe': 'തീവ്രമായ വേദന',
      'worst-pain': 'ഏറ്റവും മോശം വേദന',
      'less-pain': 'കുറഞ്ഞ വേദന',
      'moderate-pain': 'മിതമായ വേദന',
      'severe-pain': 'തീവ്രമായ വേദന'
    }
  },
  distress: {
    title: '🌡️ മാനസിക സമ്മർദ്ദം ട്രാക്കർ',
    subtitle: 'കാലക്രമേണ നിങ്ങളുടെ മാനസിക സമ്മർദ്ദ നിലകൾ രേഖപ്പെടുത്തി നിരീക്ഷിക്കുക',
    loading: 'മാനസിക സമ്മർദ്ദ ചരിത്രം ലോഡ് ചെയ്യുന്നു...',
    error: {
      title: 'മാനസിക സമ്മർദ്ദ ചരിത്രം ലോഡ് ചെയ്യുന്നതിൽ പിശക്',
      message: 'മാനസിക സമ്മർദ്ദ രേഖകൾ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
      retry: 'വീണ്ടും ശ്രമിക്കുക',
      login: 'മാനസിക സമ്മർദ്ദ രേഖകൾ കാണാൻ ദയവായി ലോഗിൻ ചെയ്യുക'
    },
    empty: {
      title: 'മാനസിക സമ്മർദ്ദ രേഖകൾ കണ്ടെത്തിയില്ല',
      textFilter: 'നിങ്ങളുടെ ഫിൽട്ടർ മാനദണ്ഡങ്ങളുമായി പൊരുത്തപ്പെടുന്ന രേഖകൾ ഇല്ല',
      textNoFilter: 'മുകളിലുള്ള തെർമോമീറ്റർ ഉപയോഗിച്ച് നിങ്ങളുടെ മാനസിക സമ്മർദ്ദ നില രേഖപ്പെടുത്തുക'
    },
    status: {
      offline: 'ഓഫ്‌ലൈൻ മോഡ്',
      syncing: 'സമന്വയിപ്പിക്കുന്നു…',
      online: 'ഓൺലൈൻ',
      pending: 'തീർച്ചപ്പെടുത്താത്തത്',
      pendingSync: 'തീർച്ചപ്പെടുത്താത്ത സമന്വയം',
      pendingSyncEntries: 'തീർച്ചപ്പെടുത്താത്ത സമന്വയ എൻട്രികൾ',
      syncNow: 'ഇപ്പോൾ സമന്വയിപ്പിക്കുക'
    },
    filters: {
      minScore: 'സ്കോർ:',
      startDate: 'ആരംഭ തീയതി:',
      endDate: 'അവസാന തീയതി:',
      allScores: 'എല്ലാ സ്കോറുകളും',
      score0: '0+',
      score4: '4+ (ക്ലിനിക്കൽ ആയി പ്രാധാന്യമുള്ളത്)',
      score7: '7+ (ഉയർന്നത്)',
      clinicallySignificant: '4+ (ക്ലിനിക്കൽ ആയി പ്രാധാന്യമുള്ളത്)',
      high: '7+ (ഉയർന്നത്)',
      clear: 'ഫിൽട്ടറുകൾ മായ്ക്കുക'
    },
    stats: {
      totalEntries: 'മൊത്തം എൻട്രികൾ',
      averageScore: 'ശരാശരി സ്കോർ',
      highestScore: 'ഏറ്റവും ഉയർന്ന സ്കോർ',
      highDistress: 'ഉയർന്ന മാനസിക സമ്മർദ്ദം (≥4)'
    },
    labels: {
      distressScore: 'മാനസിക സമ്മർദ്ദ സ്കോർ:',
      sourcesOfDistress: 'മാനസിക സമ്മർദ്ദത്തിന്റെ ഉറവിടങ്ങൾ:',
      notes: 'കുറിപ്പുകൾ:',
      extreme: 'തീവ്രമായ മാനസിക സമ്മർദ്ദം',
      moderateToSevere: 'മിതമായ മുതൽ തീവ്രമായ മാനസിക സമ്മർദ്ദം',
      mild: 'ലഘുവായ മാനസിക സമ്മർദ്ദം',
      none: 'മാനസിക സമ്മർദ്ദം ഇല്ല',
      alert: '🚨 ക്ലിനിക്കൽ ആയി പ്രാധാന്യമുള്ള മാനസിക സമ്മർദ്ദം - നിങ്ങളുടെ ഡോക്ടറെ അറിയിച്ചു'
    },
    header: {
      title: 'മാനസിക സമ്മർദ്ദ രേഖകൾ ({{count}})',
      refresh: 'പുതുക്കുക'
    }
  },
  distressThermometer: {
    title: '🌡️ മാനസിക സമ്മർദ്ദം തെർമോമീറ്റർ',
    subtitle: 'കഴിഞ്ഞ ആഴ്ചയിലെ നിങ്ങളുടെ നിലവിലെ മാനസിക സമ്മർദ്ദ നില റേറ്റ് ചെയ്യുക (0 = മാനസിക സമ്മർദ്ദം ഇല്ല, 10 = തീവ്രമായ മാനസിക സമ്മർദ്ദം)',
    guidelines: 'NCCN മാനസിക സമ്മർദ്ദം തെർമോമീറ്റർ ഗൈഡ്ലൈനുകളെ അടിസ്ഥാനമാക്കി',
    problemList: {
      label: 'പ്രശ്നങ്ങളുടെ പട്ടിക - നിങ്ങളുടെ മാനസിക സമ്മർദ്ദത്തിന് കാരണമെന്താണ്? (ഓപ്ഷണൽ)',
      helper: 'ബാധകമായവയെല്ലാം തിരഞ്ഞെടുക്കുക (NCCN പ്രശ്നങ്ങളുടെ പട്ടിക)'
    },
    notes: {
      label: 'അധിക കുറിപ്പുകൾ (ഓപ്ഷണൽ)',
      placeholder: 'നിങ്ങളുടെ മാനസിക സമ്മർദ്ദത്തെക്കുറിച്ചുള്ള അധിക വിശദാംശങ്ങൾ വിവരിക്കുക...',
      enterNumberHint: 'അല്ലെങ്കിൽ നമ്പർ നൽകുക (0-10):'
    },
    buttons: {
      record: 'മാനസിക സമ്മർദ്ദ നില രേഖപ്പെടുത്തുക',
      recording: 'രേഖപ്പെടുത്തുന്നു...',
      clear: 'മായ്ക്കുക'
    },
    scoreLabels: {
      noDistress: 'മാനസിക സമ്മർദ്ദം ഇല്ല',
      mildDistress: 'ലഘുവായ മാനസിക സമ്മർദ്ദം',
      moderateDistress: 'മിതമായ മാനസിക സമ്മർദ്ദം',
      severeDistress: 'തീവ്രമായ മാനസിക സമ്മർദ്ദം',
      extremeDistress: 'അങ്ങേയറ്റത്തെ മാനസിക സമ്മർദ്ദം'
    },
    alert: '⚠️ NCCN മുന്നറിയിപ്പ്: {{score}} സ്കോർ ക്ലിനിക്കൽ ആയി പ്രാധാന്യമുള്ള മാനസിക സമ്മർദ്ദത്തെ സൂചിപ്പിക്കുന്നു (≥4). നിങ്ങളുടെ ആരോഗ്യ സേവന വിതരണക്കാരനെ അറിയിക്കും.',
    success: {
      recorded: 'മാനസിക സമ്മർദ്ദ നില {{score}} വിജയകരമായി രേഖപ്പെടുത്തി'
    },
    errors: {
      login: 'മാനസിക സമ്മർദ്ദം രേഖപ്പെടുത്താൻ ദയവായി ലോഗിൻ ചെയ്യുക',
      recordFailed: 'മാനസിക സമ്മർദ്ദം രേഖപ്പെടുത്താൻ കഴിഞ്ഞില്ല'
    },
    sources: {
      transportation: 'ഗതാഗതം',
      housing: 'വീട്',
      insuranceFinancial: 'ഇൻഷുറൻസ്/ധനകാര്യം',
      workSchool: 'ജോലി/സ്കൂൾ',
      dealingWithPartner: 'പങ്കാളിയുമായി കൈകാര്യം ചെയ്യുന്നത്',
      dealingWithChildren: 'കുട്ടികളുമായി കൈകാര്യം ചെയ്യുന്നത്',
      familyHealthIssues: 'കുടുംബ ആരോഗ്യ പ്രശ്നങ്ങൾ',
      depression: 'വിഷാദം',
      fears: 'ഭയങ്ങൾ',
      nervousness: 'നാഡീവ്യവസ്ഥ',
      sadness: 'ദുഃഖം',
      worry: 'വിഷമം',
      lossOfInterest: 'സാധാരണ പ്രവർത്തനങ്ങളിൽ താൽപ്പര്യം നഷ്ടപ്പെടൽ',
      spiritualReligiousConcerns: 'ആത്മീയ/മതപരമായ ആശങ്കകൾ',
      fatigue: 'ക്ഷീണം',
      pain: 'വേദന',
      sleepProblems: 'ഉറക്കം പ്രശ്നങ്ങൾ',
      eatingProblems: 'ഭക്ഷണം കഴിക്കുന്നതിലെ പ്രശ്നങ്ങൾ',
      bathingDressing: 'കുളി/വസ്ത്രം',
      breathingProblems: 'ശ്വസന പ്രശ്നങ്ങൾ',
      nausea: 'ഓക്കാനം',
      memoryConcentration: 'മെമ്മറി/ഏകാഗ്രത',
      mouthSores: 'വായിലെ പുണ്ണുകൾ',
      skinProblems: 'തൊലി പ്രശ്നങ്ങൾ',
      tinglingHandsFeet: 'കൈകളിലും കാലുകളിലും മുള്ള്',
      otherPhysicalProblems: 'മറ്റ് ശാരീരിക പ്രശ്നങ്ങൾ'
    }
  },
  bodyParts: {
    'right palm': 'വലത് കൈപ്പത്തി',
    'left lower abdomen': 'ഇടത് താഴെ വയറ്',
    'right lower abdomen': 'വലത് താഴെ വയറ്',
    'left upper abdomen': 'ഇടത് മുകളിൽ വയറ്',
    'right upper abdomen': 'വലത് മുകളിൽ വയറ്',
    'left front forearm': 'ഇടത് മുൻ മുഴങ്കൈ',
    'left elbow': 'ഇടത് മുഴങ്കൈ',
    'right elbow': 'വലത് മുഴങ്കൈ',
    'front head right': 'മുൻ തല വലത്',
    'front head left': 'മുൻ തല ഇടത്',
    'frons right': 'നെറ്റി വലത്',
    'frons left': 'നെറ്റി ഇടത്',
    'left shoulder': 'ഇടത് തോള്',
    'cervicis left': 'കഴുത്ത് ഇടത്',
    'left lower leg': 'ഇടത് താഴെ കാൽ',
    'right lower leg': 'വലത് താഴെ കാൽ',
    'left ankle': 'ഇടത് കണങ്കാൽ',
    'right ankle': 'വലത് കണങ്കാൽ',
    'right foot': 'വലത് പാദം',
    'left foot': 'ഇടത് പാദം',
    'left upper arm': 'ഇടത് മുകളിൽ കൈ',
    'left lower arm': 'ഇടത് താഴെ കൈ',
    'facies left': 'മുഖം ഇടത്',
    'cervicis right': 'കഴുത്ത് വലത്',
    'facies right': 'മുഖം വലത്',
    'right front forearm': 'വലത് മുൻ മുഴങ്കൈ',
    'right wrist': 'വലത് കൈത്തണ്ട',
    'left wrist': 'ഇടത് കൈത്തണ്ട',
    'left palm': 'ഇടത് കൈപ്പത്തി',
    'right upper chest': 'വലത് മുകളിൽ നെഞ്ച്',
    'left upper chest': 'ഇടത് മുകളിൽ നെഞ്ച്',
    'right lower chest': 'വലത് താഴെ നെഞ്ച്',
    'left lower chest': 'ഇടത് താഴെ നെഞ്ച്',
    'right shoulder': 'വലത് തോള്',
    'right upper arm': 'വലത് മുകളിൽ കൈ',
    'right diaphragm': 'വലത് ശ്വാസകോശം',
    'right lower arm': 'വലത് താഴെ കൈ',
    'left diaphragm': 'ഇടത് ശ്വാസകോശം',
    'left upper leg': 'ഇടത് മുകളിൽ കാൽ',
    'left knee': 'ഇടത് മുഴങ്കാൽ',
    'left lower thigh': 'ഇടത് താഴെ തുട',
    'left upper thigh': 'ഇടത് മുകളിൽ തുട',
    'right upper leg': 'വലത് മുകളിൽ കാൽ',
    'right knee': 'വലത് മുഴങ്കാൽ',
    'right lower thigh': 'വലത് താഴെ തുട',
    'right upper thigh': 'വലത് മുകളിൽ തുട',
    'right pelvic region': 'വലത് ശ്രോണി പ്രദേശം',
    'left pelvic region': 'ഇടത് ശ്രോണി പ്രദേശം',
    'back head left': 'പിൻ തല ഇടത്',
    'back head right': 'പിൻ തല വലത്',
    'left upper back': 'ഇടത് മുകളിൽ പുറം',
    'left lumbar': 'ഇടത് അരക്കെട്ട്',
    'left buttock': 'ഇടത് പൃഷ്ഠം',
    'left back of knee': 'ഇടത് മുഴങ്കാൽ പിൻഭാഗം',
    'right back of knee': 'വലത് മുഴങ്കാൽ പിൻഭാഗം',
    'left upper calf': 'ഇടത് മുകളിൽ കാൽ',
    'right upper calf': 'വലത് മുകളിൽ കാൽ',
    'right lower calf': 'വലത് താഴെ കാൽ',
    'left lower calf': 'ഇടത് താഴെ കാൽ',
    'left ankle & foot': 'ഇടത് കണങ്കാൽ & പാദം',
    'right upper forearm': 'വലത് മുകളിൽ മുഴങ്കൈ',
    'left lower arm': 'ഇടത് താഴെ കൈ',
    'left upper forearm': 'ഇടത് മുകളിൽ മുഴങ്കൈ',
    'right ankle & foot': 'വലത് കണങ്കാൽ & പാദം',
    'right buttock': 'വലത് പൃഷ്ഠം',
    'lower head left': 'താഴെ തല ഇടത്',
    'lower head right': 'താഴെ തല വലത്',
    'neck left': 'കഴുത്ത് ഇടത്',
    'right upper back': 'വലത് മുകളിൽ പുറം',
    'left middle back': 'ഇടത് നടുപുറം',
    'right middle back': 'വലത് നടുപുറം',
    'left lower forearm': 'ഇടത് താഴെ മുഴങ്കൈ',
    'right lumbar': 'വലത് അരക്കെട്ട്',
    'left sacral': 'ഇടത് ത്രികാസ്ഥി',
    'right sacral': 'വലത് ത്രികാസ്ഥി',
    'right lower forearm': 'വലത് താഴെ മുഴങ്കൈ',
    'neck right': 'കഴുത്ത് വലത്',
    left: 'ഇടത്',
    right: 'വലത്',
    upper: 'മുകളിൽ',
    lower: 'താഴെ',
    front: 'മുൻ',
    back: 'പിൻ',
    middle: 'നടു'
  },
  bodyDiagram: {
    selectTimeOfDay: 'ദിവസത്തിന്റെ സമയം തിരഞ്ഞെടുക്കുക',
    title: 'ശരീര ചിത്രം',
    subtitle: 'വേദന നില രേഖപ്പെടുത്താൻ ഏത് ശരീര മേഖലയും ക്ലിക്ക് ചെയ്യുക',
    loading: 'ശരീര ചിത്രം ലോഡ് ചെയ്യുന്നു...',
    todaysRecords: 'ഇന്നത്തെ വേദന രേഖകൾ',
    unknownZone: 'അജ്ഞാത മേഖല',
    instructions: {
      title: 'എങ്ങനെ ഉപയോഗിക്കാം',
      step1: 'വേദന രേഖപ്പെടുത്താൻ ഏത് ശരീര മേഖലയും ക്ലിക്ക് ചെയ്യുക',
      step2: 'നിങ്ങളുടെ വേദന നില തിരഞ്ഞെടുക്കുക (വേദനയില്ല, കുറഞ്ഞ വേദന, മിതമായ വേദന, അല്ലെങ്കിൽ തീവ്ര വേദന)',
      step3: 'ലക്ഷണങ്ങൾ, ട്രിഗറുകൾ, കുറിപ്പുകൾ എന്നിവ പോലുള്ള ഐച്ഛിക വിവരങ്ങൾ ചേർക്കുക',
      step4: 'ചുവടെയുള്ള രേഖകളിൽ നിങ്ങളുടെ വേദന ഡാറ്റ കാണുക'
    },
    view: {
      front: 'മുൻവശ കാഴ്ച',
      back: 'പിൻവശ കാഴ്ച',
      switch: 'കാഴ്ച മാറ്റുക'
    },
    actions: {
      clearAll: 'എല്ലാം മായ്ക്കുക',
      sync: 'ഇപ്പോൾ സിങ്ക് ചെയ്യുക',
      syncing: 'സിങ്ക് ചെയ്യുന്നു...'
    },
    status: {
      online: 'ഓൺലൈൻ',
      offline: 'ഓഫ്‌ലൈൻ',
      syncing: 'സിങ്ക് ചെയ്യുന്നു'
    },
    empty: {
      title: 'ഇന്ന് വേദന രേഖകളില്ല',
      text: 'നിങ്ങളുടെ വേദന ട്രാക്ക് ചെയ്യാൻ ആരംഭിക്കാൻ മുകളിലെ ശരീര മേഖലകൾ ക്ലിക്ക് ചെയ്യുക'
    },
    pending: 'സിങ്ക് ചെയ്യാൻ നിലവിലുണ്ട്',
    time: {
      unknown: 'അജ്ഞാത സമയം',
      invalidDate: 'അസാധുവായ തീയതി',
      invalid: 'അസാധുവായ സമയം',
      justNow: 'ഇപ്പോൾ',
      minutesAgo: 'മിനിറ്റുകൾക്ക് മുമ്പ്',
      hoursAgo: 'മണിക്കൂറുകൾക്ക് മുമ്പ്'
    },
    success: {
      cleared: 'എല്ലാ വേദന ഡാറ്റയും വിജയകരമായി മായ്ച്ചു!'
    },
    errors: {
      title: 'ശരീര ചിത്രം ലോഡ് ചെയ്യുന്നതിൽ പിശക്',
      loadFailed: 'വേദന ഡാറ്റ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല',
      clearFailed: 'വേദന ഡാറ്റ മായ്ക്കാൻ കഴിഞ്ഞില്ല',
      failed: 'വേദന നില രേഖപ്പെടുത്താൻ കഴിഞ്ഞില്ല: {{message}}'
    },
    success: {
      recorded: 'വേദന നില വിജയകരമായി രേഖപ്പെടുത്തി!',
      withAudio: 'ഓഡിയോ കുറിപ്പുമായി വേദന നില വിജയകരമായി രേഖപ്പെടുത്തി!',
      cleared: 'എല്ലാ വേദന ഡാറ്റയും വിജയകരമായി മായ്ച്ചു!'
    },
    confirm: {
      clearAll: 'ഇന്നത്തെ എല്ലാ വേദന ഡാറ്റയും മായ്ക്കാൻ നിങ്ങൾ ഉറപ്പാണോ?'
    }
  },
  audioRecorder: {
    title: 'ശബ്ദ റെക്കോർഡിംഗ്',
    help: {
      start: 'നിങ്ങളുടെ വേദന വിവരണം റെക്കോർഡ് ചെയ്യാൻ "റെക്കോർഡിംഗ് ആരംഭിക്കുക" ക്ലിക്ക് ചെയ്യുക.',
      recording: 'റെക്കോർഡിംഗ് പുരോഗതിയിലാണ്... പൂർത്തിയാക്കിയാൽ "റെക്കോർഡിംഗ് നിർത്തുക" ക്ലിക്ക് ചെയ്യുക.',
      recorded: 'ഓഡിയോ വിജയകരമായി റെക്കോർഡ് ചെയ്തു! നിങ്ങൾക്ക് പ്ലേ ചെയ്യാം, ഇല്ലാതാക്കാം അല്ലെങ്കിൽ പുതിയത് റെക്കോർഡ് ചെയ്യാം.'
    },
    buttons: {
      start: 'റെക്കോർഡിംഗ് ആരംഭിക്കുക',
      stop: 'റെക്കോർഡിംഗ് നിർത്തുക',
      play: 'റെക്കോർഡിംഗ് പ്ലേ ചെയ്യുക',
      pause: 'താൽക്കാലികമായി നിർത്തുക',
      delete: 'ഇല്ലാതാക്കുക',
      rerecord: 'വീണ്ടും റെക്കോർഡ് ചെയ്യുക'
    },
    info: {
      duration: 'കാലാവധി',
      format: 'ഫോർമാറ്റ്'
    },
    errors: {
      microphoneAccess: 'മൈക്രോഫോൺ ആക്സസ് ചെയ്യാൻ കഴിയില്ല. ദയവായി നിങ്ങളുടെ ബ്രൗസർ അനുമതികൾ പരിശോധിക്കുക.'
    }
  },
  audioPlayer: {
    label: 'ശബ്ദ കുറിപ്പ്',
    loading: 'ഓഡിയോ ലോഡ് ചെയ്യുന്നു...',
    errors: {
      loadFailed: 'ഓഡിയോ ലോഡ് ചെയ്യാൻ കഴിഞ്ഞില്ല: {{message}}',
      notLoaded: 'ഓഡിയോ ഇതുവരെ ലോഡ് ചെയ്തിട്ടില്ല',
      playFailed: 'ഓഡിയോ പ്ലേ ചെയ്യാൻ കഴിഞ്ഞില്ല',
      playFailedWithMessage: 'ഓഡിയോ പ്ലേ ചെയ്യാൻ കഴിഞ്ഞില്ല: {{message}}'
    }
  }
};

const hiOverrides = {
  app: {
    loginPortal: 'लॉगिन पोर्टल'
  },
  common: {
    back: 'वापस',
    cancel: 'रद्द करें',
    confirm: 'पुष्टि करें',
    loading: 'कृपया प्रतीक्षा करें...',
    language: 'भाषा',
    requiredField: 'कृपया सभी आवश्यक फ़ील्ड भरें'
  },
  languageGate: {
    title: 'अपनी भाषा चुनें',
    subtitle: 'पोर्टल जारी रखने के लिए अपनी पसंदीदा भाषा चुनें।',
    helper: 'आप लॉगिन स्क्रीन से कभी भी भाषा बदल सकते हैं।',
    helperEmphasis: 'चिंता न करें!',
    secondary: 'पहले ही कदम से आपको घर जैसा महसूस कराने में यह मदद करता है।'
  },
  buttons: {
    login: 'लॉगिन',
    register: 'पंजीकरण',
    verifyPasskey: 'पासकी सत्यापित करें'
  },
  login: {
    initialChoice: {
      login: 'लॉगिन',
      register: 'पंजीकरण'
    },
    passkey: {
      prompt: 'पासकी दर्ज करें *',
      helper: 'रोगी/चिकित्सक पंजीकरण के लिए पंजीकरण पासकी या प्रशासनिक पहुँच के लिए एडमिन पासकी का उपयोग करें',
      invalid: 'अमान्य पासकी। कृपया पुनः प्रयास करें।'
    },
    form: {
      idLabel: 'आईडी *',
      idPlaceholder: 'Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) या Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      passwordLabel: 'पासवर्ड *',
      passwordPlaceholder: 'अपना पासवर्ड दर्ज करें',
      rememberMe: 'मुझे 90 दिनों तक याद रखें'
    },
    errors: {
      missingId: 'कृपया अपना Patient ID या Doctor ID दर्ज करें',
      missingPassword: 'कृपया अपना पासवर्ड दर्ज करें',
      invalidIdFormat: 'अमान्य आईडी प्रारूप। कृपया Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) या Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001) का उपयोग करें',
      adminLoginDisabled: 'एडमिन लॉगिन उपलब्ध नहीं है। कृपया पंजीकरण विकल्प के माध्यम से एडमिन पासकी का उपयोग करें।',
      missingDoctorEmail: 'चिकित्सक पंजीकरण के लिए ईमेल आवश्यक है',
      missingDoctorPhone: 'चिकित्सक पंजीकरण के लिए फोन नंबर आवश्यक है',
      passwordMismatch: 'पासवर्ड मेल नहीं खाते',
      passwordLength: 'पासवर्ड कम से कम 6 अक्षरों का होना चाहिए',
      missingPatientDoctor: 'कृपया डॉक्टर चयन सहित सभी आवश्यक फ़ील्ड भरें',
      genericLogin: 'लॉगिन विफल रहा',
      loadDoctorsFailed: 'डॉक्टरों की सूची लोड नहीं हो सकी। कृपया पेज रीफ्रेश करें।',
      noDoctors: 'कोई डॉक्टर उपलब्ध नहीं है। कृपया सहायता से संपर्क करें।'
    },
    success: {
      welcomeDoctor: 'फिर से स्वागत है, डॉ. {{name}}!',
      welcomePatient: 'फिर से स्वागत है!'
    },
    register: {
      title: 'मैं *',
      patient: 'रोगी',
      doctor: 'चिकित्सक',
      firstName: {
        label: 'पहला नाम *',
        placeholder: 'अपना पहला नाम दर्ज करें'
      },
      lastName: {
        label: 'अंतिम नाम (वैकल्पिक)',
        placeholder: 'अपना अंतिम नाम दर्ज करें (वैकल्पिक)'
      },
      email: {
        label: 'ईमेल *',
        placeholder: 'अपना ईमेल पता दर्ज करें'
      },
      phone: {
        label: 'फोन नंबर *',
        placeholder: 'अपना फोन नंबर दर्ज करें'
      },
      selectDoctor: {
        label: 'अपने डॉक्टर का चयन करें *',
        placeholder: '-- डॉक्टर चुनें --',
        loading: 'डॉक्टरों को लोड किया जा रहा है...',
        noDoctors: 'कोई डॉक्टर उपलब्ध नहीं है। कृपया सहायता से संपर्क करें।'
      },
      dateOfBirth: {
        label: 'जन्म तिथि *'
      },
      contactNumber: {
        label: 'संपर्क नंबर *',
        placeholder: 'अपना संपर्क नंबर दर्ज करें'
      },
      whatsappNumber: {
        label: 'व्हाट्सएप नंबर (वैकल्पिक) 💬',
        placeholder: 'व्हाट्सएप नंबर दर्ज करें (उदा. +1234567890)',
        helper: 'यदि प्रदान किया गया, तो दवा सूचनाएँ व्हाट्सएप पर भेजी जाएँगी'
      },
      password: {
        label: 'पासवर्ड *',
        placeholder: 'अपना पासवर्ड दर्ज करें'
      },
      confirmPassword: {
        label: 'पासवर्ड की पुष्टि करें *',
        placeholder: 'अपना पासवर्ड पुष्टि करें'
      },
      passkeyTitle: 'पासकी दर्ज करें *',
      passkeyHelper: 'रोगी/चिकित्सक पंजीकरण के लिए पंजीकरण पासकी या प्रशासनिक पहुँच के लिए एडमिन पासकी का उपयोग करें'
    }
  },
  toast: {
    adminWelcome: 'स्वागत है, {{name}}!',
    doctorRegistrationSuccess: 'पंजीकरण सफल! आपका डॉक्टर आईडी: {{id}}. कृपया जारी रखने के लिए लॉगिन करें.'
  },
  dashboard: {
    loading: 'डैशबोर्ड लोड हो रहा है...',
    welcome: {
      title: 'पेन ट्रैकर में आपका स्वागत है',
      subtitle: 'अपने दर्द के स्तर और स्वास्थ्य प्रगति पर नज़र रखें'
    },
    stats: {
      logsToday: 'आज के लॉग',
      noPain: 'दर्द नहीं',
      mild: 'हल्का',
      moderatePain: 'मध्यम दर्द',
      severePain: 'तेज़ दर्द',
      worstPain: 'सबसे खराब दर्द',
      lessPain: 'कम दर्द',
      unknown: 'अज्ञात'
    },
    actions: {
      retry: 'फिर से प्रयास करें',
      bodyDiagram: 'शरीर का चित्र',
      viewLogs: 'लॉग देखें',
      viewStatistics: 'आँकड़े देखें',
      history: 'इतिहास'
    },
    errors: {
      title: 'डैशबोर्ड लोड करने में त्रुटि',
      loadFailed: 'दर्द डेटा लोड नहीं हो सका'
    },
    logs: {
      title: 'हाल के दर्द लॉग',
      emptyTitle: 'आज कोई दर्द लॉग नहीं',
      emptyText: 'शरीर के भागों पर क्लिक करके दर्द दर्ज करना शुरू करें'
    },
    sidebar: {
      title: 'पेन ट्रैकर',
      links: {
        dashboard: 'डैशबोर्ड',
        bodyDiagram: 'शरीर का चित्र',
        painLogs: 'दर्द लॉग',
        history: 'इतिहास',
        statistics: 'सांख्यिकी',
        distress: 'तनाव',
        medication: 'दवा'
      },
      user: {
        profile: 'रोगी प्रोफ़ाइल'
      },
      actions: {
        logout: 'लॉगआउट'
      }
    }
  },
  painScale: {
    title: 'दर्द स्तर दर्ज करें',
    vasTitle: 'विज़ुअल एनालॉग स्केल (VAS)',
    vasDescription: 'मानक 0–10 स्केल: 0 = कोई दर्द नहीं, 10 = सबसे खराब दर्द',
    vasCurrentValue: 'आपका दर्द स्तर',
    vasWorstPain: 'सबसे खराब दर्द',
    vasDescriptors: {
      noPain: 'कोई दर्द नहीं',
      mild: 'हल्का',
      moderate: 'मध्यम',
      severe: 'गंभीर',
      verySevere: 'बहुत गंभीर',
      worstPain: 'सबसे खराब दर्द'
    },
    fixedScales: 'निश्चित दर्द पैमाने: कोई दर्द नहीं (0) • कम दर्द (1-3) • मध्यम (4-7) • गंभीर (8-10)',
    frontView: '👤 सामने का दृश्य',
    backView: '👥 पीछे का दृश्य',
    levels: {
      noPain: 'कोई दर्द नहीं',
      lessPain: 'कम दर्द',
      moderatePain: 'मध्यम दर्द',
      severePain: 'तेज़ दर्द'
    },
    descriptions: {
      noPain: 'कोई असुविधा नहीं',
      lessPain: 'हल्की असुविधा',
      moderatePain: 'ध्यान देने योग्य लेकिन प्रबंधनीय',
      severePain: 'तीव्र और चिंताजनक'
    },
    additionalInfo: 'अतिरिक्त जानकारी (वैकल्पिक)',
    painScale: 'दर्द पैमाना',
    timeOfDay: 'दिन का समय',
    mood: 'मनोदशा',
    activity: 'गतिविधि स्तर',
    symptoms: 'लक्षण (अल्पविराम से अलग)',
    triggers: 'ट्रिगर (अल्पविराम से अलग)',
    notes: 'नोट्स (पाठ)',
    audioNote: 'या अपना विवरण रिकॉर्ड करें (ऑडियो)',
    placeholders: {
      symptoms: 'जैसे, कठोरता, सूजन, सुन्नता',
      triggers: 'जैसे, तनाव, मौसम, व्यायाम',
      notes: 'अपना दर्द विवरण यहाँ टाइप करें...',
      selectMood: 'मनोदशा चुनें (वैकल्पिक)',
      selectActivity: 'गतिविधि चुनें (वैकल्पिक)',
      symptomsMultiSelect: 'कई लक्षणों को चुनने के लिए Ctrl/Cmd दबाकर रखें'
    },
    timeOptions: {
      morning: 'सुबह',
      afternoon: 'दोपहर',
      evening: 'शाम',
      night: 'रात'
    },
    moodOptions: {
      good: 'अच्छा',
      moderate: 'मध्यम',
      bad: 'खराब'
    },
    activityOptions: {
      able: 'सक्षम',
      partiallyDependent: 'आंशिक रूप से निर्भर',
      notAble: 'असक्षम'
    },
    symptomOptions: {
      none: 'कुछ नहीं',
      vomiting: 'उल्टी',
      nausea: 'मतली',
      dizziness: 'चक्कर आना',
      headache: 'सिरदर्द',
      fever: 'बुखार',
      fatigue: 'थकान',
      shortnessOfBreath: 'सांस की तकलीफ',
      cough: 'खांसी',
      swelling: 'सूजन',
      numbness: 'सुन्नता',
      weakness: 'कमजोरी'
    },
    buttons: {
      cancel: 'रद्द करें',
      clear: 'साफ़ करें',
      record: 'दर्द दर्ज करें'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'दिन का समय चुनें',
    success: {
      recorded: 'दर्द स्तर सफलतापूर्वक दर्ज किया गया',
      withAudio: 'ऑडियो नोट के साथ दर्द स्तर सफलतापूर्वक दर्ज किया गया'
    },
    errors: {
      failed: 'दर्द स्तर दर्ज करने में विफल: {{message}}'
    },
    confirm: {
      clearAll: 'क्या आप वाकई आज के सभी दर्द डेटा को साफ़ करना चाहते हैं?'
    }
  },
  profile: {
    title: 'मेरी प्रोफ़ाइल',
    subtitle: 'अपनी व्यक्तिगत जानकारी प्रबंधित करें',
    loading: 'प्रोफ़ाइल लोड हो रही है...',
    editWhatsApp: '✏️ व्हाट्सएप नंबर संपादित करें',
    whatsAppLabel: 'व्हाट्सएप नंबर',
    whatsAppPlaceholder: 'व्हाट्सएप नंबर दर्ज करें (उदा. +1234567890)',
    whatsAppHelper: 'यदि प्रदान किया गया, तो दवा सूचनाएँ व्हाट्सएप पर भेजी जाएँगी। हटाने के लिए खाली छोड़ दें।',
    save: '💾 परिवर्तन सहेजें',
    saving: 'सहेजा जा रहा है...',
    cancel: 'रद्द करें',
    success: 'व्हाट्सएप नंबर सफलतापूर्वक अपडेट किया गया!',
    optional: 'वैकल्पिक',
    forNotifications: 'दवा सूचनाओं के लिए',
    notProvided: 'प्रदान नहीं किया गया',
    patientId: 'रोगी आईडी',
    age: 'उम्र',
    sections: {
      personalInfo: 'व्यक्तिगत जानकारी',
      contactInfo: 'संपर्क जानकारी'
    },
    errors: {
      authRequired: 'प्रमाणीकरण आवश्यक',
      updateFailed: 'व्हाट्सएप नंबर अपडेट करने में विफल'
    }
  },
  painLogs: {
    title: 'दर्द लॉग',
    loading: 'दर्द लॉग लोड हो रहे हैं...',
    error: 'दर्द लॉग लोड करने में त्रुटि',
    filters: {
      painLevel: 'दर्द स्तर',
      allLevels: 'सभी स्तर',
      noPain: 'कोई दर्द नहीं',
      mild: 'हल्का',
      lessPain: 'कम दर्द',
      moderatePain: 'मध्यम दर्द',
      severePain: 'तेज़ दर्द',
      worstPain: 'सबसे खराब दर्द'
    },
    table: {
      painLevel: 'दर्द स्तर',
      zone: 'शरीर का भाग',
      date: 'तारीख',
      time: 'समय',
      actions: 'क्रियाएं'
    },
    actions: {
      delete: 'हटाएं',
      previous: 'पिछला',
      next: 'अगला'
    },
    empty: 'कोई दर्द लॉग नहीं मिला',
    confirmDelete: 'क्या आप वाकई इस दर्द लॉग को हटाना चाहते हैं?',
    success: {
      deleted: 'दर्द लॉग सफलतापूर्वक हटाया गया'
    },
    errors: {
      deleteFailed: 'दर्द लॉग हटाने में विफल'
    }
  },
  statistics: {
    title: 'सांख्यिकी',
    vasScaleCaption: 'दर्द स्तर: VAS 0–10 — कोई दर्द नहीं · हल्का (1–3) · मध्यम (4–6) · तीव्र (7–9) · सबसे खराब (10)',
    loading: 'सांख्यिकी लोड हो रही है...',
    error: 'सांख्यिकी लोड करने में त्रुटि',
    loadFailed: 'सांख्यिकी लोड करने में विफल',
    painLevelDistribution: 'दर्द स्तर वितरण',
    painTrends: 'समय के साथ दर्द रुझान',
    painByZone: 'शरीर क्षेत्र के अनुसार दर्द',
    timePeriod: 'समय अवधि',
    filterByRegion: 'क्षेत्र के अनुसार फ़िल्टर करें',
    lastDays: 'पिछले {{count}} दिन',
    totalLogs: 'कुल लॉग',
    empty: 'डेटा उपलब्ध नहीं',
    emptyText: 'सांख्यिकी और रुझान देखने के लिए अपने दर्द को ट्रैक करना शुरू करें',
    labels: {
      severePain: 'तेज़ दर्द',
      moderatePain: 'मध्यम दर्द',
      mild: 'हल्का',
      lessPain: 'कम दर्द',
      noPain: 'कोई दर्द नहीं',
      worstPain: 'सबसे खराब दर्द',
      intensityOverTime: 'समय के साथ दर्द की तीव्रता',
      painIntensity: 'दर्द की तीव्रता',
      painLevel: 'दर्द स्तर',
      time: 'समय',
      notes: 'टिप्पणियाँ'
    },
    noDataForRegion: 'इस क्षेत्र के लिए डेटा उपलब्ध नहीं',
    totalEntries: 'कुल प्रविष्टियाँ',
    emptyRegionLine: 'चयनित अवधि में "{{zone}}" के लिए दर्द लॉग नहीं मिले। लंबी अवधि चुनें या दूसरा क्षेत्र चुनें।',
    noPainTrendsData: 'चयनित अवधि के लिए दर्द रुझान डेटा उपलब्ध नहीं।',
    emptyRegionBar: 'चयनित अवधि में "{{zone}}" के लिए दर्द डेटा नहीं मिला।',
    noPainByZoneData: 'शरीर क्षेत्र के अनुसार दर्द डेटा उपलब्ध नहीं।',
    emptyRegionDoughnut: 'चयनित अवधि में "{{zone}}" के लिए दर्द स्तर वितरण डेटा नहीं मिला।',
    noPainLevelDistributionData: 'दर्द स्तर वितरण डेटा उपलब्ध नहीं।',
    painTimelineFor: '{{zone}} के लिए दर्द टाइमलाइन',
    allRegions: 'सभी क्षेत्र',
    dateRange: 'तिथि सीमा',
    heatmapTitle: 'क्षेत्र के अनुसार दर्द हीटमैप',
    heatmap: {
      zone: 'क्षेत्र',
      intensity: 'तीव्रता',
      logs: 'लॉग',
      intensityScale: 'दर्द तीव्रता पैमाना',
      intensityLabels: {
        severe: 'तीव्र',
        moderate: 'मध्यम',
        less: 'कम',
        none: 'कोई नहीं'
      }
    }
  },
  medication: {
    title: 'दवा',
    subtitle: 'अपने डॉक्टर द्वारा निर्धारित दवा योजनाओं की समीक्षा करें। एक फार्मेसी-तैयार प्रति डाउनलोड करें या आवश्यकता पड़ने पर पिछले प्रिस्क्रिप्शन को फिर से देखें।',
    loading: 'प्रिस्क्रिप्शन लोड हो रहे हैं...',
    empty: {
      title: 'अभी तक कोई प्रिस्क्रिप्शन नहीं',
      message: 'आपका डॉक्टर यहाँ दवा योजनाएँ साझा करेगा। अपनी सलाह के बाद वापस जांचें।'
    },
    description: 'अपने डॉक्टर द्वारा निर्धारित दवा योजनाओं की समीक्षा करें। एक फार्मेसी-तैयार प्रति डाउनलोड करें या आवश्यकता पड़ने पर पिछले प्रिस्क्रिप्शन को फिर से देखें।',
    legacy: {
      title: 'पुराना प्रिस्क्रिप्शन',
      subtitle: 'यह प्रिस्क्रिप्शन पिछले प्रारूप का उपयोग करके भेजा गया था।',
      badge: 'टेक्स्ट-आधारित प्रिस्क्रिप्शन'
    },
    current: {
      title: '⭐ वर्तमान प्रिस्क्रिप्शन',
      badge: 'वर्तमान प्रिस्क्रिप्शन',
      lastUpdated: 'अंतिम अपडेट',
      notice: 'यह सबसे हाल का प्रिस्क्रिप्शन है। इन निर्देशों का सावधानीपूर्वक पालन करें और दवा को बिल्कुल निर्धारित रूप में देने के लिए अपने फार्मासिस्ट को डाउनलोड करने योग्य प्रति प्रदान करें।'
    },
    history: {
      title: '📚 प्रिस्क्रिप्शन इतिहास',
      subtitle: 'अपने डॉक्टर द्वारा साझा किए गए पिछले प्रिस्क्रिप्शन की समीक्षा करें।',
      badge: 'प्रिस्क्रिप्शन इतिहास'
    },
    download: {
      formal: '📥 औपचारिक प्रिस्क्रिप्शन डाउनलोड करें',
      text: '📄 टेक्स्ट प्रति डाउनलोड करें',
      success: 'प्रिस्क्रिप्शन डाउनलोड किया गया',
      error: 'डाउनलोड करने के लिए कोई प्रिस्क्रिप्शन उपलब्ध नहीं'
    },
    labels: {
      patient: 'मरीज',
      doctor: 'डॉक्टर',
      issuedOn: 'जारी किया गया',
      medication: 'दवा',
      doseQty: 'खुराक / मात्रा',
      timing: 'समय',
      food: 'भोजन',
      duration: 'अवधि',
      notes: 'नोट्स',
      patientInstructions: 'मरीज निर्देश',
      pharmacyNotes: 'फार्मेसी नोट्स',
      asPrescribed: 'निर्धारित अनुसार',
      asDirected: 'निर्देशित अनुसार',
      id: 'आईडी:',
      reg: 'रजिस्ट्रेशन:',
      contact: 'संपर्क:',
      prescribingDoctor: 'प्रिस्क्रिप्शन देने वाला डॉक्टर:',
      treatingPhysician: 'उपचार करने वाला चिकित्सक',
      yourDoctor: 'आपका डॉक्टर',
      schedule: {
        morning: 'सुबह',
        afternoon: 'दोपहर',
        evening: 'शाम',
        night: 'रात'
      },
      foodInstruction: {
        before: 'भोजन से पहले',
        with: 'भोजन के साथ',
        after: 'भोजन के बाद',
        none: 'भोजन पर निर्भर नहीं'
      }
    },
    footer: {
      needAssistance: 'सहायता चाहिए?',
      shareNote: 'सत्यापन और पूर्ति के लिए डाउनलोड किए गए PDF को अपने फार्मासिस्ट या परामर्श चिकित्सक के साथ साझा करें।'
    },
    success: {
      newPrescription: '💊 नया प्रिस्क्रिप्शन प्राप्त हुआ',
      downloaded: 'प्रिस्क्रिप्शन डाउनलोड किया गया'
    },
    errors: {
      loadFailed: 'अभी प्रिस्क्रिप्शन लोड नहीं कर सकते',
      noPrescription: 'डाउनलोड करने के लिए कोई प्रिस्क्रिप्शन उपलब्ध नहीं'
    },
    reminders: {
      title: 'दवा अनुस्मारक',
      next: 'अगला अनुस्मारक',
      at: 'बजे',
      taken: 'ली गई! 30 मिनट में दर्द रिकॉर्ड करें',
      iTookIt: 'मैंने इसे ले लिया',
      takeMedication: 'दवा लें: {{name}}',
      recordPainAfter: 'अपने डॉक्टर के लिए लेने के 30 मिनट बाद दर्द रिकॉर्ड करें.',
      recordPainNow: 'अपने डॉक्टर को प्रभाव देखने के लिए दवा लेने के 30 मिनट बाद अभी दर्द रिकॉर्ड करें.',
      recordPainNotification: 'दवा लेने के 30 मिनट बाद अभी अपना दर्द स्तर रिकॉर्ड करें.',
      recordPain: 'दर्द रिकॉर्ड करें',
      recordPainPrompt: 'अपनी दवा का प्रभाव ट्रैक करने के लिए अभी अपना दर्द रिकॉर्ड करें.',
      recordPainNowButton: 'अभी दर्द रिकॉर्ड करें',
      upcoming: 'आगामी',
      noUpcoming: 'कोई आगामी अनुस्मारक नहीं'
    }
  },
  messages: {
    title: 'संदेश',
    loading: 'संदेश लोड हो रहे हैं...',
    error: 'संदेश लोड करने में विफल',
    empty: 'अभी तक कोई संदेश नहीं'
  },
  history: {
    title: 'इतिहास',
    subtitle: 'आपकी सभी दर्द ट्रैकिंग गतिविधियों का पूरा रिकॉर्ड',
    loading: 'इतिहास लोड हो रहा है...',
    error: {
      title: 'इतिहास लोड करने में त्रुटि',
      message: 'इतिहास डेटा लोड करने में विफल',
      retry: 'पुनः प्रयास करें'
    },
    empty: {
      title: 'कोई रिकॉर्ड नहीं मिला',
      filtered: 'अपने फ़िल्टर या खोज शब्दों को समायोजित करने का प्रयास करें',
      noData: 'अपना इतिहास यहाँ देखने के लिए अपने दर्द को ट्रैक करना शुरू करें'
    },
    stats: {
      total: 'कुल रिकॉर्ड',
      today: 'आज',
      thisWeek: 'इस सप्ताह',
      thisMonth: 'इस महीने'
    },
    filters: {
      painLevel: 'दर्द स्तर:',
      view: 'दृश्य:',
      dateRange: 'तारीख सीमा:',
      allLevels: 'सभी स्तर',
      noPain: 'कोई दर्द नहीं',
      mild: 'हल्का',
      lessPain: 'कम दर्द',
      moderatePain: 'मध्यम दर्द',
      severePain: 'तीव्र दर्द',
      worstPain: 'सबसे खराब दर्द',
      bothViews: 'दोनों दृश्य',
      frontView: 'सामने का दृश्य',
      backView: 'पीछे का दृश्य',
      allTime: 'सभी समय',
      today: 'आज',
      thisWeek: 'इस सप्ताह',
      thisMonth: 'इस महीने'
    },
    search: {
      placeholder: 'रिकॉर्ड खोजें...'
    },
    actions: {
      refresh: 'ताज़ा करें',
      deleteLogs: '🗑️ लॉग हटाएं',
      selectAll: 'सभी चुनें',
      deselectAll: 'सभी अचयनित करें',
      deleteSelected: 'चयनित हटाएं',
      cancel: 'रद्द करें'
    },
    deleteMode: {
      title: '🗑️ हटाने का मोड: हटाने के लिए लॉग चुनें',
      selected: 'चयनित',
      confirm: 'क्या आप वाकई {{count}} चयनित लॉग(स) हटाना चाहते हैं? इस क्रिया को पूर्ववत नहीं किया जा सकता।',
      success: '{{count}} लॉग(स) सफलतापूर्वक हटाए गए',
      error: 'चयनित लॉग हटाने में विफल',
      selectAtLeastOne: 'कृपया कम से कम एक लॉग चुनें'
    },
    header: {
      title: 'इतिहास रिकॉर्ड ({{count}})'
    },
    labels: {
      intensity: 'तीव्रता:',
      symptoms: 'लक्षण:',
      triggers: 'ट्रिगर:',
      view: 'दृश्य',
      unknownTime: 'अज्ञात समय',
      invalidDate: 'अमान्य तारीख',
      noDate: 'कोई तारीख नहीं',
      justNow: 'अभी',
      minutesAgo: 'मिनट पहले',
      hoursAgo: 'घंटे पहले'
    },
    painLevels: {
      'no-pain': 'कोई दर्द नहीं',
      'mild': 'हल्का',
      'moderate': 'मध्यम दर्द',
      'severe': 'तीव्र दर्द',
      'worst-pain': 'सबसे खराब दर्द',
      'less-pain': 'कम दर्द',
      'moderate-pain': 'मध्यम दर्द',
      'severe-pain': 'तीव्र दर्द'
    }
  },
  distress: {
    title: '🌡️ तनाव ट्रैकर',
    subtitle: 'समय के साथ अपने तनाव के स्तर को रिकॉर्ड और निगरानी करें',
    loading: 'तनाव इतिहास लोड हो रहा है...',
    error: {
      title: 'तनाव इतिहास लोड करने में त्रुटि',
      message: 'तनाव लॉग लोड करने में विफल',
      retry: 'पुनः प्रयास करें',
      login: 'तनाव लॉग देखने के लिए कृपया लॉगिन करें'
    },
    empty: {
      title: 'कोई तनाव लॉग नहीं मिला',
      textFilter: 'आपके फ़िल्टर मानदंडों से मेल खाने वाले कोई लॉग नहीं',
      textNoFilter: 'ऊपर दिए गए थर्मामीटर का उपयोग करके अपना तनाव स्तर रिकॉर्ड करें'
    },
    status: {
      offline: 'ऑफ़लाइन मोड',
      syncing: 'सिंक हो रहा है…',
      online: 'ऑनलाइन',
      pending: 'लंबित',
      pendingSync: 'लंबित सिंक',
      pendingSyncEntries: 'लंबित सिंक प्रविष्टियां',
      syncNow: 'अभी सिंक करें'
    },
    filters: {
      minScore: 'स्कोर:',
      startDate: 'प्रारंभ तिथि:',
      endDate: 'अंत तिथि:',
      allScores: 'सभी स्कोर',
      score0: '0+',
      score4: '4+ (क्लिनिकली महत्वपूर्ण)',
      score7: '7+ (उच्च)',
      clinicallySignificant: '4+ (क्लिनिकली महत्वपूर्ण)',
      high: '7+ (उच्च)',
      clear: 'फ़िल्टर साफ़ करें'
    },
    stats: {
      totalEntries: 'कुल प्रविष्टियां',
      averageScore: 'औसत स्कोर',
      highestScore: 'उच्चतम स्कोर',
      highDistress: 'उच्च तनाव (≥4)'
    },
    labels: {
      distressScore: 'तनाव स्कोर:',
      sourcesOfDistress: 'तनाव के स्रोत:',
      notes: 'नोट्स:',
      extreme: 'चरम तनाव',
      moderateToSevere: 'मध्यम से गंभीर तनाव',
      mild: 'हल्का तनाव',
      none: 'कोई तनाव नहीं',
      alert: '🚨 क्लिनिकली महत्वपूर्ण तनाव - आपके डॉक्टर को सूचित किया गया है'
    },
    header: {
      title: 'तनाव लॉग ({{count}})',
      refresh: 'ताज़ा करें'
    }
  },
  distressThermometer: {
    title: '🌡️ तनाव थर्मामीटर',
    subtitle: 'पिछले सप्ताह में अपने वर्तमान तनाव स्तर को रेट करें (0 = कोई तनाव नहीं, 10 = चरम तनाव)',
    guidelines: 'NCCN तनाव थर्मामीटर दिशानिर्देशों के आधार पर',
    problemList: {
      label: 'समस्या सूची - आपके तनाव का कारण क्या है? (वैकल्पिक)',
      helper: 'सभी लागू विकल्पों का चयन करें (NCCN समस्या सूची)'
    },
    notes: {
      label: 'अतिरिक्त नोट्स (वैकल्पिक)',
      placeholder: 'अपने तनाव के बारे में अतिरिक्त विवरण वर्णन करें...',
      enterNumberHint: 'या नंबर दर्ज करें (0-10):'
    },
    buttons: {
      record: 'तनाव स्तर रिकॉर्ड करें',
      recording: 'रिकॉर्ड हो रहा है...',
      clear: 'साफ़ करें'
    },
    scoreLabels: {
      noDistress: 'कोई तनाव नहीं',
      mildDistress: 'हल्का तनाव',
      moderateDistress: 'मध्यम तनाव',
      severeDistress: 'गंभीर तनाव',
      extremeDistress: 'चरम तनाव'
    },
    alert: '⚠️ NCCN चेतावनी: {{score}} स्कोर क्लिनिकली महत्वपूर्ण तनाव को इंगित करता है (≥4)। आपके स्वास्थ्य सेवा प्रदाता को सूचित किया जाएगा।',
    success: {
      recorded: 'तनाव स्तर {{score}} सफलतापूर्वक रिकॉर्ड किया गया'
    },
    errors: {
      login: 'तनाव रिकॉर्ड करने के लिए कृपया लॉगिन करें',
      recordFailed: 'तनाव रिकॉर्ड करने में विफल'
    },
    sources: {
      transportation: 'परिवहन',
      housing: 'आवास',
      insuranceFinancial: 'बीमा/वित्तीय',
      workSchool: 'काम/स्कूल',
      dealingWithPartner: 'साथी के साथ निपटना',
      dealingWithChildren: 'बच्चों के साथ निपटना',
      familyHealthIssues: 'पारिवारिक स्वास्थ्य समस्याएं',
      depression: 'अवसाद',
      fears: 'डर',
      nervousness: 'घबराहट',
      sadness: 'उदासी',
      worry: 'चिंता',
      lossOfInterest: 'सामान्य गतिविधियों में रुचि की हानि',
      spiritualReligiousConcerns: 'आध्यात्मिक/धार्मिक चिंताएं',
      fatigue: 'थकान',
      pain: 'दर्द',
      sleepProblems: 'नींद की समस्याएं',
      eatingProblems: 'खाने की समस्याएं',
      bathingDressing: 'स्नान/कपड़े',
      breathingProblems: 'सांस लेने की समस्याएं',
      nausea: 'मतली',
      memoryConcentration: 'स्मृति/एकाग्रता',
      mouthSores: 'मुंह के छाले',
      skinProblems: 'त्वचा की समस्याएं',
      tinglingHandsFeet: 'हाथों/पैरों में झुनझुनी',
      otherPhysicalProblems: 'अन्य शारीरिक समस्याएं'
    }
  },
  bodyParts: {
    'right palm': 'दाहिना हथेली',
    'left lower abdomen': 'बायां निचला पेट',
    'right lower abdomen': 'दाहिना निचला पेट',
    'left upper abdomen': 'बायां ऊपरी पेट',
    'right upper abdomen': 'दाहिना ऊपरी पेट',
    'left front forearm': 'बायां सामने की बांह',
    'left elbow': 'बायां कोहनी',
    'right elbow': 'दाहिना कोहनी',
    'front head right': 'सामने का सिर दाहिना',
    'front head left': 'सामने का सिर बायां',
    'frons right': 'माथा दाहिना',
    'frons left': 'माथा बायां',
    'left shoulder': 'बायां कंधा',
    'cervicis left': 'गर्दन बायां',
    'left lower leg': 'बायां निचला पैर',
    'right lower leg': 'दाहिना निचला पैर',
    'left ankle': 'बायां टखना',
    'right ankle': 'दाहिना टखना',
    'right foot': 'दाहिना पैर',
    'left foot': 'बायां पैर',
    'left upper arm': 'बायां ऊपरी बांह',
    'left lower arm': 'बायां निचली बांह',
    'facies left': 'चेहरा बायां',
    'cervicis right': 'गर्दन दाहिना',
    'facies right': 'चेहरा दाहिना',
    'right front forearm': 'दाहिना सामने की बांह',
    'right wrist': 'दाहिना कलाई',
    'left wrist': 'बायां कलाई',
    'left palm': 'बायां हथेली',
    'right upper chest': 'दाहिना ऊपरी छाती',
    'left upper chest': 'बायां ऊपरी छाती',
    'right lower chest': 'दाहिना निचली छाती',
    'left lower chest': 'बायां निचली छाती',
    'right shoulder': 'दाहिना कंधा',
    'right upper arm': 'दाहिना ऊपरी बांह',
    'right diaphragm': 'दाहिना डायाफ्राम',
    'right lower arm': 'दाहिना निचली बांह',
    'left diaphragm': 'बायां डायाफ्राम',
    'left upper leg': 'बायां ऊपरी पैर',
    'left knee': 'बायां घुटना',
    'left lower thigh': 'बायां निचली जांघ',
    'left upper thigh': 'बायां ऊपरी जांघ',
    'right upper leg': 'दाहिना ऊपरी पैर',
    'right knee': 'दाहिना घुटना',
    'right lower thigh': 'दाहिना निचली जांघ',
    'right upper thigh': 'दाहिना ऊपरी जांघ',
    'right pelvic region': 'दाहिना श्रोणि क्षेत्र',
    'left pelvic region': 'बायां श्रोणि क्षेत्र',
    'back head left': 'पीछे का सिर बायां',
    'back head right': 'पीछे का सिर दाहिना',
    'left upper back': 'बायां ऊपरी पीठ',
    'left lumbar': 'बायां कमर',
    'left buttock': 'बायां नितंब',
    'left back of knee': 'बायां घुटने का पीछे',
    'right back of knee': 'दाहिना घुटने का पीछे',
    'left upper calf': 'बायां ऊपरी पिंडली',
    'right upper calf': 'दाहिना ऊपरी पिंडली',
    'right lower calf': 'दाहिना निचली पिंडली',
    'left lower calf': 'बायां निचली पिंडली',
    'left ankle & foot': 'बायां टखना और पैर',
    'right upper forearm': 'दाहिना ऊपरी बांह',
    'left lower arm': 'बायां निचली बांह',
    'left upper forearm': 'बायां ऊपरी बांह',
    'right ankle & foot': 'दाहिना टखना और पैर',
    'right buttock': 'दाहिना नितंब',
    'lower head left': 'निचला सिर बायां',
    'lower head right': 'निचला सिर दाहिना',
    'neck left': 'गर्दन बायां',
    'right upper back': 'दाहिना ऊपरी पीठ',
    'left middle back': 'बायां मध्य पीठ',
    'right middle back': 'दाहिना मध्य पीठ',
    'left lower forearm': 'बायां निचली बांह',
    'right lumbar': 'दाहिना कमर',
    'left sacral': 'बायां त्रिकास्थि',
    'right sacral': 'दाहिना त्रिकास्थि',
    'right lower forearm': 'दाहिना निचली बांह',
    'neck right': 'गर्दन दाहिना',
    left: 'बायां',
    right: 'दाहिना',
    upper: 'ऊपरी',
    lower: 'निचला',
    front: 'सामने',
    back: 'पीछे',
    middle: 'मध्य'
  },
  bodyDiagram: {
    selectTimeOfDay: 'दिन का समय चुनें',
    title: 'शरीर का चित्र',
    subtitle: 'दर्द स्तर दर्ज करने के लिए किसी भी शरीर क्षेत्र पर क्लिक करें',
    loading: 'शरीर का चित्र लोड हो रहा है...',
    todaysRecords: 'आज के दर्द रिकॉर्ड',
    unknownZone: 'अज्ञात क्षेत्र',
    instructions: {
      title: 'कैसे उपयोग करें',
      step1: 'दर्द दर्ज करने के लिए किसी भी शरीर क्षेत्र पर क्लिक करें',
      step2: 'अपना दर्द स्तर चुनें (कोई दर्द नहीं, कम दर्द, मध्यम दर्द, या तेज़ दर्द)',
      step3: 'लक्षण, ट्रिगर और नोट्स जैसे वैकल्पिक विवरण जोड़ें',
      step4: 'नीचे दिए गए लॉग में अपना दर्द डेटा देखें'
    },
    view: {
      front: 'सामने का दृश्य',
      back: 'पीछे का दृश्य',
      switch: 'दृश्य बदलें'
    },
    actions: {
      clearAll: 'सभी साफ़ करें',
      sync: 'अभी सिंक करें',
      syncing: 'सिंक हो रहा है...'
    },
    status: {
      online: 'ऑनलाइन',
      offline: 'ऑफ़लाइन',
      syncing: 'सिंक हो रहा है'
    },
    empty: {
      title: 'आज कोई दर्द रिकॉर्ड नहीं',
      text: 'अपने दर्द को ट्रैक करना शुरू करने के लिए ऊपर शरीर क्षेत्रों पर क्लिक करें'
    },
    pending: 'सिंक लंबित',
    time: {
      unknown: 'अज्ञात समय',
      invalidDate: 'अमान्य तारीख',
      invalid: 'अमान्य समय',
      justNow: 'अभी',
      minutesAgo: 'मिनट पहले',
      hoursAgo: 'घंटे पहले'
    },
    success: {
      recorded: 'दर्द स्तर सफलतापूर्वक दर्ज किया गया!',
      withAudio: 'ऑडियो नोट के साथ दर्द स्तर सफलतापूर्वक दर्ज किया गया!',
      cleared: 'सभी दर्द डेटा सफलतापूर्वक साफ़ किया गया!'
    },
    confirm: {
      clearAll: 'क्या आप वाकई आज के सभी दर्द डेटा को साफ़ करना चाहते हैं?'
    },
    errors: {
      title: 'शरीर का चित्र लोड करने में त्रुटि',
      loadFailed: 'दर्द डेटा लोड नहीं हो सका',
      clearFailed: 'दर्द डेटा साफ़ करने में विफल',
      failed: 'दर्द स्तर दर्ज करने में विफल: {{message}}'
    }
  },
  audioRecorder: {
    title: 'आवाज रिकॉर्डिंग',
    help: {
      start: 'अपने दर्द विवरण को रिकॉर्ड करने के लिए "रिकॉर्डिंग शुरू करें" पर क्लिक करें.',
      recording: 'रिकॉर्डिंग प्रगति में है... समाप्त होने पर "रिकॉर्डिंग रोकें" पर क्लिक करें.',
      recorded: 'ऑडियो सफलतापूर्वक रिकॉर्ड किया गया! आप प्ले कर सकते हैं, हटा सकते हैं या नया रिकॉर्ड कर सकते हैं.'
    },
    buttons: {
      start: 'रिकॉर्डिंग शुरू करें',
      stop: 'रिकॉर्डिंग रोकें',
      play: 'रिकॉर्डिंग प्ले करें',
      pause: 'रोकें',
      delete: 'हटाएं',
      rerecord: 'फिर से रिकॉर्ड करें'
    },
    info: {
      duration: 'अवधि',
      format: 'प्रारूप'
    },
    errors: {
      microphoneAccess: 'माइक्रोफोन तक पहुंचने में असमर्थ. कृपया अपने ब्राउज़र अनुमतियों की जांच करें.'
    }
  },
  audioPlayer: {
    label: 'आवाज नोट',
    loading: 'ऑडियो लोड हो रहा है...',
    errors: {
      loadFailed: 'ऑडियो लोड करने में विफल: {{message}}',
      notLoaded: 'ऑडियो अभी तक लोड नहीं हुआ',
      playFailed: 'ऑडियो प्ले करने में विफल',
      playFailedWithMessage: 'ऑडियो प्ले करने में विफल: {{message}}'
    }
  }
};

const mrOverrides = {
  app: {
    loginPortal: 'लॉगिन पोर्टल'
  },
  common: {
    back: 'मागे',
    cancel: 'रद्द करा',
    confirm: 'निश्चित करा',
    loading: 'कृपया थांबा...',
    language: 'भाषा',
    requiredField: 'कृपया सर्व आवश्यक फील्ड भरा'
  },
  languageGate: {
    title: 'तुमची भाषा निवडा',
    subtitle: 'पोर्टल पुढे सुरू करण्यासाठी तुमची आवडती भाषा निवडा.',
    helper: 'तुम्ही लॉगिन स्क्रीनवरून कधीही भाषा बदलू शकता.',
    helperEmphasis: 'काळजी करू नका!',
    secondary: 'पहिल्याच पावलापासून तुम्हाला घरच्यासारखे वाटण्यासाठी हे मदत करते.'
  },
  buttons: {
    login: 'लॉगिन',
    register: 'नोंदणी',
    verifyPasskey: 'पासकी पडताळा'
  },
  login: {
    initialChoice: {
      login: 'लॉगिन',
      register: 'नोंदणी'
    },
    passkey: {
      prompt: 'पासकी प्रविष्ट करा *',
      helper: 'रुग्ण/डॉक्टर नोंदणीसाठी नोंदणी पासकी किंवा प्रशासकीय प्रवेशासाठी अॅडमिन पासकी वापरा',
      invalid: 'अवैध पासकी. कृपया पुन्हा प्रयत्न करा.'
    },
    form: {
      idLabel: 'आयडी *',
      idPlaceholder: 'Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) किंवा Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      passwordLabel: 'पासवर्ड *',
      passwordPlaceholder: 'तुमचा पासवर्ड प्रविष्ट करा',
      rememberMe: 'मला 90 दिवसांसाठी लक्षात ठेवा'
    },
    errors: {
      missingId: 'कृपया तुमचा Patient ID किंवा Doctor ID प्रविष्ट करा',
      missingPassword: 'कृपया तुमचा पासवर्ड प्रविष्ट करा',
      invalidIdFormat: 'अवैध आयडी फॉरमॅट. कृपया Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) किंवा Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001) वापरा',
      adminLoginDisabled: 'अॅडमिन लॉगिन उपलब्ध नाही. कृपया नोंदणी पर्यायातून अॅडमिन पासकी वापरा.',
      missingDoctorEmail: 'डॉक्टर नोंदणीसाठी ईमेल आवश्यक आहे',
      missingDoctorPhone: 'डॉक्टर नोंदणीसाठी फोन नंबर आवश्यक आहे',
      passwordMismatch: 'पासवर्ड जुळत नाहीत',
      passwordLength: 'पासवर्ड किमान 6 अक्षरांचा असावा',
      missingPatientDoctor: 'डॉक्टर निवडीसह सर्व आवश्यक फील्ड कृपया भरा',
      genericLogin: 'लॉगिन अयशस्वी झाले',
      loadDoctorsFailed: 'डॉक्टरांची यादी लोड करण्यात अयशस्वी. कृपया पृष्ठ रीफ्रेश करा.',
      noDoctors: 'कोणतेही डॉक्टर उपलब्ध नाहीत. कृपया सहाय्याशी संपर्क करा.'
    },
    success: {
      welcomeDoctor: 'पुन्हा स्वागत आहे, डॉ. {{name}}!',
      welcomePatient: 'पुन्हा स्वागत आहे!'
    },
    register: {
      title: 'मी *',
      patient: 'रुग्ण',
      doctor: 'डॉक्टर',
      firstName: {
        label: 'पहिले नाव *',
        placeholder: 'तुमचे पहिले नाव प्रविष्ट करा'
      },
      lastName: {
        label: 'आडनाव (ऐच्छिक)',
        placeholder: 'तुमचे आडनाव प्रविष्ट करा (ऐच्छिक)'
      },
      email: {
        label: 'ईमेल *',
        placeholder: 'तुमचा ईमेल पत्ता प्रविष्ट करा'
      },
      phone: {
        label: 'फोन नंबर *',
        placeholder: 'तुमचा फोन नंबर प्रविष्ट करा'
      },
      selectDoctor: {
        label: 'तुमचे डॉक्टर निवडा *',
        placeholder: '-- डॉक्टर निवडा --',
        loading: 'डॉक्टर लोड होत आहेत...',
        noDoctors: 'कोणतेही डॉक्टर उपलब्ध नाहीत. कृपया सहाय्याशी संपर्क करा.'
      },
      dateOfBirth: {
        label: 'जन्मतारीख *'
      },
      contactNumber: {
        label: 'संपर्क क्रमांक *',
        placeholder: 'तुमचा संपर्क क्रमांक प्रविष्ट करा'
      },
      whatsappNumber: {
        label: 'व्हॉट्सअॅप क्रमांक (ऐच्छिक) 💬',
        placeholder: 'व्हॉट्सअॅप क्रमांक प्रविष्ट करा (उदा. +1234567890)',
        helper: 'तुम्ही दिल्यास औषध सूचना व्हॉट्सअॅपवर पाठवल्या जातील'
      },
      password: {
        label: 'पासवर्ड *',
        placeholder: 'तुमचा पासवर्ड प्रविष्ट करा'
      },
      confirmPassword: {
        label: 'पासवर्डची पुष्टी करा *',
        placeholder: 'तुमचा पासवर्ड पुन्हा प्रविष्ट करा'
      },
      passkeyTitle: 'पासकी प्रविष्ट करा *',
      passkeyHelper: 'रुग्ण/डॉक्टर नोंदणीसाठी नोंदणी पासकी किंवा प्रशासकीय प्रवेशासाठी अॅडमिन पासकी वापरा'
    }
  },
  toast: {
    adminWelcome: 'स्वागत आहे, {{name}}!',
    doctorRegistrationSuccess: 'नोंदणी यशस्वी! तुमचा डॉक्टर आयडी: {{id}}. पुढे जाण्यासाठी कृपया लॉगिन करा.'
  },
  dashboard: {
    loading: 'डॅशबोर्ड लोड होत आहे...',
    welcome: {
      title: 'पेन ट्रॅकरमध्ये स्वागत आहे',
      subtitle: 'तुमच्या वेदना पातळी आणि आरोग्य प्रगतीवर लक्ष ठेवा'
    },
    stats: {
      logsToday: 'आजचे लॉग',
      noPain: 'वेदना नाही',
      mild: 'सौम्य',
      moderatePain: 'मध्यम वेदना',
      severePain: 'तीव्र वेदना',
      worstPain: 'सर्वात वाईट वेदना',
      lessPain: 'कमी वेदना',
      unknown: 'अज्ञात'
    },
    actions: {
      retry: 'पुन्हा प्रयत्न करा',
      bodyDiagram: 'शरीराचा नकाशा',
      viewLogs: 'लॉग पहा',
      viewStatistics: 'आकडेवारी पहा',
      history: 'इतिहास'
    },
    errors: {
      title: 'डॅशबोर्ड लोड करताना त्रुटी',
      loadFailed: 'वेदना माहिती लोड करण्यात अयशस्वी'
    },
    logs: {
      title: 'अलीकडील वेदना लॉग',
      emptyTitle: 'आज कोणतेही वेदना लॉग नाहीत',
      emptyText: 'शरीराच्या भागांवर क्लिक करून वेदना ट्रॅक करायला सुरुवात करा'
    },
    sidebar: {
      title: 'पेन ट्रॅकर',
      links: {
        dashboard: 'पेन ट्रॅकर',
        bodyDiagram: 'शरीराचा नकाशा',
        painLogs: 'वेदना लॉग',
        history: 'इतिहास',
        statistics: 'आकडेवारी',
        distress: 'तणाव',
        medication: 'औषध'
      },
      user: {
        profile: 'रुग्ण प्रोफाइल'
      },
      actions: {
        logout: 'लॉगआउट'
      }
    }
  },
  painScale: {
    title: 'वेदना पातळी नोंदवा',
    vasTitle: 'व्हिज्युअल अॅनालॉग स्केल (VAS)',
    vasDescription: 'मानक 0–10 स्केल: 0 = वेदना नाही, 10 = सर्वात वाईट वेदना',
    vasCurrentValue: 'तुमची वेदना पातळी',
    vasWorstPain: 'सर्वात वाईट वेदना',
    vasDescriptors: {
      noPain: 'वेदना नाही',
      mild: 'सौम्य',
      moderate: 'मध्यम',
      severe: 'तीव्र',
      verySevere: 'अतिशय तीव्र',
      worstPain: 'सर्वात वाईट वेदना'
    },
    fixedScales: 'निश्चित वेदना मोजमाप: वेदना नाही (0) • कमी वेदना (1-3) • मध्यम (4-7) • तीव्र (8-10)',
    frontView: '👤 समोरचे दृश्य',
    backView: '👥 मागचे दृश्य',
    levels: {
      noPain: 'वेदना नाही',
      lessPain: 'कमी वेदना',
      moderatePain: 'मध्यम वेदना',
      severePain: 'तीव्र वेदना'
    },
    descriptions: {
      noPain: 'कोणतीही अस्वस्थता नाही',
      lessPain: 'सौम्य अस्वस्थता',
      moderatePain: 'लक्षात येण्याजोगे परंतु व्यवस्थापन करण्यायोग्य',
      severePain: 'तीव्र आणि काळजीचे'
    },
    additionalInfo: 'अतिरिक्त माहिती (ऐच्छिक)',
    painScale: 'वेदना मोजमाप',
    timeOfDay: 'दिवसाचा वेळ',
    mood: 'मनःस्थिती',
    activity: 'क्रियाकलाप पातळी',
    symptoms: 'लक्षणे (स्वल्पविरामाने विभक्त)',
    triggers: 'ट्रिगर्स (स्वल्पविरामाने विभक्त)',
    notes: 'नोट्स (मजकूर)',
    audioNote: 'किंवा तुमचे वर्णन रेकॉर्ड करा (ऑडिओ)',
    placeholders: {
      symptoms: 'उदा., कडकपणा, सूज, सुन्नता',
      triggers: 'उदा., तणाव, हवामान, व्यायाम',
      notes: 'तुमचे वेदना वर्णन येथे टाइप करा...',
      selectMood: 'मनःस्थिती निवडा (ऐच्छिक)',
      selectActivity: 'क्रियाकलाप निवडा (ऐच्छिक)',
      symptomsMultiSelect: 'अनेक लक्षणे निवडण्यासाठी Ctrl/Cmd दाबून ठेवा'
    },
    timeOptions: {
      morning: 'सकाळ',
      afternoon: 'दुपार',
      evening: 'संध्याकाळ',
      night: 'रात्र'
    },
    moodOptions: {
      good: 'चांगले',
      moderate: 'मध्यम',
      bad: 'खराब'
    },
    activityOptions: {
      able: 'सक्षम',
      partiallyDependent: 'आंशिक अवलंबित',
      notAble: 'असक्षम'
    },
    symptomOptions: {
      none: 'काही नाही',
      vomiting: 'उलटी',
      nausea: 'मळमळ',
      dizziness: 'चक्कर येणे',
      headache: 'डोकेदुखी',
      fever: 'ताप',
      fatigue: 'थकवा',
      shortnessOfBreath: 'श्वास अडचण',
      cough: 'खोकला',
      swelling: 'सूज',
      numbness: 'बधिरता',
      weakness: 'कमकुवतपणा'
    },
    buttons: {
      cancel: 'रद्द करा',
      clear: 'साफ करा',
      record: 'वेदना नोंदवा'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'दिवसाचा वेळ निवडा',
    success: {
      recorded: 'वेदना पातळी यशस्वीरित्या नोंदवली',
      withAudio: 'ऑडिओ नोटसह वेदना पातळी यशस्वीरित्या नोंदवली'
    },
    errors: {
      failed: 'वेदना पातळी नोंदवण्यात अयशस्वी: {{message}}'
    },
    confirm: {
      clearAll: 'तुम्ही आजचा सर्व वेदना डेटा साफ करू इच्छिता?'
    }
  },
  profile: {
    title: 'माझे प्रोफाइल',
    subtitle: 'तुमची वैयक्तिक माहिती व्यवस्थापित करा',
    loading: 'प्रोफाइल लोड होत आहे...',
    editWhatsApp: '✏️ व्हॉट्सअॅप क्रमांक संपादित करा',
    whatsAppLabel: 'व्हॉट्सअॅप क्रमांक',
    whatsAppPlaceholder: 'व्हॉट्सअॅप क्रमांक प्रविष्ट करा (उदा. +1234567890)',
    whatsAppHelper: 'तुम्ही दिल्यास औषध सूचना व्हॉट्सअॅपवर पाठवल्या जातील. काढण्यासाठी रिक्त सोडा.',
    save: '💾 बदल जतन करा',
    saving: 'जतन होत आहे...',
    cancel: 'रद्द करा',
    success: 'व्हॉट्सअॅप क्रमांक यशस्वीरित्या अद्यतनित केला!',
    optional: 'ऐच्छिक',
    forNotifications: 'औषध सूचनांसाठी',
    notProvided: 'प्रदान केले नाही',
    patientId: 'रुग्ण आयडी',
    age: 'वय',
    sections: {
      personalInfo: 'वैयक्तिक माहिती',
      contactInfo: 'संपर्क माहिती'
    },
    errors: {
      authRequired: 'प्रमाणीकरण आवश्यक',
      updateFailed: 'व्हॉट्सअॅप क्रमांक अद्यतनित करण्यात अयशस्वी'
    }
  },
  painLogs: {
    title: 'वेदना लॉग',
    loading: 'वेदना लॉग लोड होत आहेत...',
    error: 'वेदना लॉग लोड करताना त्रुटी',
    filters: {
      painLevel: 'वेदना पातळी',
      allLevels: 'सर्व पातळी',
      noPain: 'वेदना नाही',
      mild: 'सौम्य',
      lessPain: 'कमी वेदना',
      moderatePain: 'मध्यम वेदना',
      severePain: 'तीव्र वेदना',
      worstPain: 'सर्वात वाईट वेदना'
    },
    table: {
      painLevel: 'वेदना पातळी',
      zone: 'शरीराचा भाग',
      date: 'तारीख',
      time: 'वेळ',
      actions: 'क्रिया'
    },
    actions: {
      delete: 'हटवा',
      previous: 'मागील',
      next: 'पुढील'
    },
    empty: 'वेदना लॉग सापडले नाहीत',
    confirmDelete: 'तुम्ही हा वेदना लॉग हटवू इच्छिता?',
    success: {
      deleted: 'वेदना लॉग यशस्वीरित्या हटवला'
    },
    errors: {
      deleteFailed: 'वेदना लॉग हटवण्यात अयशस्वी'
    }
  },
  statistics: {
    title: 'आकडेवारी',
    vasScaleCaption: 'वेदना पातळी: VAS 0–10 — वेदना नाही · सौम्य (1–3) · मध्यम (4–6) · तीव्र (7–9) · सर्वात वाईट (10)',
    loading: 'आकडेवारी लोड होत आहे...',
    error: 'आकडेवारी लोड करताना त्रुटी',
    loadFailed: 'आकडेवारी लोड करण्यात अयशस्वी',
    painLevelDistribution: 'वेदना पातळी वितरण',
    painTrends: 'कालांतराने वेदना कल',
    painByZone: 'शरीराच्या क्षेत्रानुसार वेदना',
    timePeriod: 'कालावधी',
    filterByRegion: 'क्षेत्रानुसार फिल्टर करा',
    lastDays: 'मागील {{count}} दिवस',
    totalLogs: 'एकूण लॉग',
    empty: 'डेटा उपलब्ध नाही',
    emptyText: 'आकडेवारी आणि कल पाहण्यासाठी वेदना ट्रॅक करणे सुरू करा',
    labels: {
      severePain: 'तीव्र वेदना',
      moderatePain: 'मध्यम वेदना',
      mild: 'सौम्य',
      lessPain: 'कमी वेदना',
      noPain: 'वेदना नाही',
      worstPain: 'सर्वात वाईट वेदना',
      intensityOverTime: 'वेळेनुसार तीव्रतेची वेदना',
      painIntensity: 'वेदना तीव्रता',
      painLevel: 'वेदना पातळी',
      time: 'वेळ',
      notes: 'नोट्स'
    },
    noDataForRegion: 'या क्षेत्रासाठी डेटा उपलब्ध नाही',
    totalEntries: 'एकूण नोंदी',
    emptyRegionLine: 'निवडलेल्या कालावधीसाठी "{{zone}}" साठी वेदना लॉग सापडले नाहीत. जास्त कालावधी निवडा किंवा दुसरे क्षेत्र निवडा.',
    noPainTrendsData: 'निवडलेल्या कालावधीसाठी वेदना कल डेटा उपलब्ध नाही.',
    emptyRegionBar: 'निवडलेल्या कालावधीत "{{zone}}" साठी वेदना डेटा सापडला नाही.',
    noPainByZoneData: 'शरीराच्या क्षेत्रानुसार वेदना डेटा उपलब्ध नाही.',
    emptyRegionDoughnut: 'निवडलेल्या कालावधीत "{{zone}}" साठी वेदना पातळी वितरण डेटा सापडला नाही.',
    noPainLevelDistributionData: 'वेदना पातळी वितरण डेटा उपलब्ध नाही.',
    painTimelineFor: '{{zone}} साठी वेदना टाइमलाइन',
    allRegions: 'सर्व क्षेत्रे',
    dateRange: 'तारीख श्रेणी',
    heatmapTitle: 'क्षेत्रानुसार वेदना हीटमॅप',
    heatmap: {
      zone: 'क्षेत्र',
      intensity: 'तीव्रता',
      logs: 'लॉग',
      intensityScale: 'वेदना तीव्रता प्रमाण',
      intensityLabels: {
        severe: 'तीव्र',
        moderate: 'मध्यम',
        less: 'कमी',
        none: 'नाही'
      }
    }
  },
  medication: {
    title: 'औषध',
    subtitle: 'तुमच्या डॉक्टरांनी निर्धारित केलेल्या औषध योजनांची समीक्षा करा. फार्मसी-तयार प्रत डाउनलोड करा किंवा आवश्यकतेनुसार मागील प्रिस्क्रिप्शन पुन्हा पहा.',
    loading: 'प्रिस्क्रिप्शन लोड होत आहेत...',
    empty: {
      title: 'अद्याप प्रिस्क्रिप्शन नाहीत',
      message: 'तुमचे डॉक्टर येथे औषध योजना सामायिक करतील. सल्लामसलत नंतर पुन्हा तपासा.'
    },
    description: 'तुमच्या डॉक्टरांनी निर्धारित केलेल्या औषध योजनांची समीक्षा करा. फार्मसी-तयार प्रत डाउनलोड करा किंवा आवश्यकतेनुसार मागील प्रिस्क्रिप्शन पुन्हा पहा.',
    legacy: {
      title: 'जुने प्रिस्क्रिप्शन',
      subtitle: 'हे प्रिस्क्रिप्शन मागील फॉरमॅट वापरून पाठवले गेले.',
      badge: 'मजकूर-आधारित प्रिस्क्रिप्शन'
    },
    current: {
      title: '⭐ वर्तमान प्रिस्क्रिप्शन',
      badge: 'वर्तमान प्रिस्क्रिप्शन',
      lastUpdated: 'शेवटचे अपडेट',
      notice: 'हे सर्वात अलीकडील प्रिस्क्रिप्शन आहे. या सूचनांचे काळजीपूर्वक पालन करा आणि औषध अचूकपणे निर्धारित केल्याप्रमाणे देण्यासाठी तुमच्या फार्मसिस्टला डाउनलोड करण्यायोग्य प्रत प्रदान करा.'
    },
    history: {
      title: '📚 प्रिस्क्रिप्शन इतिहास',
      subtitle: 'तुमच्या डॉक्टरांनी सामायिक केलेल्या मागील प्रिस्क्रिप्शनची समीक्षा करा.',
      badge: 'प्रिस्क्रिप्शन इतिहास'
    },
    download: {
      formal: '📥 औपचारिक प्रिस्क्रिप्शन डाउनलोड करा',
      text: '📄 मजकूर प्रत डाउनलोड करा',
      success: 'प्रिस्क्रिप्शन डाउनलोड केले',
      error: 'डाउनलोड करण्यासाठी प्रिस्क्रिप्शन उपलब्ध नाही'
    },
    labels: {
      patient: 'रुग्ण',
      doctor: 'डॉक्टर',
      issuedOn: 'जारी केले',
      medication: 'औषध',
      doseQty: 'डोस / प्रमाण',
      timing: 'वेळ',
      food: 'अन्न',
      duration: 'कालावधी',
      notes: 'नोट्स',
      patientInstructions: 'रुग्ण सूचना',
      pharmacyNotes: 'फार्मसी नोट्स',
      asPrescribed: 'निर्धारित केल्याप्रमाणे',
      asDirected: 'निर्देशित केल्याप्रमाणे',
      id: 'आयडी:',
      reg: 'रजिस्ट्रेशन:',
      contact: 'संपर्क:',
      prescribingDoctor: 'प्रिस्क्रिप्शन देणारा डॉक्टर:',
      treatingPhysician: 'उपचार करणारा वैद्यकीय',
      yourDoctor: 'तुमचे डॉक्टर',
      schedule: {
        morning: 'सकाळ',
        afternoon: 'दुपार',
        evening: 'संध्याकाळ',
        night: 'रात्री'
      },
      foodInstruction: {
        before: 'अन्नापूर्वी',
        with: 'अन्नासह',
        after: 'अन्नानंतर',
        none: 'अन्नावर अवलंबून नाही'
      }
    },
    footer: {
      needAssistance: 'मदत हवी आहे?',
      shareNote: 'सत्यापन आणि पूर्ततेसाठी डाउनलोड केलेले PDF तुमच्या फार्मसिस्ट किंवा सल्लागार डॉक्टरसोबत सामायिक करा.'
    },
    success: {
      newPrescription: '💊 नवीन प्रिस्क्रिप्शन प्राप्त झाले',
      downloaded: 'प्रिस्क्रिप्शन डाउनलोड केले'
    },
    errors: {
      loadFailed: 'आत्ता प्रिस्क्रिप्शन लोड करू शकत नाही',
      noPrescription: 'डाउनलोड करण्यासाठी प्रिस्क्रिप्शन उपलब्ध नाही'
    },
    reminders: {
      title: 'औषध स्मरणपत्रे',
      next: 'पुढील स्मरणपत्र',
      at: 'वाजता',
      taken: 'घेतले! 30 मिनिटांत वेदना नोंदवा',
      iTookIt: 'मी ते घेतले',
      takeMedication: 'औषध घ्या: {{name}}',
      recordPainAfter: 'तुमच्या डॉक्टरसाठी घेतल्यानंतर 30 मिनिटांनी वेदना नोंदवा.',
      recordPainNow: 'तुमच्या डॉक्टरला परिणाम पाहण्यासाठी औषध घेतल्यानंतर 30 मिनिटांनी आता वेदना नोंदवा.',
      recordPainNotification: 'औषध घेतल्यानंतर 30 मिनिटांनी आता तुमची वेदना पातळी नोंदवा.',
      recordPain: 'वेदना नोंदवा',
      recordPainPrompt: 'तुमच्या औषधाचा परिणाम डॉक्टर ट्रॅक करू शकतील म्हणून आता वेदना नोंदवा.',
      recordPainNowButton: 'आता वेदना नोंदवा',
      upcoming: 'आगामी',
      noUpcoming: 'कोणतेही आगामी स्मरणपत्र नाही'
    }
  },
  messages: {
    title: 'संदेश',
    loading: 'संदेश लोड होत आहेत...',
    error: 'संदेश लोड करण्यात अयशस्वी',
    empty: 'अद्याप संदेश नाहीत'
  },
  history: {
    title: 'इतिहास',
    subtitle: 'तुमच्या सर्व वेदना ट्रॅकिंग क्रियाकलापांची संपूर्ण नोंद',
    loading: 'इतिहास लोड होत आहे...',
    error: {
      title: 'इतिहास लोड करताना त्रुटी',
      message: 'इतिहास डेटा लोड करण्यात अयशस्वी',
      retry: 'पुन्हा प्रयत्न करा'
    },
    empty: {
      title: 'कोणतेही रेकॉर्ड सापडले नाहीत',
      filtered: 'तुमचे फिल्टर किंवा शोध शब्द समायोजित करण्याचा प्रयत्न करा',
      noData: 'तुमचा इतिहास येथे पाहण्यासाठी तुमच्या वेदनेचा ट्रॅक करणे सुरू करा'
    },
    stats: {
      total: 'एकूण रेकॉर्ड',
      today: 'आज',
      thisWeek: 'या आठवड्यात',
      thisMonth: 'या महिन्यात'
    },
    filters: {
      painLevel: 'वेदना पातळी:',
      view: 'दृश्य:',
      dateRange: 'तारीख श्रेणी:',
      allLevels: 'सर्व पातळी',
      noPain: 'वेदना नाही',
      mild: 'सौम्य',
      lessPain: 'कमी वेदना',
      moderatePain: 'मध्यम वेदना',
      severePain: 'तीव्र वेदना',
      worstPain: 'सर्वात वाईट वेदना',
      bothViews: 'दोन्ही दृश्ये',
      frontView: 'समोरचे दृश्य',
      backView: 'मागचे दृश्य',
      allTime: 'सर्व वेळ',
      today: 'आज',
      thisWeek: 'या आठवड्यात',
      thisMonth: 'या महिन्यात'
    },
    search: {
      placeholder: 'रेकॉर्ड शोधा...'
    },
    actions: {
      refresh: 'रिफ्रेश',
      deleteLogs: '🗑️ लॉग हटवा',
      selectAll: 'सर्व निवडा',
      deselectAll: 'सर्व निवड रद्द करा',
      deleteSelected: 'निवडलेले हटवा',
      cancel: 'रद्द करा'
    },
    deleteMode: {
      title: '🗑️ हटवण्याचा मोड: हटवण्यासाठी लॉग निवडा',
      selected: 'निवडले',
      confirm: 'तुम्हाला निवडलेले {{count}} लॉग(स) हटवायचे आहेत का? या क्रियेची पूर्ववत करता येणार नाही.',
      success: '{{count}} लॉग(स) यशस्वीरित्या हटवले',
      error: 'निवडलेले लॉग हटवण्यात अयशस्वी',
      selectAtLeastOne: 'कृपया किमान एक लॉग निवडा'
    },
    header: {
      title: 'इतिहास रेकॉर्ड ({{count}})'
    },
    labels: {
      intensity: 'तीव्रता:',
      symptoms: 'लक्षणे:',
      triggers: 'ट्रिगर्स:',
      view: 'दृश्य',
      unknownTime: 'अज्ञात वेळ',
      invalidDate: 'अवैध तारीख',
      noDate: 'तारीख नाही',
      justNow: 'आत्ताच',
      minutesAgo: 'मिनिटांपूर्वी',
      hoursAgo: 'तासांपूर्वी'
    },
    painLevels: {
      'no-pain': 'वेदना नाही',
      'mild': 'सौम्य',
      'moderate': 'मध्यम वेदना',
      'severe': 'तीव्र वेदना',
      'worst-pain': 'सर्वात वाईट वेदना',
      'less-pain': 'कमी वेदना',
      'moderate-pain': 'मध्यम वेदना',
      'severe-pain': 'तीव्र वेदना'
    }
  },
  distress: {
    title: '🌡️ तणाव ट्रॅकर',
    subtitle: 'कालांतराने तुमच्या तणावाच्या पातळीची नोंद आणि निरीक्षण करा',
    loading: 'तणाव इतिहास लोड होत आहे...',
    error: {
      title: 'तणाव इतिहास लोड करण्यात त्रुटी',
      message: 'तणाव लॉग लोड करण्यात अयशस्वी',
      retry: 'पुन्हा प्रयत्न करा',
      login: 'तणाव लॉग पाहण्यासाठी कृपया लॉगिन करा'
    },
    empty: {
      title: 'तणाव लॉग सापडले नाहीत',
      textFilter: 'तुमच्या फिल्टर निकषांशी जुळणारे लॉग नाहीत',
      textNoFilter: 'वरील थर्मामीटर वापरून तुमची तणाव पातळी नोंदवा'
    },
    status: {
      offline: 'ऑफलाइन मोड',
      syncing: 'सिंक होत आहे…',
      online: 'ऑनलाइन',
      pending: 'प्रलंबित',
      pendingSync: 'प्रलंबित सिंक',
      pendingSyncEntries: 'प्रलंबित सिंक नोंदी',
      syncNow: 'आत्ता सिंक करा'
    },
    filters: {
      minScore: 'स्कोर:',
      startDate: 'प्रारंभ तारीख:',
      endDate: 'अंत तारीख:',
      allScores: 'सर्व स्कोर',
      score0: '0+',
      score4: '4+ (क्लिनिकली महत्त्वाचे)',
      score7: '7+ (उच्च)',
      clinicallySignificant: '4+ (क्लिनिकली महत्त्वाचे)',
      high: '7+ (उच्च)',
      clear: 'फिल्टर साफ करा'
    },
    stats: {
      totalEntries: 'एकूण नोंदी',
      averageScore: 'सरासरी स्कोर',
      highestScore: 'सर्वोच्च स्कोर',
      highDistress: 'उच्च तणाव (≥4)'
    },
    labels: {
      distressScore: 'तणाव स्कोर:',
      sourcesOfDistress: 'तणावाचे स्रोत:',
      notes: 'नोट्स:',
      extreme: 'अत्यंत तणाव',
      moderateToSevere: 'मध्यम ते तीव्र तणाव',
      mild: 'हलका तणाव',
      none: 'तणाव नाही',
      alert: '🚨 क्लिनिकली महत्त्वाचा तणाव - तुमच्या डॉक्टरांना सूचित केले आहे'
    },
    header: {
      title: 'तणाव लॉग ({{count}})',
      refresh: 'रिफ्रेश करा'
    }
  },
  distressThermometer: {
    title: '🌡️ तणाव थर्मामीटर',
    subtitle: 'मागील आठवड्यात तुमच्या सध्याच्या तणाव पातळीचे रेटिंग करा (0 = तणाव नाही, 10 = अत्यंत तणाव)',
    guidelines: 'NCCN तणाव थर्मामीटर मार्गदर्शक तत्त्वांवर आधारित',
    problemList: {
      label: 'समस्या यादी - तुमच्या तणावाचे कारण काय आहे? (पर्यायी)',
      helper: 'लागू असलेले सर्व निवडा (NCCN समस्या यादी)'
    },
    notes: {
      label: 'अतिरिक्त नोट्स (पर्यायी)',
      placeholder: 'तुमच्या तणावाबद्दल अतिरिक्त तपशील वर्णन करा...',
      enterNumberHint: 'किंवा नंबर प्रविष्ट करा (0-10):'
    },
    buttons: {
      record: 'तणाव पातळी नोंदवा',
      recording: 'नोंदवत आहे...',
      clear: 'साफ करा'
    },
    scoreLabels: {
      noDistress: 'तणाव नाही',
      mildDistress: 'हलका तणाव',
      moderateDistress: 'मध्यम तणाव',
      severeDistress: 'तीव्र तणाव',
      extremeDistress: 'अत्यंत तणाव'
    },
    alert: '⚠️ NCCN चेतावणी: {{score}} स्कोर क्लिनिकली महत्त्वाचा तणाव दर्शवते (≥4). तुमच्या आरोग्य सेवा प्रदात्याला सूचित केले जाईल.',
    success: {
      recorded: 'तणाव पातळी {{score}} यशस्वीरित्या नोंदवली'
    },
    errors: {
      login: 'तणाव नोंदवण्यासाठी कृपया लॉगिन करा',
      recordFailed: 'तणाव नोंदवण्यात अयशस्वी'
    },
    sources: {
      transportation: 'वाहतूक',
      housing: 'गृहनिर्माण',
      insuranceFinancial: 'विमा/आर्थिक',
      workSchool: 'काम/शाळा',
      dealingWithPartner: 'जोडीदाराशी व्यवहार करणे',
      dealingWithChildren: 'मुलांशी व्यवहार करणे',
      familyHealthIssues: 'कुटुंब आरोग्य समस्या',
      depression: 'नैराश्य',
      fears: 'भीती',
      nervousness: 'चिंता',
      sadness: 'दु:ख',
      worry: 'काळजी',
      lossOfInterest: 'सामान्य क्रियाकलापांमध्ये रुचीची हानी',
      spiritualReligiousConcerns: 'आध्यात्मिक/धार्मिक चिंता',
      fatigue: 'थकवा',
      pain: 'वेदना',
      sleepProblems: 'झोपेच्या समस्या',
      eatingProblems: 'खाण्याच्या समस्या',
      bathingDressing: 'स्नान/कपडे',
      breathingProblems: 'श्वासोच्छ्वासाच्या समस्या',
      nausea: 'मळमळ',
      memoryConcentration: 'स्मृती/एकाग्रता',
      mouthSores: 'तोंडातील जखमा',
      skinProblems: 'त्वचेच्या समस्या',
      tinglingHandsFeet: 'हात/पायांमध्ये झुनझुणी',
      otherPhysicalProblems: 'इतर शारीरिक समस्या'
    }
  },
  bodyParts: {
    'right palm': 'उजवी हथेली',
    'left lower abdomen': 'डावा खालचा पोट',
    'right lower abdomen': 'उजवा खालचा पोट',
    'left upper abdomen': 'डावा वरचा पोट',
    'right upper abdomen': 'उजवा वरचा पोट',
    'left front forearm': 'डावा समोरचा मनगट',
    'left elbow': 'डावा कोपर',
    'right elbow': 'उजवा कोपर',
    'front head right': 'समोरचे डोके उजवे',
    'front head left': 'समोरचे डोके डावे',
    'frons right': 'कपाळ उजवे',
    'frons left': 'कपाळ डावे',
    'left shoulder': 'डावा खांदा',
    'cervicis left': 'मान डावी',
    'left lower leg': 'डावी खालची पाय',
    'right lower leg': 'उजवी खालची पाय',
    'left ankle': 'डावा घोटा',
    'right ankle': 'उजवा घोटा',
    'right foot': 'उजवा पाय',
    'left foot': 'डावा पाय',
    'left upper arm': 'डावा वरचा हात',
    'left lower arm': 'डावा खालचा हात',
    'facies left': 'चेहरा डावा',
    'cervicis right': 'मान उजवी',
    'facies right': 'चेहरा उजवा',
    'right front forearm': 'उजवा समोरचा मनगट',
    'right wrist': 'उजवा मनगट',
    'left wrist': 'डावा मनगट',
    'left palm': 'डावी हथेली',
    'right upper chest': 'उजवा वरचा छाती',
    'left upper chest': 'डावा वरचा छाती',
    'right lower chest': 'उजवा खालचा छाती',
    'left lower chest': 'डावा खालचा छाती',
    'right shoulder': 'उजवा खांदा',
    'right upper arm': 'उजवा वरचा हात',
    'right diaphragm': 'उजवा डायाफ्राम',
    'right lower arm': 'उजवा खालचा हात',
    'left diaphragm': 'डावा डायाफ्राम',
    'left upper leg': 'डावी वरची पाय',
    'left knee': 'डावे गुडघे',
    'left lower thigh': 'डावी खालची मांडी',
    'left upper thigh': 'डावी वरची मांडी',
    'right upper leg': 'उजवी वरची पाय',
    'right knee': 'उजवे गुडघे',
    'right lower thigh': 'उजवी खालची मांडी',
    'right upper thigh': 'उजवी वरची मांडी',
    'right pelvic region': 'उजवा पेल्विक प्रदेश',
    'left pelvic region': 'डावा पेल्विक प्रदेश',
    'back head left': 'मागचे डोके डावे',
    'back head right': 'मागचे डोके उजवे',
    'left upper back': 'डावा वरचा माग',
    'left lumbar': 'डावा कंबर',
    'left buttock': 'डावा नितंब',
    'left back of knee': 'डाव्या गुडघ्याचा माग',
    'right back of knee': 'उजव्या गुडघ्याचा माग',
    'left upper calf': 'डावी वरची पिंडी',
    'right upper calf': 'उजवी वरची पिंडी',
    'right lower calf': 'उजवी खालची पिंडी',
    'left lower calf': 'डावी खालची पिंडी',
    'left ankle & foot': 'डावा घोटा आणि पाय',
    'right upper forearm': 'उजवा वरचा मनगट',
    'left lower arm': 'डावा खालचा हात',
    'left upper forearm': 'डावा वरचा मनगट',
    'right ankle & foot': 'उजवा घोटा आणि पाय',
    'right buttock': 'उजवा नितंब',
    'lower head left': 'खालचे डोके डावे',
    'lower head right': 'खालचे डोके उजवे',
    'neck left': 'मान डावी',
    'right upper back': 'उजवा वरचा माग',
    'left middle back': 'डावा मध्य माग',
    'right middle back': 'उजवा मध्य माग',
    'left lower forearm': 'डावा खालचा मनगट',
    'right lumbar': 'उजवा कंबर',
    'left sacral': 'डावा सॅक्रल',
    'right sacral': 'उजवा सॅक्रल',
    'right lower forearm': 'उजवा खालचा मनगट',
    'neck right': 'मान उजवी',
    left: 'डावा',
    right: 'उजवा',
    upper: 'वरचा',
    lower: 'खालचा',
    front: 'समोर',
    back: 'माग',
    middle: 'मध्य'
  },
  bodyDiagram: {
    selectTimeOfDay: 'दिवसाचा वेळ निवडा',
    title: 'शरीराचा नकाशा',
    subtitle: 'वेदना पातळी नोंदवण्यासाठी कोणत्याही शरीर क्षेत्रावर क्लिक करा',
    loading: 'शरीराचा नकाशा लोड होत आहे...',
    todaysRecords: 'आजचे वेदना रेकॉर्ड',
    unknownZone: 'अज्ञात क्षेत्र',
    instructions: {
      title: 'कसे वापरावे',
      step1: 'वेदना नोंदवण्यासाठी कोणत्याही शरीर क्षेत्रावर क्लिक करा',
      step2: 'तुमची वेदना पातळी निवडा (वेदना नाही, कमी वेदना, मध्यम वेदना, किंवा तीव्र वेदना)',
      step3: 'लक्षणे, ट्रिगर्स आणि नोट्स सारख्या वैकल्पिक तपशील जोडा',
      step4: 'खाली दिलेल्या रेकॉर्डमध्ये तुमचा वेदना डेटा पहा'
    },
    view: {
      front: 'समोरचे दृश्य',
      back: 'मागचे दृश्य',
      switch: 'दृश्य बदला'
    },
    actions: {
      clearAll: 'सर्व साफ करा',
      sync: 'आत्ता सिंक करा',
      syncing: 'सिंक होत आहे...'
    },
    status: {
      online: 'ऑनलाइन',
      offline: 'ऑफलाइन',
      syncing: 'सिंक होत आहे'
    },
    empty: {
      title: 'आज वेदना रेकॉर्ड नाहीत',
      text: 'तुमची वेदना ट्रॅक करणे सुरू करण्यासाठी वरच्या शरीर क्षेत्रांवर क्लिक करा'
    },
    pending: 'सिंक लंबित',
    time: {
      unknown: 'अज्ञात वेळ',
      invalidDate: 'अवैध तारीख',
      invalid: 'अवैध वेळ',
      justNow: 'आत्ता',
      minutesAgo: 'मिनिटांपूर्वी',
      hoursAgo: 'तासांपूर्वी'
    },
    success: {
      recorded: 'वेदना पातळी यशस्वीरित्या नोंदवली!',
      withAudio: 'ऑडियो नोटसह वेदना पातळी यशस्वीरित्या नोंदवली!',
      cleared: 'सर्व वेदना डेटा यशस्वीरित्या साफ केला!'
    },
    confirm: {
      clearAll: 'आजचे सर्व वेदना डेटा साफ करायचे आहेत का?'
    },
    errors: {
      title: 'शरीराचा नकाशा लोड करताना त्रुटी',
      loadFailed: 'वेदना डेटा लोड करण्यात अयशस्वी',
      clearFailed: 'वेदना डेटा साफ करण्यात अयशस्वी',
      failed: 'वेदना पातळी नोंदवण्यात अयशस्वी: {{message}}'
    }
  },
  audioRecorder: {
    title: 'आवाज रेकॉर्डिंग',
    help: {
      start: 'तुमचे वेदना वर्णन रेकॉर्ड करण्यासाठी "रेकॉर्डिंग सुरू करा" क्लिक करा.',
      recording: 'रेकॉर्डिंग प्रगतीत आहे... पूर्ण झाल्यावर "रेकॉर्डिंग थांबवा" क्लिक करा.',
      recorded: 'ऑडियो यशस्वीरित्या रेकॉर्ड केले! तुम्ही प्ले करू शकता, हटवू शकता किंवा नवीन रेकॉर्ड करू शकता.'
    },
    buttons: {
      start: 'रेकॉर्डिंग सुरू करा',
      stop: 'रेकॉर्डिंग थांबवा',
      play: 'रेकॉर्डिंग प्ले करा',
      pause: 'विराम',
      delete: 'हटवा',
      rerecord: 'पुन्हा रेकॉर्ड करा'
    },
    info: {
      duration: 'कालावधी',
      format: 'स्वरूप'
    },
    errors: {
      microphoneAccess: 'मायक्रोफोन प्रवेश करू शकत नाही. कृपया तुमच्या ब्राउझर परवानग्या तपासा.'
    }
  },
  audioPlayer: {
    label: 'आवाज नोट',
    loading: 'ऑडियो लोड होत आहे...',
    errors: {
      loadFailed: 'ऑडियो लोड करण्यात अयशस्वी: {{message}}',
      notLoaded: 'ऑडियो अद्याप लोड केलेले नाही',
      playFailed: 'ऑडियो प्ले करण्यात अयशस्वी',
      playFailedWithMessage: 'ऑडियो प्ले करण्यात अयशस्वी: {{message}}'
    }
  }
};

const teOverrides = {
  app: {
    loginPortal: 'లాగిన్ పోర్టల్'
  },
  common: {
    back: 'వెనుకకు',
    cancel: 'రద్దు చేయండి',
    confirm: 'నిర్ధారించండి',
    loading: 'దయచేసి వేచి ఉండండి...',
    language: 'భాష',
    requiredField: 'దయచేసి అవసరమైన అన్ని ఫీల్డులను పూరించండి'
  },
  languageGate: {
    title: 'మీ భాషను ఎంచుకోండి',
    subtitle: 'పోర్టల్ కొనసాగడానికి మీకు ఇష్టమైన భాషను ఎంచుకోండి.',
    helper: 'లాగిన్ స్క్రీన్ నుండి ఎప్పుడైనా భాషను మార్చవచ్చు.',
    helperEmphasis: 'ఆందోళన పడవద్దు!',
    secondary: 'మొదటి అడుగులోనే మీరు ఇంటిలో ఉన్నట్లుగా అనిపించడానికి ఇది సహాయపడుతుంది.'
  },
  buttons: {
    login: 'లాగిన్',
    register: 'నమోదు',
    verifyPasskey: 'పాస్‌కీని నిర్ధారించండి'
  },
  login: {
    initialChoice: {
      login: 'లాగిన్',
      register: 'నమోదు'
    },
    passkey: {
      prompt: 'పాస్‌కీను నమోదు చేయండి *',
      helper: 'రోగి/వైద్యుడు నమోదు కోసం నమోదు పాస్‌కీ లేదా అడ్మిన్ యాక్సెస్ కోసం అడ్మిన్ పాస్‌కీని ఉపయෝගించండి',
      invalid: 'చెల్లని పాస్‌కీ. దయచేసి మళ్లీ ప్రయత్నించండి.'
    },
    form: {
      idLabel: 'ఐడి *',
      idPlaceholder: 'Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) లేదా Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001)',
      passwordLabel: 'పాస్‌వర్డ్ *',
      passwordPlaceholder: 'మీ పాస్‌వర్డ్‌ను నమోదు చేయండి',
      rememberMe: 'నన్ను 90 రోజులకు గుర్తుంచుకోండి'
    },
    errors: {
      missingId: 'దయచేసి మీ Patient ID లేదా Doctor ID‌ను నమోదు చేయండి',
      missingPassword: 'దయచేసి మీ పాస్‌వర్డ్‌ను నమోదు చేయండి',
      invalidIdFormat: 'చెల్లని ఐడి ఫార్మాట్. దయచేసి Patient ID (DEMOP00001 / HSP-MNG-7F29A1-00001) లేదా Doctor ID (DEMOD0001 / DOC-HSP-MNG-7F29A1-00001) ఉపయෝගించండి',
      adminLoginDisabled: 'అడ్మిన్ లాగిన్ అందుబాటులో లేదు. దయచేసి నమోదు ఎంపిక ద్వారా అడ్మిన్ పాస్‌కీని ఉపయෝගించండి.',
      missingDoctorEmail: 'వైద్యుడు నమోదుకు ఇమెయిల్ అవసరం',
      missingDoctorPhone: 'వైద్యుడు నమోదుకు ఫోన్ నంబర్ అవసరం',
      passwordMismatch: 'పాస్‌వర్డ్లు సరిపోలడం లేదు',
      passwordLength: 'పాస్‌వర్డ్ కనీసం 6 అక్షరాలు ఉండాలి',
      missingPatientDoctor: 'డాక్టర్ ఎంపిక సహా అన్ని అవసరమైన ఫీల్డులను దయచేసి పూరించండి',
      genericLogin: 'లాగిన్ విఫలమైంది',
      loadDoctorsFailed: 'డాక్టర్ జాబితాను లోడ్ చేయలేకపోయాము. దయచేసి పేజీని రీఫ్రెష్ చేయండి.',
      noDoctors: 'డాక్టర్లు అందుబాటులో లేరు. దయచేసి సపోర్ట్‌ను సంప్రదించండి.'
    },
    success: {
      welcomeDoctor: 'మళ్లీ స్వాగతం, డా. {{name}}!',
      welcomePatient: 'మళ్లీ స్వాగతం!'
    },
    register: {
      title: 'నేను *',
      patient: 'రోగి',
      doctor: 'వైద్యుడు',
      firstName: {
        label: 'మొదటి పేరు *',
        placeholder: 'మీ మొదటి పేరును నమోదు చేయండి'
      },
      lastName: {
        label: 'చివరి పేరు (ఐచ్ఛికం)',
        placeholder: 'మీ చివరి పేరును నమోదు చేయండి (ఐచ్ఛికం)'
      },
      email: {
        label: 'ఇమెయిల్ *',
        placeholder: 'మీ ఇమెయిల్ చిరునామాను నమోదు చేయండి'
      },
      phone: {
        label: 'ఫోన్ నంబర్ *',
        placeholder: 'మీ ఫోన్ నంబర్‌ను నమోదు చేయండి'
      },
      selectDoctor: {
        label: 'మీ డాక్టర్‌ను ఎంచుకోండి *',
        placeholder: '-- డాక్టర్‌ను ఎంచుకోండి --',
        loading: 'డాక్టర్లు లోడ్ అవుతున్నారు...',
        noDoctors: 'డాక్టర్లు అందుబాటులో లేరు. దయచేసి సపోర్ట్‌ను సంప్రదించండి.'
      },
      dateOfBirth: {
        label: 'పుట్టిన తేదీ *'
      },
      contactNumber: {
        label: 'సంప్రదింపు నంబర్ *',
        placeholder: 'మీ సంప్రదింపు నంబర్‌ను నమోదు చేయండి'
      },
      whatsappNumber: {
        label: 'వాట్సాప్ నంబర్ (ఐచ్ఛికం) 💬',
        placeholder: 'వాట్సాప్ నంబర్‌ను నమోదు చేయండి (ఉదా. +1234567890)',
        helper: 'మీరు ఇస్తే మందుల సమాచారం వాట్సాప్ ద్వారా పంపబడుతుంది'
      },
      password: {
        label: 'పాస్‌వర్డ్ *',
        placeholder: 'మీ పాస్‌వర్డ్‌ను నమోదు చేయండి'
      },
      confirmPassword: {
        label: 'పాస్‌వర్డ్‌ను నిర్ధారించండి *',
        placeholder: 'మీ పాస్‌వర్డ్‌ను మళ్లీ నమోదు చేయండి'
      },
      passkeyTitle: 'పాస్‌కీను నమోదు చేయండి *',
      passkeyHelper: 'రోగి/వైద్యుడు నమోదు కోసం నమోదు పాస్‌కీ లేదా అడ్మిన్ యాక్సెస్ కోసం అడ్మిన్ పాస్‌కీని ఉపయෝගించండి'
    }
  },
  toast: {
    adminWelcome: 'స్వాగతం, {{name}}!',
    doctorRegistrationSuccess: 'నమోదు విజయవంతం! మీ డాక్టర్ ఐడి: {{id}}. కొనసాగడానికి లాగిన్ చేయండి.'
  },
  dashboard: {
    loading: 'డాష్‌బోర్డ్ లోడ్ అవుతోంది...',
    welcome: {
      title: 'పేన్ ట్రాకర్‌కి స్వాగతం',
      subtitle: 'మీ నొప్పి స్థాయిలు మరియు ఆరోగ్యం పురోగతిని ట్రాక్ చేయండి'
    },
    stats: {
      logsToday: 'ఈరోజు లాగ్‌లు',
      noPain: 'నొప్పి లేదు',
      mild: 'తేలికపాటి',
      moderatePain: 'మధ్యస్థ నొప్పి',
      severePain: 'తీవ్ర నొప్పి',
      worstPain: 'చెత్త నొప్పి',
      lessPain: 'తక్కువ నొప్పి',
      unknown: 'తెలియదు'
    },
    actions: {
      retry: 'మళ్లీ ప్రయత్నించండి',
      bodyDiagram: 'శరీర చిత్రము',
      viewLogs: 'లాగ్‌లు చూడండి',
      viewStatistics: 'గణాంకాలు చూడండి',
      history: 'చరిత్ర'
    },
    errors: {
      title: 'డాష్‌బోర్డ్ లోడ్ చేయడంలో లోపం',
      loadFailed: 'నొప్పి డేటాను లోడ్ చేయడం విఫలమైంది'
    },
    logs: {
      title: 'ఇటీవలి నొప్పి లాగ్‌లు',
      emptyTitle: 'ఈరోజు నొప్పి లాగ్‌లు లేవు',
      emptyText: 'శరీర భాగాలపై క్లిక్ చేసి నొప్పిని ట్రాక్ చేయడం ప్రారంభించండి'
    },
    sidebar: {
      title: 'పేన్ ట్రాకర్',
      links: {
        dashboard: 'డాష్‌బోర్డ్',
        bodyDiagram: 'శరీర చిత్రము',
        painLogs: 'నొప్పి లాగ్‌లు',
        history: 'చరిత్ర',
        statistics: 'గణాంకాలు',
        distress: 'మానసిక సమ్రద్దం',
        medication: 'ఔషధం'
      },
      user: {
        profile: 'రోగి ప్రొఫైల్'
      },
      actions: {
        logout: 'లాగౌట్'
      }
    }
  },
  bodyParts: {
    'right palm': 'కుడి అరచేతి',
    'left lower abdomen': 'ఎడమ దిగువ కడుపు',
    'right lower abdomen': 'కుడి దిగువ కడుపు',
    'left upper abdomen': 'ఎడమ ఎగువ కడుపు',
    'right upper abdomen': 'కుడి ఎగువ కడుపు',
    'left front forearm': 'ఎడమ ముందు ముంజేయి',
    'left elbow': 'ఎడమ మోచేయి',
    'right elbow': 'కుడి మోచేయి',
    'front head right': 'ముందు తల కుడి',
    'front head left': 'ముందు తల ఎడమ',
    'frons right': 'నుదురు కుడి',
    'frons left': 'నుదురు ఎడమ',
    'left shoulder': 'ఎడమ భుజం',
    'cervicis left': 'మెడ ఎడమ',
    'left lower leg': 'ఎడమ దిగువ కాలు',
    'right lower leg': 'కుడి దిగువ కాలు',
    'left ankle': 'ఎడమ చీలమండ',
    'right ankle': 'కుడి చీలమండ',
    'right foot': 'కుడి పాదం',
    'left foot': 'ఎడమ పాదం',
    'left upper arm': 'ఎడమ ఎగువ చేయి',
    'left lower arm': 'ఎడమ దిగువ చేయి',
    'facies left': 'ముఖం ఎడమ',
    'cervicis right': 'మెడ కుడి',
    'facies right': 'ముఖం కుడి',
    'right front forearm': 'కుడి ముందు ముంజేయి',
    'right wrist': 'కుడి మణికట్టు',
    'left wrist': 'ఎడమ మణికట్టు',
    'left palm': 'ఎడమ అరచేతి',
    'right upper chest': 'కుడి ఎగువ ఛాతీ',
    'left upper chest': 'ఎడమ ఎగువ ఛాతీ',
    'right lower chest': 'కుడి దిగువ ఛాతీ',
    'left lower chest': 'ఎడమ దిగువ ఛాతీ',
    'right shoulder': 'కుడి భుజం',
    'right upper arm': 'కుడి ఎగువ చేయి',
    'right diaphragm': 'కుడి శ్వాసకోశం',
    'right lower arm': 'కుడి దిగువ చేయి',
    'left diaphragm': 'ఎడమ శ్వాసకోశం',
    'left upper leg': 'ఎడమ ఎగువ కాలు',
    'left knee': 'ఎడమ మోకాలి',
    'left lower thigh': 'ఎడమ దిగువ తొడ',
    'left upper thigh': 'ఎడమ ఎగువ తొడ',
    'right upper leg': 'కుడి ఎగువ కాలు',
    'right knee': 'కుడి మోకాలి',
    'right lower thigh': 'కుడి దిగువ తొడ',
    'right upper thigh': 'కుడి ఎగువ తొడ',
    'right pelvic region': 'కుడి శ్రోణి ప్రాంతం',
    'left pelvic region': 'ఎడమ శ్రోణి ప్రాంతం',
    'back head left': 'వెనుక తల ఎడమ',
    'back head right': 'వెనుక తల కుడి',
    'left upper back': 'ఎడమ ఎగువ వెనుక',
    'left lumbar': 'ఎడమ నడుము',
    'left buttock': 'ఎడమ పిరుదు',
    'left back of knee': 'ఎడమ మోకాలి వెనుక',
    'right back of knee': 'కుడి మోకాలి వెనుక',
    'left upper calf': 'ఎడమ ఎగువ కాలు',
    'right upper calf': 'కుడి ఎగువ కాలు',
    'right lower calf': 'కుడి దిగువ కాలు',
    'left lower calf': 'ఎడమ దిగువ కాలు',
    'left ankle & foot': 'ఎడమ చీలమండ & పాదం',
    'right upper forearm': 'కుడి ఎగువ ముంజేయి',
    'left lower arm': 'ఎడమ దిగువ చేయి',
    'left upper forearm': 'ఎడమ ఎగువ ముంజేయి',
    'right ankle & foot': 'కుడి చీలమండ & పాదం',
    'right buttock': 'కుడి పిరుదు',
    'lower head left': 'దిగువ తల ఎడమ',
    'lower head right': 'దిగువ తల కుడి',
    'neck left': 'మెడ ఎడమ',
    'right upper back': 'కుడి ఎగువ వెనుక',
    'left middle back': 'ఎడమ మధ్య వెనుక',
    'right middle back': 'కుడి మధ్య వెనుక',
    'left lower forearm': 'ఎడమ దిగువ ముంజేయి',
    'right lumbar': 'కుడి నడుము',
    'left sacral': 'ఎడమ త్రికాస్థి',
    'right sacral': 'కుడి త్రికాస్థి',
    'right lower forearm': 'కుడి దిగువ ముంజేయి',
    'neck right': 'మెడ కుడి',
    left: 'ఎడమ',
    right: 'కుడి',
    upper: 'ఎగువ',
    lower: 'దిగువ',
    front: 'ముందు',
    back: 'వెనుక',
    middle: 'మధ్య'
  },
  messages: {
    title: 'సందేశాలు',
    loading: 'సందేశాలు లోడ్ అవుతోంది...',
    error: 'సందేశాలను లోడ్ చేయడంలో విఫలమైంది',
    empty: 'ఇంకా సందేశాలు లేవు'
  },
  history: {
    title: 'చరిత్ర',
    subtitle: 'మీ అన్ని నొప్పి ట్రాకింగ్ కార్యకలాపాల పూర్తి రికార్డ్',
    loading: 'చరిత్ర లోడ్ అవుతోంది...',
    error: {
      title: 'చరిత్రను లోడ్ చేయడంలో లోపం',
      message: 'చరిత్ర డేటాను లోడ్ చేయడంలో విఫలమైంది',
      retry: 'మళ్లీ ప్రయత్నించండి'
    },
    empty: {
      title: 'రికార్డులు కనుగొనబడలేదు',
      filtered: 'మీ ఫిల్టర్‌లు లేదా శోధన పదాలను సర్దుబాటు చేయడానికి ప్రయత్నించండి',
      noData: 'మీ చరిత్రను ఇక్కడ చూడడానికి మీ నొప్పిని ట్రాక్ చేయడం ప్రారంభించండి'
    },
    stats: {
      total: 'మొత్తం రికార్డులు',
      today: 'ఈరోజు',
      thisWeek: 'ఈ వారం',
      thisMonth: 'ఈ నెల'
    },
    filters: {
      painLevel: 'నొప్పి స్థాయి:',
      view: 'వీక్షణ:',
      dateRange: 'తేదీ పరిధి:',
      allLevels: 'అన్ని స్థాయిలు',
      noPain: 'నొప్పి లేదు',
      mild: 'తేలికపాటి',
      lessPain: 'తక్కువ నొప్పి',
      moderatePain: 'మధ్యస్థ నొప్పి',
      severePain: 'తీవ్రమైన నొప్పి',
      worstPain: 'చెత్త నొప్పి',
      bothViews: 'రెండు వీక్షణలు',
      frontView: 'ముందు వీక్షణ',
      backView: 'వెనుక వీక్షణ',
      allTime: 'అన్ని సమయం',
      today: 'ఈరోజు',
      thisWeek: 'ఈ వారం',
      thisMonth: 'ఈ నెల'
    },
    search: {
      placeholder: 'రికార్డులు శోధించండి...'
    },
    actions: {
      refresh: 'రిఫ్రెష్',
      deleteLogs: '🗑️ లాగ్‌లను తొలగించండి',
      selectAll: 'అన్నింటినీ ఎంచుకోండి',
      deselectAll: 'అన్నింటినీ ఎంపిక రద్దు చేయండి',
      deleteSelected: 'ఎంచుకున్నవి తొలగించండి',
      cancel: 'రద్దు చేయండి'
    },
    deleteMode: {
      title: '🗑️ తొలగించే మోడ్: తొలగించడానికి లాగ్‌లను ఎంచుకోండి',
      selected: 'ఎంచుకున్నారు',
      confirm: 'మీరు ఎంచుకున్న {{count}} లాగ్(లను) తొలగించాలని ఖచ్చితంగా ఉన్నారా? ఈ చర్యను రద్దు చేయలేము.',
      success: '{{count}} లాగ్(లు) విజయవంతంగా తొలగించబడ్డాయి',
      error: 'ఎంచుకున్న లాగ్‌లను తొలగించడంలో విఫలమైంది',
      selectAtLeastOne: 'దయచేసి కనీసం ఒక లాగ్‌ను ఎంచుకోండి'
    },
    header: {
      title: 'చరిత్ర రికార్డులు ({{count}})'
    },
    labels: {
      intensity: 'తీవ్రత:',
      symptoms: 'లక్షణాలు:',
      triggers: 'ట్రిగ్గర్లు:',
      view: 'వీక్షణ',
      unknownTime: 'తెలియని సమయం',
      invalidDate: 'చెల్లని తేదీ',
      noDate: 'తేదీ లేదు',
      justNow: 'ఇప్పుడే',
      minutesAgo: 'నిమిషాల క్రితం',
      hoursAgo: 'గంటల క్రితం'
    },
    painLevels: {
      'no-pain': 'నొప్పి లేదు',
      'mild': 'తేలికపాటి',
      'moderate': 'మధ్యస్థ నొప్పి',
      'severe': 'తీవ్రమైన నొప్పి',
      'worst-pain': 'చెత్త నొప్పి',
      'less-pain': 'తక్కువ నొప్పి',
      'moderate-pain': 'మధ్యస్థ నొప్పి',
      'severe-pain': 'తీవ్రమైన నొప్పి'
    }
  },
  statistics: {
    title: 'గణాంకాలు',
    vasScaleCaption: 'నొప్పి స్థాయిలు: VAS 0–10 — నొప్పి లేదు · తేలికపాటి (1–3) · మధ్యస్థ (4–6) · తీవ్రమైన (7–9) · చెత్త (10)',
    loading: 'గణాంకాలు లోడ్ అవుతోంది...',
    error: 'గణాంకాలను లోడ్ చేయడంలో లోపం',
    loadFailed: 'గణాంకాలను లోడ్ చేయడంలో విఫలమైంది',
    painLevelDistribution: 'నొప్పి స్థాయి పంపిణీ',
    painTrends: 'కాలక్రమేణా నొప్పి పోకడలు',
    painByZone: 'శరీర ప్రాంతం ప్రకారం నొప్పి',
    timePeriod: 'సమయ వ్యవధి',
    filterByRegion: 'ప్రాంతం ప్రకారం ఫిల్టర్ చేయండి',
    lastDays: 'గత {{count}} రోజులు',
    totalLogs: 'మొత్తం లాగ్‌లు',
    empty: 'డేటా అందుబాటులో లేదు',
    emptyText: 'గణాంకాలు మరియు పోకడలను చూడటానికి నొప్పిని ట్రాక్ చేయడం ప్రారంభించండి',
    labels: {
      severePain: 'తీవ్రమైన నొప్పి',
      moderatePain: 'మధ్యస్థ నొప్పి',
      mild: 'తేలికపాటి',
      lessPain: 'తక్కువ నొప్పి',
      noPain: 'నొప్పి లేదు',
      worstPain: 'చెత్త నొప్పి',
      intensityOverTime: 'కాలక్రమేణా తీవ్రత',
      painIntensity: 'నొప్పి తీవ్రత',
      painLevel: 'నొప్పి స్థాయి',
      time: 'సమయం',
      notes: 'నోట్స్'
    },
    noDataForRegion: 'ఈ ప్రాంతానికి డేటా లేదు',
    totalEntries: 'మొత్తం ఎంట్రీలు',
    emptyRegionLine: 'ఎంచుకున్న కాలంలో "{{zone}}" కోసం నొప్పి లాగ్‌లు కనుగొనబడలేదు. ఎక్కువ కాలాన్ని ఎంచుకోండి లేదా వేరే ప్రాంతాన్ని ఎంచుకోండి.',
    noPainTrendsData: 'ఎంచుకున్న కాలానికి నొప్పి పోకడల డేటా అందుబాటులో లేదు.',
    emptyRegionBar: 'ఎంచుకున్న కాలంలో "{{zone}}" కోసం నొప్పి డేటా కనుగొనబడలేదు.',
    noPainByZoneData: 'శరీర ప్రాంతం ప్రకారం నొప్పి డేటా అందుబాటులో లేదు.',
    emptyRegionDoughnut: 'ఎంచుకున్న కాలంలో "{{zone}}" కోసం నొప్పి స్థాయి పంపిణీ డేటా కనుగొనబడలేదు.',
    noPainLevelDistributionData: 'నొప్పి స్థాయి పంపిణీ డేటా అందుబాటులో లేదు.',
    painTimelineFor: '{{zone}} కోసం నొప్పి టైమ్‌లైన్',
    allRegions: 'అన్ని ప్రాంతాలు',
    dateRange: 'తేదీ పరిధి',
    heatmapTitle: 'ప్రాంతం ప్రకారం నొప్పి హీట్‌మ్యాప్',
    heatmap: {
      zone: 'ప్రాంతం',
      intensity: 'తీవ్రత',
      logs: 'లాగ్‌లు',
      intensityScale: 'నొప్పి తీవ్రత స్కేల్',
      intensityLabels: {
        severe: 'తీవ్ర',
        moderate: 'మధ్యస్థ',
        less: 'తక్కువ',
        none: 'లేదు'
      }
    }
  },
  medication: {
    title: 'ఔషధం',
    subtitle: 'మీ వైద్యుడు నిర్దేశించిన ఔషధ ప్రణాళికలను సమీక్షించండి. ఫార్మసీ-సిద్ధమైన కాపీని డౌన్‌లోడ్ చేయండి లేదా అవసరమైనప్పుడు మునుపటి ప్రిస్క్రిప్షన్‌లను మళ్లీ చూడండి.',
    loading: 'ప్రిస్క్రిప్షన్‌లు లోడ్ అవుతోంది...',
    empty: {
      title: 'ఇంకా ప్రిస్క్రిప్షన్‌లు లేవు',
      message: 'మీ వైద్యుడు ఇక్కడ ఔషధ ప్రణాళికలను భాగస్వామ్యం చేస్తారు. సంప్రదింపు తర్వాత మళ్లీ తనిఖీ చేయండి.'
    },
    description: 'మీ వైద్యుడు నిర్దేశించిన ఔషధ ప్రణాళికలను సమీక్షించండి. ఫార్మసీ-సిద్ధమైన కాపీని డౌన్‌లోడ్ చేయండి లేదా అవసరమైనప్పుడు మునుపటి ప్రిస్క్రిప్షన్‌లను మళ్లీ చూడండి.',
    legacy: {
      title: 'పాత ప్రిస్క్రిప్షన్',
      subtitle: 'ఈ ప్రిస్క్రిప్షన్ మునుపటి ఫార్మాట్ ఉపయోగించి పంపబడింది.',
      badge: 'టెక్స్ట్-ఆధారిత ప్రిస్క్రిప్షన్'
    },
    current: {
      title: '⭐ ప్రస్తుత ప్రిస్క్రిప్షన్',
      badge: 'ప్రస్తుత ప్రిస్క్రిప్షన్',
      lastUpdated: 'చివరి నవీకరణ',
      notice: 'ఇది అత్యంత ఇటీవలి ప్రిస్క్రిప్షన్. ఈ సూచనలను జాగ్రత్తగా అనుసరించండి మరియు ఔషధాన్ని ఖచ్చితంగా నిర్దేశించినట్లుగా అందించడానికి మీ ఫార్మసిస్ట్‌కు డౌన్‌లోడ్ చేయగల కాపీని అందించండి.'
    },
    history: {
      title: '📚 ప్రిస్క్రిప్షన్ చరిత్ర',
      subtitle: 'మీ వైద్యుడు భాగస్వామ్యం చేసిన మునుపటి ప్రిస్క్రిప్షన్‌లను సమీక్షించండి.',
      badge: 'ప్రిస్క్రిప్షన్ చరిత్ర'
    },
    download: {
      formal: '📥 అధికారిక ప్రిస్క్రిప్షన్‌ను డౌన్‌లోడ్ చేయండి',
      text: '📄 టెక్స్ట్ కాపీని డౌన్‌లోడ్ చేయండి',
      success: 'ప్రిస్క్రిప్షన్ డౌన్‌లోడ్ చేయబడింది',
      error: 'డౌన్‌లోడ్ చేయడానికి ప్రిస్క్రిప్షన్ అందుబాటులో లేదు'
    },
    labels: {
      patient: 'రోగి',
      doctor: 'వైద్యుడు',
      issuedOn: 'ఇవ్వబడింది',
      medication: 'ఔషధం',
      doseQty: 'డోస్ / పరిమాణం',
      timing: 'సమయం',
      food: 'ఆహారం',
      duration: 'కాలవ్యవధి',
      notes: 'నోట్స్',
      patientInstructions: 'రోగి సూచనలు',
      pharmacyNotes: 'ఫార్మసీ నోట్స్',
      asPrescribed: 'నిర్దేశించినట్లు',
      asDirected: 'నిర్దేశించినట్లు',
      id: 'ఐడి:',
      reg: 'రెజ్:',
      contact: 'సంప్రదింపు:',
      prescribingDoctor: 'ప్రిస్క్రైబ్ చేసే వైద్యుడు:',
      treatingPhysician: 'చికిత్స చేసే వైద్యుడు',
      yourDoctor: 'మీ వైద్యుడు',
      schedule: {
        morning: 'ఉదయం',
        afternoon: 'మధ్యాహ్నం',
        evening: 'సాయంత్రం',
        night: 'రాత్రి'
      },
      foodInstruction: {
        before: 'ఆహారానికి ముందు',
        with: 'ఆహారంతో',
        after: 'ఆహారానికి తర్వాత',
        none: 'ఆహారంపై ఆధారపడదు'
      }
    },
    footer: {
      needAssistance: 'సహాయం కావాలా?',
      shareNote: 'ధృవీకరణ మరియు నెరవేర్పు కోసం డౌన్‌లోడ్ చేసిన PDF ను మీ ఫార్మసిస్ట్ లేదా సలహాదారు వైద్యుడితో భాగస్వామ్యం చేయండి.'
    },
    success: {
      newPrescription: '💊 కొత్త ప్రిస్క్రిప్షన్ స్వీకరించబడింది',
      downloaded: 'ప్రిస్క్రిప్షన్ డౌన్‌లోడ్ చేయబడింది'
    },
    errors: {
      loadFailed: 'ఇప్పుడు ప్రిస్క్రిప్షన్‌లను లోడ్ చేయలేము',
      noPrescription: 'డౌన్‌లోడ్ చేయడానికి ప్రిస్క్రిప్షన్ అందుబాటులో లేదు'
    },
    reminders: {
      title: 'మందుల రిమైండర్‌లు',
      next: 'తదుపరి రిమైండర్',
      at: 'వద్ద',
      taken: 'తీసుకున్నాను! 30 నిమిషాల్లో నొప్పిని రికార్డ్ చేయండి',
      iTookIt: 'నేను తీసుకున్నాను',
      takeMedication: 'మందు తీసుకోండి: {{name}}',
      recordPainAfter: 'మీ వైద్యుడి కోసం తీసుకున్న 30 నిమిషాల తర్వాత నొప్పిని రికార్డ్ చేయండి.',
      recordPainNow: 'మీ వైద్యుడు ప్రభావాన్ని చూడడానికి మందు తీసుకున్న 30 నిమిషాల తర్వాత ఇప్పుడు నొప్పిని రికార్డ్ చేయండి.',
      recordPainNotification: 'మందు తీసుకున్న 30 నిమిషాల తర్వాత ఇప్పుడు మీ నొప్పి స్థాయిని రికార్డ్ చేయండి.',
      recordPain: 'నొప్పిని రికార్డ్ చేయండి',
      recordPainPrompt: 'మీ మందు ప్రభావాన్ని వైద్యుడు ట్రాక్ చేయడానికి ఇప్పుడు నొప్పిని రికార్డ్ చేయండి.',
      recordPainNowButton: 'ఇప్పుడు నొప్పిని రికార్డ్ చేయండి',
      upcoming: 'రాబోయేవి',
      noUpcoming: 'ఎటువంటి రిమైండర్లు లేవు'
    }
  },
  bodyDiagram: {
    selectTimeOfDay: 'రోజు సమయాన్ని ఎంచుకోండి',
    title: 'శరీర చిత్రము',
    subtitle: 'నొప్పి స్థాయిని నమోదు చేయడానికి ఏదైనా శరీర ప్రాంతంపై క్లిక్ చేయండి',
    loading: 'శరీర చిత్రము లోడ్ అవుతోంది...',
    todaysRecords: 'నేటి నొప్పి రికార్డులు',
    unknownZone: 'తెలియని ప్రాంతం',
    instructions: {
      title: 'ఎలా ఉపయోగించాలి',
      step1: 'నొప్పిని నమోదు చేయడానికి ఏదైనా శరీర ప్రాంతంపై క్లిక్ చేయండి',
      step2: 'మీ నొప్పి స్థాయిని ఎంచుకోండి (నొప్పి లేదు, తక్కువ నొప్పి, మధ్యస్థ నొప్పి, లేదా తీవ్రమైన నొప్పి)',
      step3: 'లక్షణాలు, ట్రిగ్గర్లు మరియు నోట్స్ వంటి ఐచ్ఛిక వివరాలను జోడించండి',
      step4: 'క్రింద ఇచ్చిన రికార్డులలో మీ నొప్పి డేటాను చూడండి'
    },
    view: {
      front: 'ముందు వీక్షణ',
      back: 'వెనుక వీక్షణ',
      switch: 'వీక్షణ మార్చండి'
    },
    actions: {
      clearAll: 'అన్నీ క్లియర్ చేయండి',
      sync: 'ఇప్పుడే సింక్ చేయండి',
      syncing: 'సింక్ అవుతోంది...'
    },
    status: {
      online: 'ఆన్‌లైన్',
      offline: 'ఆఫ్‌లైన్',
      syncing: 'సింక్ అవుతోంది'
    },
    empty: {
      title: 'నేడు నొప్పి రికార్డులు లేవు',
      text: 'మీ నొప్పిని ట్రాక్ చేయడం ప్రారంభించడానికి పైన ఉన్న శరీర ప్రాంతాలపై క్లిక్ చేయండి'
    },
    pending: 'సింక్ పెండింగ్‌లో ఉంది',
    time: {
      unknown: 'తెలియని సమయం',
      invalidDate: 'చెల్లని తేదీ',
      invalid: 'చెల్లని సమయం',
      justNow: 'ఇప్పుడే',
      minutesAgo: 'నిమిషాల క్రితం',
      hoursAgo: 'గంటల క్రితం'
    },
    success: {
      recorded: 'నొప్పి స్థాయి విజయవంతంగా నమోదు చేయబడింది!',
      withAudio: 'ఆడియో నోట్‌తో నొప్పి స్థాయి విజయవంతంగా నమోదు చేయబడింది!',
      cleared: 'అన్ని నొప్పి డేటా విజయవంతంగా క్లియర్ చేయబడింది!'
    },
    confirm: {
      clearAll: 'నేటి అన్ని నొప్పి డేటాను క్లియర్ చేయాలని మీరు ఖచ్చితంగా ఉన్నారా?'
    },
    errors: {
      title: 'శరీర చిత్రాన్ని లోడ్ చేయడంలో లోపం',
      loadFailed: 'నొప్పి డేటాను లోడ్ చేయడంలో విఫలమైంది',
      clearFailed: 'నొప్పి డేటాను క్లియర్ చేయడంలో విఫలమైంది',
      failed: 'నొప్పి స్థాయిని నమోదు చేయడంలో విఫలమైంది: {{message}}'
    }
  },
  audioRecorder: {
    title: 'వాయిస్ రికార్డింగ్',
    help: {
      start: 'మీ నొప్పి వివరణను రికార్డ్ చేయడానికి "రికార్డింగ్ ప్రారంభించండి" క్లిక్ చేయండి.',
      recording: 'రికార్డింగ్ పురోగతిలో ఉంది... పూర్తయిన తర్వాత "రికార్డింగ్ ఆపండి" క్లిక్ చేయండి.',
      recorded: 'ఆడియో విజయవంతంగా రికార్డ్ చేయబడింది! మీరు ప్లే చేయవచ్చు, తొలగించవచ్చు లేదా కొత్తది రికార్డ్ చేయవచ్చు.'
    },
    buttons: {
      start: 'రికార్డింగ్ ప్రారంభించండి',
      stop: 'రికార్డింగ్ ఆపండి',
      play: 'రికార్డింగ్ ప్లే చేయండి',
      pause: 'విరామం',
      delete: 'తొలగించండి',
      rerecord: 'మళ్లీ రికార్డ్ చేయండి'
    },
    info: {
      duration: 'కాల వ్యవధి',
      format: 'ఫార్మాట్'
    },
    errors: {
      microphoneAccess: 'మైక్రోఫోన్‌ను యాక్సెస్ చేయలేకపోయింది. దయచేసి మీ బ్రౌజర్ అనుమతులను తనిఖీ చేయండి.'
    }
  },
  audioPlayer: {
    label: 'వాయిస్ నోట్',
    loading: 'ఆడియో లోడ్ అవుతోంది...',
    errors: {
      loadFailed: 'ఆడియో లోడ్ చేయడంలో విఫలమైంది: {{message}}',
      notLoaded: 'ఆడియో ఇంకా లోడ్ కాలేదు',
      playFailed: 'ఆడియో ప్లే చేయడంలో విఫలమైంది',
      playFailedWithMessage: 'ఆడియో ప్లే చేయడంలో విఫలమైంది: {{message}}'
    }
  },
  distress: {
    title: '🌡️ ఒత్తిడి ట్రాకర్',
    subtitle: 'కాలక్రమేణా మీ ఒత్తిడి స్థాయిలను రికార్డ్ చేసి పర్యవేక్షించండి',
    loading: 'ఒత్తిడి చరిత్ర లోడ్ అవుతోంది...',
    error: {
      title: 'ఒత్తిడి చరిత్ర లోడ్ చేయడంలో లోపం',
      message: 'ఒత్తిడి లాగ్‌లను లోడ్ చేయడంలో విఫలమైంది',
      retry: 'మళ్లీ ప్రయత్నించండి',
      login: 'ఒత్తిడి లాగ్‌లను వీక్షించడానికి దయచేసి లాగిన్ చేయండి'
    },
    empty: {
      title: 'ఒత్తిడి లాగ్‌లు కనుగొనబడలేదు',
      textFilter: 'మీ ఫిల్టర్ ప్రమాణాలకు సరిపోలే లాగ్‌లు లేవు',
      textNoFilter: 'పైన ఉన్న థర్మామీటర్‌ను ఉపయోగించి మీ ఒత్తిడి స్థాయిని రికార్డ్ చేయండి'
    },
    status: {
      offline: 'ఆఫ్‌లైన్ మోడ్',
      syncing: 'సమకాలీకరణ జరుగుతోంది…',
      online: 'ఆన్‌లైన్',
      pending: 'పెండింగ్‌లో',
      pendingSync: 'పెండింగ్ సమకాలీకరణ',
      pendingSyncEntries: 'పెండింగ్ సమకాలీకరణ ఎంట్రీలు',
      syncNow: 'ఇప్పుడు సమకాలీకరించండి'
    },
    filters: {
      minScore: 'స్కోర్:',
      startDate: 'ప్రారంభ తేదీ:',
      endDate: 'ముగింపు తేదీ:',
      allScores: 'అన్ని స్కోర్‌లు',
      score0: '0+',
      score4: '4+ (క్లినికల్‌గా ముఖ్యమైనది)',
      score7: '7+ (అధికం)',
      clinicallySignificant: '4+ (క్లినికల్‌గా ముఖ్యమైనది)',
      high: '7+ (అధికం)',
      clear: 'ఫిల్టర్‌లను క్లియర్ చేయండి'
    },
    stats: {
      totalEntries: 'మొత్తం ఎంట్రీలు',
      averageScore: 'సగటు స్కోర్',
      highestScore: 'అత్యధిక స్కోర్',
      highDistress: 'అధిక ఒత్తిడి (≥4)'
    },
    labels: {
      distressScore: 'ఒత్తిడి స్కోర్:',
      sourcesOfDistress: 'ఒత్తిడి మూలాలు:',
      notes: 'నోట్‌లు:',
      extreme: 'తీవ్రమైన ఒత్తిడి',
      moderateToSevere: 'మధ్యస్థం నుండి తీవ్రమైన ఒత్తిడి',
      mild: 'తేలికపాటి ఒత్తిడి',
      none: 'ఒత్తిడి లేదు',
      alert: '🚨 క్లినికల్‌గా ముఖ్యమైన ఒత్తిడి - మీ వైద్యుడికి తెలియజేయబడింది'
    },
    header: {
      title: 'ఒత్తిడి లాగ్‌లు ({{count}})',
      refresh: 'రిఫ్రెష్'
    }
  },
  distressThermometer: {
    title: '🌡️ ఒత్తిడి థర్మామీటర్',
    subtitle: 'గత వారంలో మీ ప్రస్తుత ఒత్తిడి స్థాయిని రేట్ చేయండి (0 = ఒత్తిడి లేదు, 10 = తీవ్రమైన ఒత్తిడి)',
    guidelines: 'NCCN ఒత్తిడి థర్మామీటర్ మార్గదర్శకాల ఆధారంగా',
    problemList: {
      label: 'సమస్యల జాబితా - మీ ఒత్తిడికి కారణమేమిటి? (ఐచ్ఛికం)',
      helper: 'వర్తించేవన్నీ ఎంచుకోండి (NCCN సమస్యల జాబితా)'
    },
    notes: {
      label: 'అదనపు నోట్‌లు (ఐచ్ఛికం)',
      placeholder: 'మీ ఒత్తిడి గురించి ఏదైనా అదనపు వివరాలను వివరించండి...',
      enterNumberHint: 'లేదా సంఖ్యను నమోదు చేయండి (0-10):'
    },
    buttons: {
      record: 'ఒత్తిడి స్థాయిని రికార్డ్ చేయండి',
      recording: 'రికార్డ్ చేస్తోంది...',
      clear: 'క్లియర్'
    },
    scoreLabels: {
      noDistress: 'ఒత్తిడి లేదు',
      mildDistress: 'తేలికపాటి ఒత్తిడి',
      moderateDistress: 'మధ్యస్థ ఒత్తిడి',
      severeDistress: 'తీవ్రమైన ఒత్తిడి',
      extremeDistress: 'తీవ్రమైన ఒత్తిడి'
    },
    alert: '⚠️ NCCN హెచ్చరిక: {{score}} స్కోర్ క్లినికల్‌గా ముఖ్యమైన ఒత్తిడిని (≥4) సూచిస్తుంది. మీ ఆరోగ్య సేవా ప్రదాతకు తెలియజేయబడుతుంది.',
    sources: {
      transportation: 'రవాణా',
      housing: 'వసతి',
      insuranceFinancial: 'వీమా/ఆర్థిక',
      workSchool: 'పని/పాఠశాల',
      dealingWithPartner: 'భాగస్వామితో వ్యవహరించడం',
      dealingWithChildren: 'పిల్లలతో వ్యవహరించడం',
      familyHealthIssues: 'కుటుంబ ఆరోగ్య సమస్యలు',
      depression: 'ఖిన్నత',
      fears: 'భయాలు',
      nervousness: 'ఆందోళన',
      sadness: 'విచారం',
      worry: 'ఆందోళన',
      lossOfInterest: 'సాధారణ కార్యకలాపాలలో ఆసక్తి కోల్పోవడం',
      spiritualReligiousConcerns: 'ఆధ్యాత్మిక/మత సంబంధిత ఆందోళనలు',
      fatigue: 'అలసట',
      pain: 'నొప్పి',
      sleepProblems: 'నిద్ర సమస్యలు',
      eatingProblems: 'తినడం సమస్యలు',
      bathingDressing: 'స్నానం/దుస్తులు',
      breathingProblems: 'శ్వాస సమస్యలు',
      nausea: 'వికారం',
      memoryConcentration: 'మెమరీ/కేంద్రీకరణ',
      mouthSores: 'నోటి పుండ్లు',
      skinProblems: 'చర్మ సమస్యలు',
      tinglingHandsFeet: 'చేతులు/పాదాలలో ముళ్ళు',
      otherPhysicalProblems: 'ఇతర శారీరక సమస్యలు'
    },
    success: {
      recorded: 'ఒత్తిడి స్థాయి {{score}} విజయవంతంగా రికార్డ్ చేయబడింది'
    },
    errors: {
      login: 'ఒత్తిడిని రికార్డ్ చేయడానికి దయచేసి లాగిన్ చేయండి',
      recordFailed: 'ఒత్తిడిని రికార్డ్ చేయడంలో విఫలమైంది'
    }
  },
  painScale: {
    title: 'నొప్పి స్థాయిని రికార్డ్ చేయండి',
    vasTitle: 'విజువల్ అనలాగ్ స్కేల్ (VAS)',
    vasDescription: 'స్టాండర్డ్ 0–10 స్కేల్: 0 = నొప్పి లేదు, 10 = చెత్త నొప్పి',
    vasCurrentValue: 'మీ నొప్పి స్థాయి',
    vasWorstPain: 'చెత్త నొప్పి',
    vasDescriptors: {
      noPain: 'నొప్పి లేదు',
      mild: 'తేలికపాటి',
      moderate: 'మధ్యస్థ',
      severe: 'తీవ్రమైన',
      verySevere: 'చాలా తీవ్రమైన',
      worstPain: 'చెత్త నొప్పి'
    },
    fixedScales: 'నిర్దిష్ట నొప్పి స్కేల్‌లు: నొప్పి లేదు (0) • తక్కువ నొప్పి (1-3) • మధ్యస్థ (4-7) • తీవ్ర (8-10)',
    frontView: '👤 ముందు వీక్షణ',
    backView: '👥 వెనుక వీక్షణ',
    levels: {
      noPain: 'నొప్పి లేదు',
      lessPain: 'తక్కువ నొప్పి',
      moderatePain: 'మధ్యస్థ నొప్పి',
      severePain: 'తీవ్రమైన నొప్పి'
    },
    descriptions: {
      noPain: 'అసౌకర్యం లేదు',
      lessPain: 'తేలికపాటి అసౌకర్యం',
      moderatePain: 'గమనించదగినది కానీ నిర్వహించదగినది',
      severePain: 'తీవ్రమైన మరియు ఆందోళన కలిగించేది'
    },
    additionalInfo: 'అదనపు సమాచారం (ఐచ్ఛికం)',
    painScale: 'నొప్పి స్కేల్',
    timeOfDay: 'రోజు సమయం',
    mood: 'మనస్థితి',
    activity: 'కార్యాచరణ స్థాయి',
    symptoms: 'లక్షణాలు (కామా-విభజించబడినవి)',
    triggers: 'ట్రిగ్గర్లు (కామా-విభజించబడినవి)',
    notes: 'నోట్స్ (టెక్స్ట్)',
    audioNote: 'లేదా మీ వివరణను రికార్డ్ చేయండి (ఆడియో)',
    placeholders: {
      symptoms: 'ఉదా., కఠినత, వాపు, మాంద్యం',
      triggers: 'ఉదా., ఒత్తిడి, వాతావరణం, వ్యాయామం',
      notes: 'మీ నొప్పి వివరణను ఇక్కడ టైప్ చేయండి...',
      selectMood: 'మనస్థితిని ఎంచుకోండి (ఐచ్ఛికం)',
      selectActivity: 'కార్యాచరణను ఎంచుకోండి (ఐచ్ఛికం)',
      selectTimeOfDay: 'రోజు సమయం ఎంచుకోండి (ఐచ్ఛికం)'
    },
    symptomsMultiSelect: 'పలు లక్షణాలను ఎంచుకోడానికి Ctrl/Cmd ను నొక్కి పట్టుకోండి'
  },
  timeOptions: {
    morning: 'ఉదయం',
    afternoon: 'మధ్యాహ్నం',
    evening: 'సాయంత్రం',
    night: 'రాత్రి'
  },
  moodOptions: {
    good: 'మంచిది',
    moderate: 'మధ్యస్థ',
    bad: 'చెడ్డ'
  },
  activityOptions: {
    able: 'సామర్థ్యం',
    partiallyDependent: 'భాగస్వామ్య ఆధారపడిన',
    notAble: 'సామర్థ్యం లేదు'
  },
  symptomOptions: {
    none: 'ఏమీ లేదు',
    vomiting: 'వాంతి',
    nausea: 'వికారం',
    dizziness: 'తల తిరగడం',
    headache: 'తలనొప్పి',
    fever: 'జ్వరం',
    fatigue: 'అలసట',
    shortnessOfBreath: 'ఊపిరితిత్తుల ఇబ్బంది',
    cough: 'దగ్గు',
    swelling: 'వాపు',
    numbness: 'సంవేదన లేకపోవడం',
    weakness: 'బలహీనత'
  },
  buttons: {
    cancel: 'రద్దు చేయండి',
    clear: 'క్లియర్',
    record: 'నొప్పిని రికార్డ్ చేయండి'
  },
  painLogs: {
    title: 'నొప్పి లాగ్‌లు',
    subtitle: 'మీ రికార్డ్ చేసిన నొప్పి ఎంట్రీలను సమీక్షించండి మరియు నిర్వహించండి.',
    loading: 'నొప్పి లాగ్‌లు లోడ్ అవుతోంది...',
    error: 'నొప్పి లాగ్‌లను లోడ్ చేయడంలో లోపం',
    filters: {
      painLevel: 'నొప్పి స్థాయి',
      allLevels: 'అన్ని స్థాయిలు',
      noPain: 'నొప్పి లేదు',
      lessPain: 'తక్కువ నొప్పి',
      moderatePain: 'మధ్యస్థ నొప్పి',
      severePain: 'తీవ్రమైన నొప్పి',
      startDate: 'ప్రారంభ తేదీ',
      endDate: 'ముగింపు తేదీ',
      clear: 'ఫిల్టర్‌లను క్లియర్ చేయండి'
    },
    table: {
      painLevel: 'నొప్పి స్థాయి',
      zone: 'శరీర భాగం',
      date: 'తేదీ',
      time: 'సమయం',
      actions: 'క్రియలు'
    },
    actions: {
      delete: 'తొలగించండి',
      previous: 'మునుపటి',
      next: 'తరువాత'
    },
    empty: 'నొప్పి లాగ్‌లు కనుగొనబడలేదు',
    confirmDelete: 'మీరు ఈ నొప్పి లాగ్‌ను తొలగించాలని ఖచ్చితంగా ఉన్నారా?',
    success: {
      deleted: 'నొప్పి లాగ్ విజయవంతంగా తొలగించబడింది'
    },
    errors: {
      deleteFailed: 'నొప్పి లాగ్‌ను తొలగించడంలో విఫలమైంది'
    }
  },
  profile: {
    title: 'నా ప్రొఫైల్',
    subtitle: 'మీ వ్యక్తిగత సమాచారాన్ని నిర్వహించండి',
    loading: 'ప్రొఫైల్ లోడ్ అవుతోంది...',
    editWhatsApp: '✏️ వాట్‌సాప్ నంబర్‌ను సవరించండి',
    whatsAppLabel: 'వాట్‌సాప్ నంబర్',
    whatsAppPlaceholder: 'వాట్‌సాప్ నంబర్‌ను నమోదు చేయండి (ఉదా. +1234567890)',
    whatsAppHelper: 'మీరు అందిస్తే మందుల సమాచారం వాట్‌సాప్ ద్వారా పంపబడుతుంది. తీసివేయడానికి ఖాళీగా ఉంచండి.',
    save: '💾 మార్పులను సేవ్ చేయండి',
    saving: 'సేవ్ అవుతోంది...',
    cancel: 'రద్దు చేయండి',
    success: 'వాట్‌సాప్ నంబర్ విజయవంతంగా నవీకరించబడింది!',
    optional: 'ఐచ్ఛికం',
    forNotifications: 'మందుల నోటిఫికేషన్‌ల కోసం',
    notProvided: 'అందించబడలేదు',
    patientId: 'రోగి ఐడి',
    age: 'వయసు',
    ageYears: '{{age}} సంవత్సరాలు',
    phoneNumber: 'ఫోన్ నంబర్',
    whatsAppNumber: 'వాట్‌సాప్ నంబర్',
    notRegistered: 'నమోదు చేయబడలేదు',
    notRegisteredMessage: 'నమోదు చేయబడలేదు - మీరు వాట్‌సాప్ మందుల నోటిఫికేషన్‌లను స్వీకరించరు',
    dateOfBirth: 'పుట్టిన తేదీ',
    accountStatus: 'ఖాతా స్థితి',
    active: 'సక్రియ',
    inactive: 'నిష్క్రియ',
    memberSince: 'సభ్యుడు నుండి',
    lastUpdated: 'చివరి నవీకరణ',
    role: 'పాత్ర',
    roles: {
      patient: 'రోగి',
      doctor: 'వైద్యుడు',
      admin: 'అడ్మిన్'
    },
    sections: {
      personalInfo: 'వ్యక్తిగత సమాచారం',
      contactInfo: 'సంప్రదింపు సమాచారం',
      accountInfo: 'ఖాతా సమాచారం'
    },
    errors: {
      authRequired: 'ప్రామాణీకరణ అవసరం',
      updateFailed: 'వాట్‌సాప్ నంబర్‌ను నవీకరించడంలో విఫలమైంది'
    }
  }
};

const resources = {
  en: { translation: baseTranslation },
  kn: { translation: createTranslation(knOverrides) },
  ta: { translation: createTranslation(taOverrides) },
  ml: { translation: createTranslation(mlOverrides) },
  hi: { translation: createTranslation(hiOverrides) },
  mr: { translation: createTranslation(mrOverrides) },
  te: { translation: createTranslation(teOverrides) }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: LANGUAGES.map((lang) => lang.code),
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'subdomain'],
      caches: ['localStorage']
    },
    interpolation: {
      escapeValue: false
    }
  });

i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
});

export default i18n;


